import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { courseAPI, progressAPI, videoAPI, vocabularyAPI } from '../api';
import { FiArrowLeft, FiArrowRight, FiCheck, FiVolume2, FiAward, FiSettings, FiFileText, FiRepeat, FiBookOpen, FiDownload, FiHome, FiHeart, FiMenu } from 'react-icons/fi';
import { 
  enrichVocabularyWithStatus, 
  updateItemStatus,
  getItemStatus 
} from '../utils/vocabularyStatus';
import { getMediaUrl } from '../utils/config';
import { extractAndProcessVocabulary } from '../utils/vocabularyUtils';
import { processVocabularyWithExamples } from '../utils/simpleVocabularyUtils';

// 词性缩写转换
const abbreviatePartOfSpeech = (pos) => {
  if (!pos) return pos;
  const lowerPos = pos.toLowerCase().trim();
  const posMap = {
    'noun': 'n.',
    'verb': 'v.',
    'adjective': 'adj.',
    'adverb': 'adv.',
    'preposition': 'prep.',
    'conjunction': 'conj.',
    'pronoun': 'pron.',
    'interjection': 'interj.',
    'article': 'art.',
    'numeral': 'num.'
  };
  return posMap[lowerPos] || pos;
};

const Lesson = () => {
  const params = useParams();
  const { courseId, lessonId, id } = params;
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const floatingVideoRef = useRef(null);
  const subtitleContainerRef = useRef(null);
  const currentSentencePlayCountRef = useRef(0); // 使用 ref 跟踪当前句子播放次数
  const isCurrentSentenceEndedRef = useRef(false); // 使用 ref 跟踪当前句子是否播放结束
  const lastProcessedSubtitleRef = useRef(-1); // 记录上次处理的字幕索引
  const [course, setCourse] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [currentExercise, setCurrentExercise] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [exerciseScore, setExerciseScore] = useState(100);
  
  const getCategoryLabel = (category) => {
    const categoryMap = {
      universe: '宇宙与空间科学',
      physics: '物理学',
      chemistry: '化学',
      earth: '地球科学',
      biology: '基础生物学',
      plants: '植物学',
      animals: '动物学',
      microbes: '微生物学',
      ecology: '生态学',
      human: '人体科学'
    };
    return categoryMap[category] || '英文科普';
  };
  
  // 新增状态
  const [subtitleMode, setSubtitleMode] = useState(() => {
    const saved = localStorage.getItem('subtitleMode');
    return saved || 'bilingual';
  }); // bilingual, english, chinese
  const [subtitleFontSize, setSubtitleFontSize] = useState(() => {
    const saved = localStorage.getItem('subtitleFontSize');
    return saved || 'medium';
  }); // small, medium, large, extraLarge
  const [playMode, setPlayMode] = useState('single'); // single, singleLoop, continuous, sentenceLoop
  const [exerciseMode, setExerciseMode] = useState('none'); // none, repeat, fill
  const [intensiveMode, setIntensiveMode] = useState('none'); // none, words, phrases
  const [currentSubtitle, setCurrentSubtitle] = useState(0);
  const [currentPlayCountDisplay, setCurrentPlayCountDisplay] = useState(0); // 用于UI显示的播放次数
  const [dropdownOpen, setDropdownOpen] = useState({
    subtitle: false,
    play: false,
    bottomPlay: false,
    exercise: false,
    intensive: false,
    speed: false,
    bottomSpeed: false
  });
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [currentVolume, setCurrentVolume] = useState(50);
  const [volumeSliderHovered, setVolumeSliderHovered] = useState(false);
  const [isVolumeDragging, setIsVolumeDragging] = useState(false);
  const [currentProgress, setCurrentProgress] = useState(0); // 当前视频进度（百分比）
  const [isProgressDragging, setIsProgressDragging] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false); // 视频播放状态
  const [isPlayerHovered, setIsPlayerHovered] = useState(false); // 鼠标是否悬停在播放器上
  const [isPlayerTouched, setIsPlayerTouched] = useState(false); // 播放器是否被触摸
  const [isMuted, setIsMuted] = useState(false); // 静音状态
  const [playbackSpeed, setPlaybackSpeed] = useState(1); // 播放速度
  const [followMode, setFollowMode] = useState(true); // 跟随模式（默认开启）
  const [scrollLocked, setScrollLocked] = useState(false); // 滚动锁定
  const [loopCount, setLoopCount] = useState(1); // 循环次数
  const [autoNext, setAutoNext] = useState(false); // 自动下句
  // 视频时间状态
  const [currentTime, setCurrentTime] = useState(0); // 当前播放时间（秒）
  const [videoDuration, setVideoDuration] = useState(0); // 视频总时长（秒）
  const [userAnswers, setUserAnswers] = useState({}); // 用户填空答案
  const [showResetConfirm, setShowResetConfirm] = useState(false); // 重置确认框显示状态
  const [collectedVideos, setCollectedVideos] = useState([]); // 收藏的视频
  const [playingSubtitleIndex, setPlayingSubtitleIndex] = useState(null); // 跟读练习模式下当前正在播放的字幕索引
  const [playingRecordingIndex, setPlayingRecordingIndex] = useState(null); // 当前正在播放录音的字幕索引
  // 词汇分析状态
  const [vocabularyAnalysis, setVocabularyAnalysis] = useState(null);
  const [analyzingVocabulary, setAnalyzingVocabulary] = useState(false);
  const [vocabularyError, setVocabularyError] = useState(null);

  // 格式化时间为 MM:SS 格式
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // 获取字幕字体大小
  const getSubtitleFontSizes = () => {
    const sizeMap = {
      small: { english: '1rem', chinese: '0.85rem' },
      medium: { english: '1.2rem', chinese: '1rem' },
      large: { english: '1.5rem', chinese: '1.2rem' },
      extraLarge: { english: '1.8rem', chinese: '1.5rem' }
    };
    return sizeMap[subtitleFontSize] || sizeMap.medium;
  };
  const [recordingIndex, setRecordingIndex] = useState(null); // 当前正在录音的字幕索引
  const [hasRecording, setHasRecording] = useState({}); // 记录哪些字幕有录音
  const [showReferenceImage, setShowReferenceImage] = useState(null); // 参考图显示状态
  const [revealedWords, setRevealedWords] = useState({}); // 记录已显示的单词
  const mediaRecorderRef = useRef(null); // MediaRecorder 实例
  const audioChunksRef = useRef([]); // 录音数据块
  const recordedAudioUrlRef = useRef({}); // 存储每个句子的录音 URL，key 是字幕索引
  const audioPlaybackRef = useRef(null); // 当前播放的音频元素
  // 内容精读相关状态
  const [showChinese, setShowChinese] = useState(true); // 是否显示中文
  const [showAnnotations, setShowAnnotations] = useState(true); // 是否显示标注
  const [wordFilter, setWordFilter] = useState('all'); // 单词过滤：all, unmarked, known, unknown
  const [phraseFilter, setPhraseFilter] = useState('all'); // 短语过滤：all, unmarked, known, unknown
  const [expressionFilter, setExpressionFilter] = useState('all'); // 自然科普词汇过滤：all, unmarked, known, unknown
  const [currentWordTab, setCurrentWordTab] = useState('words'); // 当前标签：words, phrases, expressions
  const [showWordCard, setShowWordCard] = useState(false); // 单词卡片弹窗
  const [showWordDetail, setShowWordDetail] = useState(false); // 单词详情弹窗
  const [showIntroduction, setShowIntroduction] = useState(false); // 简介弹窗
  const [currentCardType, setCurrentCardType] = useState('words'); // 当前卡片类型：words, phrases, expressions
  const [currentWordIndex, setCurrentWordIndex] = useState(0); // 当前单词索引
  const [showWordDefinition, setShowWordDefinition] = useState(false); // 是否显示单词释义
  const [touchStartX, setTouchStartX] = useState(0); // 触摸起始位置
  const [touchEndX, setTouchEndX] = useState(0); // 触摸结束位置
  const [isSwiping, setIsSwiping] = useState(false); // 是否正在滑动
  const [activeNavButton, setActiveNavButton] = useState(null); // 当前激活的导航按钮
  // 字幕数据状态
  const [subtitles, setSubtitles] = useState([
    { time: 0, english: '加载字幕中...', chinese: 'Loading subtitles...' }
  ]);
  // 词汇数据状态
  const [wordsData, setWordsData] = useState([]);
  const [phrasesData, setPhrasesData] = useState([]);
  const [expressionsData, setExpressionsData] = useState([]);
  const [voicesLoaded, setVoicesLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 768 && window.innerWidth < 1200);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1200);
  const [isLandscape, setIsLandscape] = useState(window.innerWidth > window.innerHeight);
  
  const isFloatingPlayerMode = (isMobile || isTablet) && isLandscape;
  const isTopPlayerMode = (isMobile || isTablet) && !isLandscape;
  
  const [playerPosition, setPlayerPosition] = useState({ x: 20, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [playerScale, setPlayerScale] = useState(1); // 播放器缩放比例
  const [lastDistance, setLastDistance] = useState(0); // 上一次双指距离
  const playerRef = useRef(null);
  
  // 解析SRT字幕文件
  const parseSRT = (srtContent) => {
    const subtitles = [];
    const blocks = srtContent.trim().split(/\n\s*\n/);
    
    blocks.forEach(block => {
      const lines = block.split('\n');
      if (lines.length >= 3) {
        const timeLine = lines[1];
        const timeMatch = timeLine.match(/(\d{2}):(\d{2}):(\d{2}),(\d{3})\s*-->\s*(\d{2}):(\d{2}):(\d{2}),(\d{3})/);
        
        if (timeMatch) {
          const startHours = parseInt(timeMatch[1]);
          const startMinutes = parseInt(timeMatch[2]);
          const startSeconds = parseInt(timeMatch[3]);
          const startMs = parseInt(timeMatch[4]);
          const startTime = startHours * 3600 + startMinutes * 60 + startSeconds + startMs / 1000;
          
          const endHours = parseInt(timeMatch[5]);
          const endMinutes = parseInt(timeMatch[6]);
          const endSeconds = parseInt(timeMatch[7]);
          const endMs = parseInt(timeMatch[8]);
          const endTime = endHours * 3600 + endMinutes * 60 + endSeconds + endMs / 1000;
          
          const textLines = lines.slice(2);
          let english = '';
          let chinese = '';
          
          textLines.forEach(line => {
            if (/[\u4e00-\u9fa5]/.test(line)) {
              chinese += (chinese ? ' ' : '') + line;
            } else if (line.trim()) {
              english += (english ? ' ' : '') + line;
            }
          });
          
          subtitles.push({
            time: startTime,
            endTime: endTime,
            english: english.trim(),
            chinese: chinese.trim()
          });
        }
      }
    });
    
    // 按时间顺序排序字幕
    subtitles.sort((a, b) => a.time - b.time);
    return subtitles;
  };
  
  // 解析VTT字幕文件
  const parseVTT = (vttContent) => {
    const subtitles = [];
    const lines = vttContent.trim().split('\n');
    let i = 0;
    
    while (i < lines.length && !lines[i].includes('-->')) {
      i++;
    }
    
    while (i < lines.length) {
      const timeLine = lines[i];
      const timeMatch = timeLine.match(/(\d{2}):(\d{2}):(\d{2})\.(\d{3})\s*-->\s*(\d{2}):(\d{2}):(\d{2})\.(\d{3})/);
      
      if (!timeMatch) {
        const shortTimeMatch = timeLine.match(/(\d{2}):(\d{2})\.(\d{3})\s*-->\s*(\d{2}):(\d{2})\.(\d{3})/);
        if (shortTimeMatch) {
          const startMinutes = parseInt(shortTimeMatch[1]);
          const startSeconds = parseInt(shortTimeMatch[2]);
          const startMs = parseInt(shortTimeMatch[3]);
          const startTime = startMinutes * 60 + startSeconds + startMs / 1000;
          
          const endMinutes = parseInt(shortTimeMatch[4]);
          const endSeconds = parseInt(shortTimeMatch[5]);
          const endMs = parseInt(shortTimeMatch[6]);
          const endTime = endMinutes * 60 + endSeconds + endMs / 1000;
          
          i++;
          let english = '';
          let chinese = '';
          
          while (i < lines.length && lines[i].trim() !== '') {
            const line = lines[i];
            if (/[\u4e00-\u9fa5]/.test(line)) {
              chinese += (chinese ? ' ' : '') + line;
            } else if (line.trim()) {
              english += (english ? ' ' : '') + line;
            }
            i++;
          }
          
          subtitles.push({
            time: startTime,
            endTime: endTime,
            english: english.trim(),
            chinese: chinese.trim()
          });
        }
        i++;
        continue;
      }
      
      const startHours = parseInt(timeMatch[1]);
      const startMinutes = parseInt(timeMatch[2]);
      const startSeconds = parseInt(timeMatch[3]);
      const startMs = parseInt(timeMatch[4]);
      const startTime = startHours * 3600 + startMinutes * 60 + startSeconds + startMs / 1000;
      
      const endHours = parseInt(timeMatch[5]);
      const endMinutes = parseInt(timeMatch[6]);
      const endSeconds = parseInt(timeMatch[7]);
      const endMs = parseInt(timeMatch[8]);
      const endTime = endHours * 3600 + endMinutes * 60 + endSeconds + endMs / 1000;
      
      i++;
      let english = '';
      let chinese = '';
      
      while (i < lines.length && lines[i].trim() !== '') {
        const line = lines[i];
        if (/[\u4e00-\u9fa5]/.test(line)) {
          chinese += (chinese ? ' ' : '') + line;
        } else if (line.trim()) {
          english += (english ? ' ' : '') + line;
        }
        i++;
      }
      
      subtitles.push({
        time: startTime,
        endTime: endTime,
        english: english.trim(),
        chinese: chinese.trim()
      });
      
      while (i < lines.length && lines[i].trim() === '') {
        i++;
      }
    }
    
    // 按时间顺序排序字幕
    subtitles.sort((a, b) => a.time - b.time);
    return subtitles;
  };
  
  // 播放器拖动事件处理
  const handlePlayerMouseDown = (e) => {
    if (!playerRef.current) return;
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - playerPosition.x,
      y: e.clientY - playerPosition.y
    });
  };
  
  const handlePlayerMouseMove = (e) => {
    if (!isDragging) return;
    const newX = e.clientX - dragOffset.x;
    const newY = e.clientY - dragOffset.y;
    
    // 计算播放器的实际宽度和高度（考虑缩放）
    const playerWidth = (isMobile ? 0.6 : 0.4) * window.innerWidth * playerScale;
    const playerHeight = playerWidth * 9 / 16; // 16:9 宽高比
    
    // 边界检查，确保播放器不会超出屏幕
    const boundedX = Math.max(10, Math.min(window.innerWidth - playerWidth - 10, newX));
    const boundedY = Math.max(10, Math.min(window.innerHeight - playerHeight - 10, newY));
    
    setPlayerPosition({
      x: boundedX,
      y: boundedY
    });
  };
  
  const handlePlayerMouseUp = () => {
    setIsDragging(false);
  };
  
  const handlePlayerMouseLeave = () => {
    setIsDragging(false);
  };
  
  // 触摸事件处理（移动端拖动）
  const handlePlayerTouchStart = (e) => {
    if (!playerRef.current) return;
    
    // 处理双指缩放
    if (e.touches.length === 2) {
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.sqrt(
        Math.pow(touch2.clientX - touch1.clientX, 2) + 
        Math.pow(touch2.clientY - touch1.clientY, 2)
      );
      setLastDistance(distance);
    } else {
      // 处理单指拖动
      const touch = e.touches[0];
      setIsDragging(true);
      setDragOffset({
        x: touch.clientX - playerPosition.x,
        y: touch.clientY - playerPosition.y
      });
    }
  };
  
  const handlePlayerTouchMove = (e) => {
    if (!playerRef.current) return;
    
    // 处理双指缩放
    if (e.touches.length === 2) {
      e.preventDefault(); // 只在双指缩放时阻止默认行为
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const currentDistance = Math.sqrt(
        Math.pow(touch2.clientX - touch1.clientX, 2) + 
        Math.pow(touch2.clientY - touch1.clientY, 2)
      );
      
      if (lastDistance > 0) {
        const scaleFactor = currentDistance / lastDistance;
        const newScale = Math.max(0.5, Math.min(2, playerScale * scaleFactor));
        setPlayerScale(newScale);
      }
      
      setLastDistance(currentDistance);
    } else if (isDragging) {
      // 处理单指拖动，添加边界检查
      e.preventDefault(); // 只在拖动时阻止默认行为
      const touch = e.touches[0];
      const newX = touch.clientX - dragOffset.x;
      const newY = touch.clientY - dragOffset.y;
      
      // 计算播放器的实际宽度和高度（考虑缩放）
      const playerWidth = (isMobile ? 0.6 : 0.4) * window.innerWidth * playerScale;
      const playerHeight = playerWidth * 9 / 16; // 16:9 宽高比
      
      // 边界检查，确保播放器不会超出屏幕
      const boundedX = Math.max(10, Math.min(window.innerWidth - playerWidth - 10, newX));
      const boundedY = Math.max(10, Math.min(window.innerHeight - playerHeight - 10, newY));
      
      setPlayerPosition({
        x: boundedX,
        y: boundedY
      });
    }
  };
  
  const handlePlayerTouchEnd = () => {
    setIsDragging(false);
    setLastDistance(0);
  };

  // 常见单词的音标映射表（备用方案）
  const wordPhonetics = {
    'digestive': '/daɪˈdʒestɪv/',
    'pancreas': '/ˈpæŋkriəs/',
    'gallbladder': '/ˈɡɔːlˌblædə/',
    'liver': '/ˈlɪvə/',
    'enzymes': '/ˈenzaɪmz/',
    'hormones': '/ˈhɔːməʊnz/',
    'stomach': '/ˈstʌmək/',
    'intestine': '/ɪnˈtestɪn/',
    'esophagus': '/ɪˈsɒfəɡəs/',
    'rectum': '/ˈrektəm/',
    'anus': '/ˈeɪnəs/',
    'saliva': '/səˈlaɪvə/',
    'chyme': '/kaɪm/',
    'bile': '/baɪl/',
    'villi': '/ˈvɪlaɪ/',
    'microvilli': '/ˌmaɪkrəʊˈvɪlaɪ/',
    'peristalsis': '/ˌperɪˈstælsɪs/',
    'digestion': '/daɪˈdʒestʃən/',
    'absorption': '/əbˈsɔːpʃən/',
    'assimilation': '/əˌsɪmɪˈleɪʃən/',
    'ghrelin': '/ˈɡreliːn/',
    'leptin': '/ˈleptɪn/',
    'insulin': '/ˈɪnsjʊlɪn/',
    'glucagon': '/ˈɡluːkəɡɒn/',
    'cell': '/sel/',
    'nucleus': '/ˈnjuːkliəs/',
    'mitochondria': '/ˌmaɪtəˈkɒndrɪə/',
    'ribosome': '/ˈraɪbəsəʊm/',
    'cytoplasm': '/ˈsaɪtəplæzəm/',
    'membrane': '/ˈmembreɪn/',
    'organelle': '/ˌɔːɡəˈnel/',
    'chromosome': '/ˈkrəʊməsəʊm/',
    'dna': '/ˌdiː en ˈeɪ/',
    'rna': '/ˌɑː en ˈeɪ/',
    'gene': '/dʒiːn/',
    'allele': '/əˈliːl/',
    'genome': '/ˈdʒiːnəʊm/',
    'protein': '/ˈprəʊtiːn/',
    'amino': '/əˈmiːnəʊ/',
    'acid': '/ˈæsɪd/',
    'lipid': '/ˈlɪpɪd/',
    'carbohydrate': '/ˌkɑːbəʊˈhaɪdreɪt/',
    'glucose': '/ˈɡluːkəʊs/',
    'fructose': '/ˈfrʌktəʊs/',
    'sucrose': '/ˈsuːkrəʊs/',
    'lactose': '/ˈlæktəʊs/',
    'glycogen': '/ˈɡlaɪkədʒən/',
    'starch': '/stɑːtʃ/',
    'cellulose': '/ˈseljʊləʊs/',
    'chlorophyll': '/ˈklɒrəfɪl/',
    'photosynthesis': '/ˌfəʊtəʊˈsɪnθəsɪs/',
    'respiration': '/ˌrespəˈreɪʃən/',
    'fermentation': '/ˌfɜːmenˈteɪʃən/',
    'glycolysis': '/ɡlaɪˈkɒlɪsɪs/',
    'krebs': '/krebz/',
    'electron': '/ɪˈlektrɒn/',
    'transport': '/ˈtrænspɔːt/',
    'atp': '/ˌeɪ tiː ˈpiː/',
    'adp': '/ˌeɪ diː ˈpiː/',
    'phosphate': '/ˈfɒsfeɪt/',
    'oxidation': '/ˌɒksɪˈdeɪʃən/',
    'reduction': '/rɪˈdʌkʃən/',
    'circulatory': '/ˈsɜːkjʊlətri/',
    'cardiovascular': '/ˌkɑːdiəʊˈvæskjʊlə/',
    'respiratory': '/rɪˈspɪrətri/',
    'nervous': '/ˈnɜːvəs/',
    'endocrine': '/ˈendəkrɪn/',
    'immune': '/ɪˈmjuːn/',
    'lymphatic': '/lɪmˈfætɪk/',
    'skeletal': '/ˈskelətl/',
    'muscular': '/ˈmʌskjʊlə/',
    'integumentary': '/ˌɪntɪɡjʊˈmentri/',
    'urinary': '/ˈjʊərɪnəri/',
    'reproductive': '/ˌriːprəˈdʌktɪv/',
    'artery': '/ˈɑːtəri/',
    'vein': '/veɪn/',
    'capillary': '/kəˈpɪləri/',
    'heart': '/hɑːt/',
    'aorta': '/eɪˈɔːtə/',
    'ventricle': '/ˈventrɪkl/',
    'atrium': '/ˈeɪtriəm/',
    'valve': '/vælv/',
    'blood': '/blʌd/',
    'plasma': '/ˈplæzmə/',
    'erythrocyte': '/ɪˈrɪθrəsaɪt/',
    'leukocyte': '/ˈluːkəsaɪt/',
    'platelet': '/ˈpleɪtlət/',
    'hemoglobin': '/ˌhiːməˈɡləʊbɪn/',
    'oxygen': '/ˈɒksɪdʒən/',
    'carbon': '/ˈkɑːbən/',
    'dioxide': '/daɪˈɒksaɪd/',
    'pharynx': '/ˈfærɪŋks/',
    'larynx': '/ˈlærɪŋks/',
    'trachea': '/ˈtreɪkiə/',
    'bronchus': '/ˈbrɒŋkəs/',
    'alveolus': '/ælˈviːələs/',
    'lung': '/lʌŋ/',
    'diaphragm': '/ˈdaɪəfræm/',
    'neuron': '/ˈnjʊərɒn/',
    'synapse': '/ˈsaɪnæps/',
    'neurotransmitter': '/ˌnjʊərəʊtrænsˈmɪtə/',
    'spinal': '/ˈspaɪnl/',
    'cord': '/kɔːd/',
    'brain': '/breɪn/',
    'cerebrum': '/səˈriːbrəm/',
    'cerebellum': '/ˌserɪˈbeləm/',
    'medulla': '/meˈdʌlə/',
    'hypothalamus': '/ˌhaɪpəˈθæləməs/',
    'pituitary': '/pɪˈtjuːɪtəri/',
    'thyroid': '/ˈθaɪrɔɪd/',
    'adrenal': '/əˈdriːnl/',
    'thymus': '/ˈθaɪməs/',
    'spleen': '/spliːn/',
    'lymph': '/lɪmf/',
    'node': '/nəʊd/',
    'antibody': '/ˈæntibɒdi/',
    'antigen': '/ˈæntɪdʒən/',
    'vaccine': '/ˈvæksiːn/',
    'pathogen': '/ˈpæθədʒən/',
    'bacteria': '/bækˈtɪəriə/',
    'virus': '/ˈvaɪrəs/',
    'fungi': '/ˈfʌŋɡi/',
    'parasite': '/ˈpærəsaɪt/',
    'infection': '/ɪnˈfekʃən/',
    'inflammation': '/ˌɪnfləˈmeɪʃən/',
    'immunity': '/ɪˈmjuːnəti/',
    'ecosystem': '/ˈiːkəʊsɪstəm/',
    'habitat': '/ˈhæbɪtæt/',
    'niche': '/nɪtʃ/',
    'population': '/ˌpɒpjuˈleɪʃən/',
    'community': '/kəˈmjuːnəti/',
    'biosphere': '/ˈbaɪəsfɪə/',
    'trophic': '/ˈtrɒfɪk/',
    'level': '/ˈlevl/',
    'food': '/fuːd/',
    'web': '/web/',
    'chain': '/tʃeɪn/',
    'producer': '/prəˈdjuːsə/',
    'consumer': '/kənˈsjuːmə/',
    'decomposer': '/ˌdiːkəmˈpəʊzə/',
    'herbivore': '/ˈhɜːbɪvɔː/',
    'carnivore': '/ˈkɑːnɪvɔː/',
    'omnivore': '/ˈɒmnɪvɔː/',
    'predator': '/ˈpredətə/',
    'prey': '/preɪ/',
    'symbiosis': '/ˌsɪmbaɪˈəʊsɪs/',
    'mutualism': '/ˈmjuːtʃuəlɪzəm/',
    'commensalism': '/kəˈmensəlɪzəm/',
    'parasitism': '/ˈpærəsaɪtɪzəm/',
    'competition': '/ˌkɒmpəˈtɪʃən/',
    'succession': '/səkˈseʃən/',
    'primary': '/ˈpraɪməri/',
    'secondary': '/ˈsekəndri/',
    'climax': '/ˈklaɪmæks/',
    'biodiversity': '/ˌbaɪəʊdaɪˈvɜːsəti/',
    'extinction': '/ɪkˈstɪŋkʃən/',
    'endangered': '/ɪnˈdeɪndʒəd/',
    'threatened': '/ˈθretnd/',
    'conservation': '/ˌkɒnsəˈveɪʃən/',
    'sustainability': '/səˌsteɪnəˈbɪləti/',
    'pollution': '/pəˈluːʃən/',
    'climate': '/ˈklaɪmət/',
    'change': '/tʃeɪndʒ/',
    'global': '/ˈɡləʊbl/',
    'warming': '/ˈwɔːmɪŋ/',
    'greenhouse': '/ˈɡriːnhaʊs/',
    'footprint': '/ˈfʊtprɪnt/',
    'renewable': '/rɪˈnjuːəbl/',
    'nonrenewable': '/ˌnɒnrɪˈnjuːəbl/',
    'resource': '/rɪˈsɔːs/',
    'element': '/ˈelɪmənt/',
    'compound': '/ˈkɒmpaʊnd/',
    'molecule': '/ˈmɒlɪkjuːl/',
    'atom': '/ˈætəm/',
    'proton': '/ˈprəʊtɒn/',
    'neutron': '/ˈnjuːtrɒn/',
    'electron': '/ɪˈlektrɒn/',
    'ion': '/ˈaɪən/',
    'isotope': '/ˈaɪsətəʊp/',
    'bond': '/bɒnd/',
    'covalent': '/kəʊˈveɪlənt/',
    'ionic': '/aɪˈɒnɪk/',
    'hydrogen': '/ˈhaɪdrədʒən/',
    'solution': '/səˈluːʃən/',
    'solute': '/ˈsɒljuːt/',
    'solvent': '/ˈsɒlvənt/',
    'acid': '/ˈæsɪd/',
    'base': '/beɪs/',
    'ph': '/piː eɪtʃ/',
    'buffer': '/ˈbʌfə/',
    'catalyst': '/ˈkætəlɪst/',
    'enzyme': '/ˈenzaɪm/',
    'substrate': '/ˈsʌbstreɪt/',
    'active': '/ˈæktɪv/',
    'site': '/saɪt/',
    'kinetic': '/kɪˈnetɪk/',
    'potential': '/pəˈtenʃl/',
    'energy': '/ˈenədʒi/',
    'thermodynamics': '/ˌθɜːməʊdaɪˈnæmɪks/',
    'entropy': '/ˈentrəpi/',
    'enthalpy': '/ˈenθælpi/',
    'free': '/friː/',
    'gibbs': '/ɡɪbz/',
    'reaction': '/riˈækʃən/',
    'equilibrium': '/ˌiːkwɪˈlɪbriəm/',
    'rate': '/reɪt/',
    'constant': '/ˈkɒnstənt/',
    'activation': '/ˌæktɪˈveɪʃən/',
    'hypothesis': '/haɪˈpɒθəsɪs/',
    'theory': '/ˈθɪəri/',
    'law': '/lɔː/',
    'experiment': '/ɪkˈsperɪmənt/',
    'control': '/kənˈtrəʊl/',
    'variable': '/ˈveəriəbl/',
    'independent': '/ˌɪndɪˈpendənt/',
    'dependent': '/dɪˈpendənt/',
    'data': '/ˈdeɪtə/',
    'quantitative': '/ˈkwɒntɪtətɪv/',
    'qualitative': '/ˈkwɒlɪtətɪv/',
    'observation': '/ˌɒbzəˈveɪʃən/',
    'inference': '/ˈɪnfərəns/',
    'conclusion': '/kənˈkluːʒn/',
    'peer': '/pɪə/',
    'review': '/rɪˈvjuː/',
    'replicate': '/ˈreplɪkeɪt/',
    'valid': '/ˈvælɪd/',
    'reliable': '/rɪˈlaɪəbl/',
    'accurate': '/ˈækjərət/',
    'precision': '/prɪˈsɪʒn/',
    'error': '/ˈerə/',
    'margin': '/ˈmɑːdʒɪn/',
    'evolution': '/ˌiːvəˈluːʃən/',
    'adaptation': '/ˌædæpˈteɪʃən/',
    'natural': '/ˈnætʃrəl/',
    'selection': '/sɪˈlekʃən/',
    'fitness': '/ˈfɪtnəs/',
    'variation': '/ˌveəriˈeɪʃən/',
    'speciation': '/ˌspiːʃiˈeɪʃən/',
    'extinct': '/ɪkˈstɪŋkt/',
    'fossil': '/ˈfɒsl/',
    'radiometric': '/ˌreɪdiəˈmetrɪk/',
    'dating': '/ˈdeɪtɪŋ/',
    'relative': '/ˈrelətɪv/',
    'absolute': '/ˈæbsəluːt/',
    'age': '/eɪdʒ/',
    'homologous': '/həˈmɒləɡəs/',
    'analogous': '/əˈnæl ə ɡəs/',
    'vestigial': '/veˈstɪdʒiəl/',
    'structure': '/ˈstrʌktʃə/',
    'embryology': '/ˌembriˈɒlədʒi/',
    'biogeography': '/ˌbaɪəʊdʒiˈɒɡrəfi/',
    'molecular': '/məˈlekjʊlə/',
    'clock': '/klɒk/',
    'phylogeny': '/faɪˈlɒdʒəni/',
    'cladogram': '/ˈklædəɡræm/',
    'taxonomy': '/tækˈsɒnəmi/',
    'binomial': '/baɪˈnəʊmiəl/',
    'nomenclature': '/nəˈmenklətʃə/',
    'domain': '/dəˈmeɪn/',
    'kingdom': '/ˈkɪŋdəm/',
    'phylum': '/ˈfaɪləm/',
    'class': '/klɑːs/',
    'order': '/ˈɔːdə/',
    'family': '/ˈfæməli/',
    'genus': '/ˈdʒiːnəs/',
    'species': '/ˈspiːʃiːz/',
    'scientific': '/ˌsaɪənˈtɪfɪk/',
    'name': '/neɪm/'
  };
  
  // 学术词汇的中文翻译映射表（确保翻译地道准确）
  const wordTranslations = {
    'digestive': '消化的',
    'pancreas': '胰腺',
    'gallbladder': '胆囊',
    'liver': '肝脏',
    'enzymes': '酶',
    'hormones': '激素',
    'stomach': '胃',
    'intestine': '肠',
    'esophagus': '食管',
    'rectum': '直肠',
    'anus': '肛门',
    'saliva': '唾液',
    'chyme': '食糜',
    'bile': '胆汁',
    'villi': '绒毛',
    'microvilli': '微绒毛',
    'peristalsis': '蠕动',
    'digestion': '消化',
    'absorption': '吸收',
    'assimilation': '同化',
    'ghrelin': '饥饿激素',
    'leptin': '瘦素',
    'insulin': '胰岛素',
    'glucagon': '胰高血糖素',
    'cell': '细胞',
    'nucleus': '细胞核',
    'mitochondria': '线粒体',
    'ribosome': '核糖体',
    'cytoplasm': '细胞质',
    'membrane': '膜',
    'organelle': '细胞器',
    'chromosome': '染色体',
    'dna': '脱氧核糖核酸',
    'rna': '核糖核酸',
    'gene': '基因',
    'allele': '等位基因',
    'genome': '基因组',
    'protein': '蛋白质',
    'amino': '氨基',
    'acid': '酸',
    'lipid': '脂质',
    'carbohydrate': '碳水化合物',
    'glucose': '葡萄糖',
    'fructose': '果糖',
    'sucrose': '蔗糖',
    'lactose': '乳糖',
    'glycogen': '糖原',
    'starch': '淀粉',
    'cellulose': '纤维素',
    'chlorophyll': '叶绿素',
    'photosynthesis': '光合作用',
    'respiration': '呼吸作用',
    'fermentation': '发酵',
    'glycolysis': '糖酵解',
    'krebs': 'Krebs循环',
    'electron': '电子',
    'transport': '运输',
    'atp': '三磷酸腺苷',
    'adp': '二磷酸腺苷',
    'phosphate': '磷酸盐',
    'oxidation': '氧化',
    'reduction': '还原',
    'circulatory': '循环的',
    'cardiovascular': '心血管的',
    'respiratory': '呼吸的',
    'nervous': '神经的',
    'endocrine': '内分泌的',
    'immune': '免疫的',
    'lymphatic': '淋巴的',
    'skeletal': '骨骼的',
    'muscular': '肌肉的',
    'integumentary': '皮肤的',
    'urinary': '泌尿的',
    'reproductive': '生殖的',
    'artery': '动脉',
    'vein': '静脉',
    'capillary': '毛细血管',
    'heart': '心脏',
    'aorta': '主动脉',
    'ventricle': '心室',
    'atrium': '心房',
    'valve': '瓣膜',
    'blood': '血液',
    'plasma': '血浆',
    'erythrocyte': '红细胞',
    'leukocyte': '白细胞',
    'platelet': '血小板',
    'hemoglobin': '血红蛋白',
    'oxygen': '氧气',
    'carbon': '碳',
    'dioxide': '二氧化物',
    'pharynx': '咽',
    'larynx': '喉',
    'trachea': '气管',
    'bronchus': '支气管',
    'alveolus': '肺泡',
    'lung': '肺',
    'diaphragm': '膈肌',
    'neuron': '神经元',
    'synapse': '突触',
    'neurotransmitter': '神经递质',
    'spinal': '脊髓的',
    'cord': '脊髓',
    'brain': '大脑',
    'cerebrum': '大脑皮层',
    'cerebellum': '小脑',
    'medulla': '延髓',
    'hypothalamus': '下丘脑',
    'pituitary': '垂体',
    'thyroid': '甲状腺',
    'adrenal': '肾上腺',
    'thymus': '胸腺',
    'spleen': '脾脏',
    'lymph': '淋巴',
    'node': '淋巴结',
    'antibody': '抗体',
    'antigen': '抗原',
    'vaccine': '疫苗',
    'pathogen': '病原体',
    'bacteria': '细菌',
    'virus': '病毒',
    'fungi': '真菌',
    'parasite': '寄生虫',
    'infection': '感染',
    'inflammation': '炎症',
    'immunity': '免疫',
    'ecosystem': '生态系统',
    'habitat': '栖息地',
    'niche': '生态位',
    'population': '种群',
    'community': '群落',
    'biosphere': '生物圈',
    'trophic': '营养的',
    'level': '水平',
    'food': '食物',
    'web': '网',
    'chain': '链',
    'producer': '生产者',
    'consumer': '消费者',
    'decomposer': '分解者',
    'herbivore': '草食动物',
    'carnivore': '肉食动物',
    'omnivore': '杂食动物',
    'predator': '捕食者',
    'prey': '猎物',
    'symbiosis': '共生',
    'mutualism': '互利共生',
    'commensalism': '偏利共生',
    'parasitism': '寄生',
    'competition': '竞争',
    'succession': '演替',
    'primary': '初级的',
    'secondary': '次级的',
    'climax': '顶极群落',
    'biodiversity': '生物多样性',
    'extinction': '灭绝',
    'endangered': '濒危的',
    'threatened': '受威胁的',
    'conservation': '保护',
    'sustainability': '可持续性',
    'pollution': '污染',
    'climate': '气候',
    'change': '变化',
    'global': '全球的',
    'warming': '变暖',
    'greenhouse': '温室',
    'footprint': '足迹',
    'renewable': '可再生的',
    'nonrenewable': '不可再生的',
    'resource': '资源',
    'element': '元素',
    'compound': '化合物',
    'molecule': '分子',
    'atom': '原子',
    'proton': '质子',
    'neutron': '中子',
    'electron': '电子',
    'ion': '离子',
    'isotope': '同位素',
    'bond': '键',
    'covalent': '共价的',
    'ionic': '离子的',
    'hydrogen': '氢',
    'solution': '溶液',
    'solute': '溶质',
    'solvent': '溶剂',
    'acid': '酸',
    'base': '碱',
    'ph': '酸碱度',
    'buffer': '缓冲液',
    'catalyst': '催化剂',
    'enzyme': '酶',
    'substrate': '底物',
    'active': '活性的',
    'site': '位点',
    'kinetic': '动能的',
    'potential': '势能的',
    'energy': '能量',
    'thermodynamics': '热力学',
    'entropy': '熵',
    'enthalpy': '焓',
    'free': '自由的',
    'gibbs': '吉布斯',
    'reaction': '反应',
    'equilibrium': '平衡',
    'rate': '速率',
    'constant': '常数',
    'activation': '激活',
    'hypothesis': '假设',
    'theory': '理论',
    'law': '定律',
    'experiment': '实验',
    'control': '对照',
    'variable': '变量',
    'independent': '自变量',
    'dependent': '因变量',
    'data': '数据',
    'quantitative': '定量的',
    'qualitative': '定性的',
    'observation': '观察',
    'inference': '推理',
    'conclusion': '结论',
    'peer': '同行',
    'review': '评审',
    'replicate': '复制',
    'valid': '有效的',
    'reliable': '可靠的',
    'accurate': '准确的',
    'precision': '精确性',
    'error': '误差',
    'margin': '误差范围',
    'evolution': '进化',
    'adaptation': '适应',
    'natural': '自然的',
    'selection': '选择',
    'fitness': '适合度',
    'variation': '变异',
    'speciation': '物种形成',
    'extinct': '灭绝的',
    'fossil': '化石',
    'radiometric': '放射性测定的',
    'dating': '年代测定',
    'relative': '相对的',
    'absolute': '绝对的',
    'age': '年龄',
    'homologous': '同源的',
    'analogous': '同功的',
    'vestigial': '退化的',
    'structure': '结构',
    'embryology': '胚胎学',
    'biogeography': '生物地理学',
    'molecular': '分子的',
    'clock': '时钟',
    'phylogeny': '系统发育',
    'cladogram': '进化树',
    'taxonomy': '分类学',
    'binomial': '双名的',
    'nomenclature': '命名法',
    'domain': '域',
    'kingdom': '界',
    'phylum': '门',
    'class': '纲',
    'order': '目',
    'family': '科',
    'genus': '属',
    'species': '种',
    'scientific': '科学的',
    'name': '名称',
    // 额外添加的常用学术词汇
    'method': '方法',
    'technique': '技术',
    'procedure': '程序',
    'protocol': '协议',
    'analysis': '分析',
    'analyze': '分析',
    'synthesize': '合成',
    'synthesis': '合成',
    'decompose': '分解',
    'decomposition': '分解',
    'interaction': '相互作用',
    'relationship': '关系',
    'correlation': '相关性',
    'causation': '因果关系',
    'mechanism': '机制',
    'process': '过程',
    'system': '系统',
    'function': '功能',
    'property': '性质',
    'characteristic': '特征',
    'trait': '性状',
    'feature': '特征',
    'phenomenon': '现象',
    'occurrence': '发生',
    'event': '事件',
    'condition': '条件',
    'environment': '环境',
    'surrounding': '周围环境',
    'context': '上下文',
    'background': '背景',
    'foundation': '基础',
    'principle': '原理',
    'concept': '概念',
    'idea': '思想',
    'prediction': '预测',
    'expectation': '期望',
    'result': '结果',
    'outcome': '结果',
    'consequence': '结果',
    'effect': '效果',
    'impact': '影响',
    'influence': '影响',
    'significance': '意义',
    'importance': '重要性',
    'relevance': '相关性',
    'application': '应用',
    'use': '使用',
    'utility': '效用',
    'value': '价值',
    'benefit': '益处',
    'advantage': '优势',
    'disadvantage': '劣势',
    'limitation': '限制',
    'constraint': '约束',
    'challenge': '挑战',
    'problem': '问题',
    'issue': '问题',
    'question': '问题',
    'inquiry': '探究',
    'investigation': '调查',
    'research': '研究',
    'study': '研究',
    'test': '测试',
    'trial': '试验',
    'sample': '样本',
    'statistic': '统计量',
    'parameter': '参数',
    'probability': '概率',
    'likelihood': '可能性',
    'chance': '机会',
    'risk': '风险',
    'uncertainty': '不确定性',
    'mistake': '错误',
    'fault': '错误',
    'flaw': '缺陷',
    'defect': '缺陷',
    'imperfection': '不完善',
    'perfect': '完美的',
    'ideal': '理想的',
    'optimal': '最优的',
    'maximum': '最大的',
    'minimum': '最小的',
    'average': '平均的',
    'mean': '均值',
    'median': '中位数',
    'mode': '众数',
    'range': '范围',
    'interval': '区间',
    'scope': '范围',
    'extent': '程度',
    'degree': '程度',
    'magnitude': '大小',
    'size': '大小',
    'scale': '规模',
    'dimension': '维度',
    'measure': '测量',
    'measurement': '测量',
    'metric': '度量',
    'unit': '单位',
    'standard': '标准',
    'norm': '规范',
    'criterion': '标准',
    'benchmark': '基准',
    'reference': '参考',
    'source': '来源',
    'origin': '起源',
    'root': '根源',
    'cause': '原因',
    'reason': '原因',
    'explanation': '解释',
    'justification': '理由',
    'rationale': '原理',
    'logic': '逻辑',
    'reasoning': '推理',
    'argument': '论证',
    'evidence': '证据',
    'proof': '证明',
    'confirmation': '确认',
    'verification': '验证',
    'validation': '验证',
    'authentication': '认证',
    'identification': '识别',
    'recognition': '识别',
    'detection': '检测',
    'discovery': '发现',
    'exploration': '探索',
    'examination': '检查',
    'inspection': '检查',
    'monitoring': '监测',
    'surveillance': '监视',
    'tracking': '跟踪',
    'recording': '记录',
    'documentation': '文档',
    'reporting': '报告',
    'communication': '交流',
    'transmission': '传输',
    'transfer': '转移',
    'movement': '移动',
    'motion': '运动',
    'kinetics': '动力学',
    'dynamics': '动力学',
    'static': '静态的',
    'dynamic': '动态的',
    'stable': '稳定的',
    'unstable': '不稳定的',
    'balance': '平衡',
    'imbalance': '不平衡',
    'homeostasis': '内稳态',
    'stability': '稳定性',
    'instability': '不稳定性',
    'transformation': '转变',
    'conversion': '转换',
    'transition': '过渡',
    'development': '发展',
    'growth': '生长',
    'expansion': '扩展',
    'contraction': '收缩',
    'increase': '增加',
    'decrease': '减少',
    'rise': '上升',
    'fall': '下降',
    'improvement': '改进',
    'deterioration': '恶化',
    'enhancement': '增强',
    'amplification': '放大',
    'attenuation': '衰减',
    'intensification': '强化',
    'weakening': '减弱',
    'strengthening': '加强',
    'optimization': '优化',
    'maximization': '最大化',
    'minimization': '最小化',
    'efficiency': '效率',
    'effectiveness': '有效性',
    'productivity': '生产力',
    'performance': '性能',
    'functionality': '功能',
    'capability': '能力',
    'capacity': '容量',
    'possibility': '可能性',
    'feasibility': '可行性',
    'viability': '可行性',
    'durability': '耐久性',
    'reliability': '可靠性',
    'consistency': '一致性',
    'uniformity': '均匀性',
    'homogeneity': '同质性',
    'heterogeneity': '异质性',
    'diversity': '多样性',
    'variety': '多样性',
    'complexity': '复杂性',
    'simplicity': '简单性',
    'sophistication': '复杂性',
    'elegance': '优雅',
    'accuracy': '准确性',
    'exactness': '精确性',
    'correctness': '正确性',
    'validity': '有效性',
    'legitimacy': '合法性',
    'authenticity': '真实性',
    'genuineness': '真实性',
    'originality': '原创性',
    'novelty': '新颖性',
    'innovation': '创新',
    'creativity': '创造力'
  };

  // 常见的自然科学/生物/医学专业词汇（用于自动提取）
  const commonAcademicWords = [
    // 消化系统
    'digestive', 'pancreas', 'gallbladder', 'liver', 'enzymes', 'hormones',
    'stomach', 'intestine', 'esophagus', 'rectum', 'anus', 'saliva',
    'chyme', 'bile', 'villi', 'microvilli', 'peristalsis', 'digestion',
    'absorption', 'assimilation', 'ghrelin', 'leptin', 'insulin', 'glucagon',
    
    // 细胞和生物学
    'cell', 'nucleus', 'mitochondria', 'ribosome', 'cytoplasm', 'membrane',
    'organelle', 'chromosome', 'dna', 'rna', 'gene', 'allele', 'genome',
    'protein', 'amino', 'acid', 'lipid', 'carbohydrate', 'glucose', 'fructose',
    'sucrose', 'lactose', 'glycogen', 'starch', 'cellulose', 'chlorophyll',
    'photosynthesis', 'respiration', 'fermentation', 'glycolysis', 'krebs',
    'electron', 'transport', 'atp', 'adp', 'phosphate', 'oxidation', 'reduction',
    
    // 人体系统
    'circulatory', 'cardiovascular', 'respiratory', 'nervous', 'endocrine',
    'immune', 'lymphatic', 'skeletal', 'muscular', 'integumentary', 'urinary',
    'reproductive', 'artery', 'vein', 'capillary', 'heart', 'aorta', 'ventricle',
    'atrium', 'valve', 'blood', 'plasma', 'erythrocyte', 'leukocyte', 'platelet',
    'hemoglobin', 'oxygen', 'carbon', 'dioxide', 'pharynx', 'larynx', 'trachea',
    'bronchus', 'alveolus', 'lung', 'diaphragm', 'neuron', 'synapse', 'neurotransmitter',
    'spinal', 'cord', 'brain', 'cerebrum', 'cerebellum', 'medulla', 'hypothalamus',
    'pituitary', 'thyroid', 'adrenal', 'thymus', 'spleen', 'lymph', 'node',
    'antibody', 'antigen', 'vaccine', 'pathogen', 'bacteria', 'virus', 'fungi',
    'parasite', 'infection', 'inflammation', 'immunity',
    
    // 生态学和环境
    'ecosystem', 'habitat', 'niche', 'population', 'community', 'biosphere',
    'trophic', 'level', 'food', 'web', 'chain', 'producer', 'consumer',
    'decomposer', 'herbivore', 'carnivore', 'omnivore', 'predator', 'prey',
    'symbiosis', 'mutualism', 'commensalism', 'parasitism', 'competition',
    'succession', 'primary', 'secondary', 'climax', 'community', 'biodiversity',
    'extinction', 'endangered', 'threatened', 'conservation', 'sustainability',
    'pollution', 'climate', 'change', 'global', 'warming', 'greenhouse',
    'carbon', 'footprint', 'renewable', 'nonrenewable', 'resource',
    
    // 化学和物理
    'element', 'compound', 'molecule', 'atom', 'proton', 'neutron', 'electron',
    'ion', 'isotope', 'bond', 'covalent', 'ionic', 'hydrogen', 'solution',
    'solute', 'solvent', 'acid', 'base', 'ph', 'buffer', 'catalyst',
    'enzyme', 'substrate', 'active', 'site', 'kinetic', 'potential', 'energy',
    'thermodynamics', 'entropy', 'enthalpy', 'free', 'gibbs', 'reaction',
    'equilibrium', 'rate', 'constant', 'activation',
    
    // 科学方法
    'hypothesis', 'theory', 'law', 'experiment', 'control', 'variable',
    'independent', 'dependent', 'data', 'quantitative', 'qualitative',
    'observation', 'inference', 'conclusion', 'peer', 'review', 'replicate',
    'valid', 'reliable', 'accurate', 'precision', 'error', 'margin',
    
    // 其他专业词汇
    'evolution', 'adaptation', 'natural', 'selection', 'fitness', 'variation',
    'speciation', 'extinct', 'fossil', 'radiometric', 'dating', 'relative',
    'absolute', 'age', 'homologous', 'analogous', 'vestigial', 'structure',
    'embryology', 'biogeography', 'molecular', 'clock', 'phylogeny', 'cladogram',
    'taxonomy', 'binomial', 'nomenclature', 'domain', 'kingdom', 'phylum',
    'class', 'order', 'family', 'genus', 'species', 'scientific', 'name'
  ];

  // 常见的地道表达/固定搭配
  const commonExpressions = [
    'in terms of', 'in order to', 'in addition to', 'as a result', 'due to',
    'according to', 'on the other hand', 'in contrast', 'for example', 'such as',
    'as well as', 'along with', 'together with', 'in fact', 'in reality', 'in practice',
    'in theory', 'on average', 'at least', 'at most', 'at present', 'at the same time',
    'by the way', 'in the way', 'on the way', 'in a way', 'in some ways', 'in many ways',
    'first of all', 'above all', 'after all', 'all in all', 'in all', 'at all', 'not at all',
    'in general', 'generally speaking', 'on the whole', 'as a whole', 'in particular',
    'in detail', 'in short', 'in brief', 'in conclusion', 'in summary', 'to sum up',
    'in other words', 'that is to say', 'namely', 'for instance', 'take for example',
    'more or less', 'sooner or later', 'now and then', 'here and there', 'back and forth',
    'up and down', 'again and again', 'over and over', 'time and again', 'once and for all',
    'little by little', 'step by step', 'one by one', 'side by side', 'face to face',
    'hand in hand', 'arm in arm', 'shoulder to shoulder', 'heart to heart', 'eye to eye',
    'day by day', 'day after day', 'year by year', 'year after year', 'day in and day out',
    'from time to time', 'from now on', 'from then on', 'from beginning to end',
    'from start to finish', 'from head to toe', 'from top to bottom', 'from inside out',
    'from outside in', 'out of date', 'up to date', 'out of order', 'in order',
    'out of sight', 'in sight', 'out of mind', 'in mind', 'out of breath', 'out of control',
    'out of danger', 'out of date', 'out of doors', 'out of fashion', 'out of favor',
    'out of hand', 'out of luck', 'out of order', 'out of place', 'out of practice',
    'out of reach', 'out of season', 'out of sight', 'out of step', 'out of touch',
    'out of work', 'in trouble', 'in danger', 'in debt', 'in love', 'in tears', 'in vain',
    'in public', 'in private', 'in secret', 'in person', 'in theory', 'in practice',
    'in principle', 'in fact', 'in reality', 'in truth', 'in effect', 'in force',
    'in stock', 'out of stock', 'in stock', 'on sale', 'for sale', 'on display',
    'on show', 'on exhibition', 'on view', 'in view', 'in sight', 'out of sight',
    'at hand', 'by hand', 'in hand', 'on hand', 'off hand', 'from hand to mouth',
    'hand in hand', 'hand over fist', 'hand to hand', 'heart in hand', 'life in hand',
    'time in hand', 'money in hand', 'future in hand', 'fate in hand', 'destiny in hand'
  ];

  // 常见的动词短语
  const commonPhrasalVerbs = [
    'break down', 'break up', 'break in', 'break out', 'break off', 'break through',
    'bring up', 'bring about', 'bring in', 'bring out', 'bring back', 'bring forward',
    'call up', 'call off', 'call on', 'call in', 'call for', 'call back',
    'come up', 'come in', 'come out', 'come on', 'come across', 'come along',
    'cut down', 'cut off', 'cut out', 'cut in', 'cut up', 'cut back',
    'do up', 'do in', 'do over', 'do without', 'do away with', 'do with',
    'figure out', 'figure on', 'figure in', 'figure up', 'find out', 'find in',
    'get up', 'get in', 'get out', 'get on', 'get off', 'get over', 'get through',
    'give up', 'give in', 'give out', 'give away', 'give back', 'give off',
    'go up', 'go in', 'go out', 'go on', 'go off', 'go over', 'go through',
    'keep up', 'keep in', 'keep out', 'keep on', 'keep off', 'keep back',
    'look up', 'look in', 'look out', 'look on', 'look into', 'look after',
    'make up', 'make out', 'make for', 'make off', 'make over', 'make up for',
    'pick up', 'pick out', 'pick off', 'pick on', 'pick at', 'pick over',
    'put up', 'put in', 'put out', 'put on', 'put off', 'put down', 'put away',
    'set up', 'set in', 'set out', 'set off', 'set down', 'set back', 'set about',
    'take up', 'take in', 'take out', 'take on', 'take off', 'take down', 'take over',
    'turn up', 'turn in', 'turn out', 'turn on', 'turn off', 'turn down', 'turn over'
  ];

  // 停用词列表（不提取这些常见词）
  const stopWords = new Set([
    'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for',
    'of', 'with', 'without', 'by', 'from', 'up', 'down', 'over', 'under',
    'again', 'further', 'then', 'once', 'here', 'there', 'when', 'where',
    'why', 'how', 'all', 'any', 'both', 'each', 'few', 'more', 'most',
    'other', 'some', 'such', 'no', 'nor', 'not', 'only', 'own', 'same',
    'so', 'than', 'too', 'very', 'just', 'also', 'now', 'here', 'there',
    'this', 'that', 'these', 'those', 'i', 'you', 'he', 'she', 'it', 'we',
    'they', 'me', 'him', 'her', 'us', 'them', 'my', 'your', 'his', 'its',
    'our', 'their', 'mine', 'yours', 'hers', 'ours', 'theirs', 'what',
    'which', 'who', 'whom', 'whose', 'is', 'am', 'are', 'was', 'were',
    'be', 'been', 'being', 'have', 'has', 'had', 'having', 'do', 'does',
    'did', 'doing', 'will', 'would', 'could', 'should', 'may', 'might',
    'must', 'can', 'need', 'dare', 'ought', 'used', 'better', 'let',
    'say', 'said', 'says', 'go', 'went', 'gone', 'going', 'come', 'came',
    'come', 'coming', 'get', 'got', 'got', 'getting', 'make', 'made',
    'made', 'making', 'know', 'knew', 'known', 'knowing', 'think', 'thought',
    'thought', 'thinking', 'see', 'saw', 'seen', 'seeing', 'want', 'wanted',
    'wanted', 'wanting', 'use', 'used', 'used', 'using', 'find', 'found',
    'found', 'finding', 'give', 'gave', 'given', 'giving', 'tell', 'told',
    'told', 'telling', 'ask', 'asked', 'asked', 'asking', 'seem', 'seemed',
    'seemed', 'seeming', 'feel', 'felt', 'felt', 'feeling', 'try', 'tried',
    'tried', 'trying', 'leave', 'left', 'left', 'leaving', 'call', 'called',
    'called', 'calling', 'work', 'worked', 'worked', 'working', 'need',
    'needed', 'needed', 'needing', 'feel', 'like', 'well', 'back', 'even',
    'still', 'never', 'time', 'look', 'only', 'come', 'over', 'think',
    'also', 'back', 'well', 'way', 'even', 'new', 'want', 'because', 'any',
    'these', 'give', 'day', 'most', 'us'
  ]);

  // 获取短语的中文翻译（带超时和错误处理）
  const fetchPhraseTranslation = async (phrase) => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      const translateResponse = await fetch(
        `https://api.mymemory.translated.net/get?q=${encodeURIComponent(phrase)}&langpair=en|zh-CN`,
        { signal: controller.signal }
      );
      clearTimeout(timeoutId);
      
      if (translateResponse.ok) {
        const translateData = await translateResponse.json();
        if (translateData.responseStatus === 200 && translateData.responseData) {
          return translateData.responseData.translatedText;
        }
      }
    } catch (error) {
      console.log('短语翻译失败（国内网络可能无法访问）:', phrase, error);
    }
    return '无中文翻译';
  };

  // 获取单词的词性和中文翻译（带超时和错误处理）
  const fetchWordDefinition = async (word) => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      const response = await fetch(
        `https://api.dictionaryapi.dev/api/v2/entries/en/${encodeURIComponent(word)}`,
        { signal: controller.signal }
      );
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        if (data.length > 0 && data[0].meanings) {
          const firstMeaning = data[0].meanings[0];
          const partOfSpeech = firstMeaning.partOfSpeech || '';
          const definition = firstMeaning.definitions && firstMeaning.definitions[0] ? 
            firstMeaning.definitions[0].definition : '';
          let phonetic = data[0].phonetic || '';
          
          // 如果API没有提供音标，从映射表中获取
          if (!phonetic && wordPhonetics[word.toLowerCase()]) {
            phonetic = wordPhonetics[word.toLowerCase()];
          }
          
          // 获取中文翻译（优先使用映射表，确保地道准确）
          let chineseTranslation = wordTranslations[word.toLowerCase()] || '';
          
          // 如果映射表中没有，尝试使用API翻译
          if (!chineseTranslation) {
            try {
              const translateController = new AbortController();
              const translateTimeoutId = setTimeout(() => translateController.abort(), 5000);
              
              const translateResponse = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(word)}&langpair=en|zh-CN`,
                { signal: translateController.signal }
              );
              clearTimeout(translateTimeoutId);
              
              if (translateResponse.ok) {
                const translateData = await translateResponse.json();
                if (translateData.responseStatus === 200 && translateData.responseData) {
                  chineseTranslation = translateData.responseData.translatedText;
                }
              }
            } catch (translateError) {
              console.log('翻译API调用失败（国内网络可能无法访问），请点击欧陆词典查询');
            }
          }
          
          return {
            partOfSpeech,
            definition: definition || '无英文定义',
            chineseTranslation: chineseTranslation || '无中文翻译',
            phonetic
          };
        }
      }
    } catch (error) {
      console.error('词典API调用失败（国内网络可能无法访问）:', error);
    }
    
    // API调用失败时，从映射表中获取音标和翻译
    const phoneticFromMap = wordPhonetics[word.toLowerCase()] || '';
    const chineseTranslationFromMap = wordTranslations[word.toLowerCase()] || '无中文翻译';
    return { partOfSpeech: '', definition: '无英文定义', chineseTranslation: chineseTranslationFromMap, phonetic: phoneticFromMap };
  };

  // 选择中等长度句子的辅助函数
  const selectMediumLengthSentence = (sentences) => {
    if (sentences.length === 0) return null;
    if (sentences.length === 1) return sentences[0];
    
    // 按句子长度排序
    const sorted = [...sentences].sort((a, b) => a.text.length - b.text.length);
    
    // 选择中间位置的句子
    const middleIndex = Math.floor(sorted.length / 2);
    return sorted[middleIndex];
  };

  // 从字幕中自动提取词汇的函数
  const extractVocabularyFromSubtitles = (subtitles) => {
    const wordSentences = new Map(); // 存储每个单词的所有句子
    const phraseSentences = new Map(); // 存储每个短语的所有句子
    const expressionSentences = new Map(); // 存储每个表达的所有句子
    
    // 第一遍：收集所有词汇的所有出现句子
    subtitles.forEach((subtitle, index) => {
      const text = subtitle.english.toLowerCase();
      const words = text.match(/\b[a-zA-Z]+(?:['’][a-zA-Z]+)?\b/g) || [];
      
      // 收集单词的所有句子
      words.forEach((word) => {
        const cleanWord = word.toLowerCase().replace(/[^a-z]/g, '');
        if (cleanWord.length > 3 && !stopWords.has(cleanWord)) {
          if (!wordSentences.has(cleanWord)) {
            wordSentences.set(cleanWord, []);
          }
          wordSentences.get(cleanWord).push({
            text: subtitle.english,
            chinese: subtitle.chinese || '',
            index: index
          });
        }
      });
      
      // 收集短语的所有句子
      commonPhrasalVerbs.forEach((phrase) => {
        if (text.includes(phrase)) {
          if (!phraseSentences.has(phrase)) {
            phraseSentences.set(phrase, []);
          }
          phraseSentences.get(phrase).push({
            text: subtitle.english,
            chinese: subtitle.chinese || '',
            index: index
          });
        }
      });
      
      // 收集地道表达的所有句子
      commonExpressions.forEach((expr) => {
        if (text.includes(expr)) {
          if (!expressionSentences.has(expr)) {
            expressionSentences.set(expr, []);
          }
          expressionSentences.get(expr).push({
            text: subtitle.english,
            chinese: subtitle.chinese || '',
            index: index
          });
        }
      });
    });
    
    // 第二遍：创建词汇对象，选择中等长度的句子
    const extractedWords = new Map();
    const extractedPhrases = new Map();
    const extractedExpressions = new Map();
    
    // 处理单词
    wordSentences.forEach((sentences, cleanWord) => {
      const isAcademic = commonAcademicWords.includes(cleanWord);
      const selectedSentence = selectMediumLengthSentence(sentences);
      
      extractedWords.set(cleanWord, {
        id: Date.now() + Math.random(),
        word: cleanWord,
        phonetic: '',
        definition: '',
        synonyms: '',
        sentence: selectedSentence.text,
        sentenceChinese: selectedSentence.chinese,
        status: 'unmarked',
        isNonfiction: isAcademic,
        source: 'auto',
        subtitleIndex: selectedSentence.index
      });
    });
    
    // 处理短语
    phraseSentences.forEach((sentences, phrase) => {
      const selectedSentence = selectMediumLengthSentence(sentences);
      
      extractedPhrases.set(phrase, {
        id: Date.now() + Math.random(),
        phrase: phrase,
        definition: '',
        sentence: selectedSentence.text,
        sentenceChinese: selectedSentence.chinese,
        status: 'unmarked',
        source: 'auto',
        subtitleIndex: selectedSentence.index
      });
    });
    
    // 处理地道表达
    expressionSentences.forEach((sentences, expr) => {
      const selectedSentence = selectMediumLengthSentence(sentences);
      
      extractedExpressions.set(expr, {
        id: Date.now() + Math.random(),
        expression: expr,
        definition: '',
        sentence: selectedSentence.text,
        sentenceChinese: selectedSentence.chinese,
        status: 'unmarked',
        source: 'auto',
        subtitleIndex: selectedSentence.index
      });
    });
    
    return {
      words: Array.from(extractedWords.values()).sort((a, b) => a.word.localeCompare(b.word)),
      phrases: Array.from(extractedPhrases.values()).sort((a, b) => a.phrase.localeCompare(b.phrase)),
      expressions: Array.from(extractedExpressions.values()).sort((a, b) => a.expression.localeCompare(b.expression))
    };
  };

  // 分析字幕词汇
  const analyzeVocabulary = async (subtitleContent) => {
    if (!subtitleContent) return;
    
    setAnalyzingVocabulary(true);
    setVocabularyError(null);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/vocabulary/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ subtitleContent })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.analysis) {
          setVocabularyAnalysis(data.analysis);
        }
      } else {
        throw new Error('分析失败');
      }
    } catch (error) {
      console.error('词汇分析失败:', error);
      setVocabularyError('词汇分析失败，请刷新页面重试');
    } finally {
      setAnalyzingVocabulary(false);
    }
  };

  // 打开欧陆词典查询
  const openEudicDictionary = (word) => {
    const encodedWord = encodeURIComponent(word);
    window.open(`eudic://dict/${encodedWord}`, '_blank');
  };

  const renderAnnotatedText = (text) => {
    if (!text || !showAnnotations) {
      return text;
    }

    const allAnnotations = [
      ...phrasesData.map(p => ({ text: p.phrase, type: 'phrase' })),
      ...wordsData.map(w => ({ text: w.word, type: 'word' })),
      ...expressionsData.map(e => ({ text: e.expression, type: 'expression' }))
    ];

    if (allAnnotations.length === 0) {
      return text;
    }

    allAnnotations.sort((a, b) => b.text.length - a.text.length);

    const pattern = allAnnotations
      .map(item => {
        const escapedText = item.text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        if (item.type === 'phrase' || item.type === 'expression') {
          return `\\b${escapedText}\\b`;
        } else {
          return `\\b${escapedText}\\b`;
        }
      })
      .join('|');
    const annotationRegex = new RegExp(pattern, 'gi');

    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = annotationRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }
      
      parts.push(
        React.createElement('span', {
          key: 'match-' + match.index,
          style: {
            position: 'relative',
            display: 'inline-block',
            paddingBottom: '7px',
            lineHeight: '1.2'
          },
          children: [
            match[0],
            React.createElement('span', {
              key: 'underline-' + match.index,
              style: {
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '7px',
                backgroundColor: '#86EFAC',
                borderRadius: '2px',
                opacity: '0.9'
              }
            })
          ]
        })
      );
      
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return React.createElement(React.Fragment, null, parts);
  };

  // 保存不认识的单词到本地存储
  const saveUnknownWord = (word) => {
    try {
      const savedWords = JSON.parse((localStorage.getItem('unknownWords') || '[]').replace(/^"undefined"$/, '[]'));
      const wordExists = savedWords.some(w => w.word === word.word);
      if (!wordExists) {
        const newWord = {
          id: Date.now(),
          word: word.word,
          phonetic: word.phonetic || '',
          definition: word.definition || '',
          synonyms: word.synonyms || '',
          sentence: word.sentence || '',
          sentenceChinese: word.sentenceChinese || '',
          addedAt: new Date().toISOString()
        };
        savedWords.push(newWord);
        localStorage.setItem('unknownWords', JSON.stringify(savedWords));
        console.log('单词已保存到单词卡:', word.word);
      }
      updateWordStatus(word.id, 'unknown');
    } catch (error) {
      console.error('保存单词失败:', error);
      alert('保存单词失败，请重试');
    }
  };

  // 保存不认识的短语到本地存储
  const saveUnknownPhrase = (phrase) => {
    try {
      const savedPhrases = JSON.parse((localStorage.getItem('unknownPhrases') || '[]').replace(/^"undefined"$/, '[]'));
      const phraseExists = savedPhrases.some(p => p.phrase === phrase.phrase);
      if (!phraseExists) {
        const newPhrase = {
          id: Date.now(),
          phrase: phrase.phrase,
          phonetic: phrase.phonetic || '',
          partOfSpeech: phrase.partOfSpeech || '',
          chineseTranslation: phrase.chineseTranslation || '',
          definition: phrase.definition || '',
          synonyms: phrase.synonyms || '',
          sentence: phrase.sentence || '',
          sentenceChinese: phrase.sentenceChinese || '',
          addedAt: new Date().toISOString()
        };
        savedPhrases.push(newPhrase);
        localStorage.setItem('unknownPhrases', JSON.stringify(savedPhrases));
        console.log('短语已保存到短语卡:', phrase.phrase);
      }
      updatePhraseStatus(phrase.id, 'unknown');
    } catch (error) {
      console.error('保存短语失败:', error);
      alert('保存短语失败，请重试');
    }
  };

  // 保存不认识的自然科普词汇到本地存储
  const saveUnknownExpression = (expression) => {
    try {
      const savedExpressions = JSON.parse((localStorage.getItem('unknownExpressions') || '[]').replace(/^"undefined"$/, '[]'));
      const expressionExists = savedExpressions.some(e => e.expression === expression.expression);
      if (!expressionExists) {
        const newExpression = {
          id: Date.now(),
          expression: expression.expression,
          phonetic: expression.phonetic || '',
          partOfSpeech: expression.partOfSpeech || '',
          chineseTranslation: expression.chineseTranslation || '',
          definition: expression.definition || '',
          synonyms: expression.synonyms || '',
          sentence: expression.sentence || '',
          sentenceChinese: expression.sentenceChinese || '',
          addedAt: new Date().toISOString()
        };
        savedExpressions.push(newExpression);
        localStorage.setItem('unknownExpressions', JSON.stringify(savedExpressions));
        console.log('自然科普词汇已保存:', expression.expression);
      }
      updateExpressionStatus(expression.id, 'unknown');
    } catch (error) {
      console.error('保存自然科普词汇失败:', error);
      alert('保存自然科普词汇失败，请重试');
    }
  };

  // 辅助函数：为字幕添加 endTime
  const addEndTimeToSubtitles = (subtitles) => {
    return subtitles.map((subtitle, index) => {
      if (!subtitle.endTime) {
        const nextSubtitle = subtitles[index + 1];
        const endTime = nextSubtitle ? nextSubtitle.time - 0.05 : subtitle.time + 5; // 默认5秒
        return { ...subtitle, endTime };
      }
      return subtitle;
    });
  };

  // 跟读练习：播放原音
  const playOriginalAudio = async (subtitleIndex) => {
    try {
      const subtitle = subtitles[subtitleIndex];
      let video = null;
      if (isFloatingPlayerMode) {
        video = floatingVideoRef.current;
      } else {
        video = videoRef.current;
      }
      if (!subtitle || !video) return;
      
      // 给字幕时间添加足够的缓冲，避免边缘重叠
      const startTime = subtitle.time + 0.05;
      const endTime = subtitle.endTime ? (subtitle.endTime - 0.1) : (startTime + 4.9);
      
      // 检查是否是YouTube视频
      if (video.tagName === 'IFRAME') {
        // YouTube视频 - 使用YouTube API
        try {
          // 发送命令给YouTube iframe
          video.contentWindow.postMessage(JSON.stringify({
            event: 'command',
            func: 'seekTo',
            args: [startTime, true]
          }), '*');
          
          // 短暂延迟后开始播放
          setTimeout(() => {
            video.contentWindow.postMessage(JSON.stringify({
              event: 'command',
              func: 'playVideo',
              args: []
            }), '*');
          }, 200);
          
        } catch (error) {
          console.error('控制YouTube视频失败:', error);
          alert('控制YouTube视频失败，请重试');
        }
      } else {
        // 本地视频
        setPlayingSubtitleIndex(subtitleIndex);
        
        let stopAtEndTime = null;
        let seekComplete = false;
        
        // 清理之前的监听器
        video.removeEventListener('seeked', () => {});
        video.removeEventListener('timeupdate', () => {});
        
        // 监听seeked事件，确保seek完成后再播放
        const handleSeeked = () => {
          seekComplete = true;
          video.removeEventListener('seeked', handleSeeked);
          video.play().catch(error => {
            console.error('播放视频失败:', error);
            alert('播放视频失败，请重试');
            if (stopAtEndTime) {
              video.removeEventListener('timeupdate', stopAtEndTime);
            }
          });
        };
        
        // 监听timeupdate事件，在到达结束时间时暂停
        stopAtEndTime = () => {
          if (seekComplete && video.currentTime >= endTime) {
            video.pause();
            setIsPlaying(false);
            setPlayingSubtitleIndex(null);
            video.removeEventListener('timeupdate', stopAtEndTime);
          }
        };
        
        video.addEventListener('seeked', handleSeeked);
        video.addEventListener('timeupdate', stopAtEndTime);
        
        // 跳转到开始时间
        video.currentTime = startTime;
      }
    } catch (e) {
      console.error('播放原音失败:', e);
      alert('播放原音失败，请重试');
    }
  };

  // 点击字幕播放视频
  const handleSubtitleClick = (index) => {
    setCurrentSubtitle(index);
    const subtitle = subtitles[index];
    if (!subtitle) return;
    
    let videoElement = null;
    if (isFloatingPlayerMode) {
      videoElement = floatingVideoRef.current;
    } else {
      videoElement = videoRef.current;
    }
    
    if (videoElement) {
      if (lesson?.content?.videoUrl && (lesson.content.videoUrl.includes('youtube.com') || lesson.content.videoUrl.includes('youtu.be'))) {
        try {
          // 给字幕时间添加缓冲
          const startTime = subtitle.time + 0.05;
          videoElement.contentWindow.postMessage(JSON.stringify({
            event: 'command',
            func: 'seekTo',
            args: [startTime, true]
          }), '*');
          
          // 延迟播放，确保seek完成
          setTimeout(() => {
            videoElement.contentWindow.postMessage(JSON.stringify({
              event: 'command',
              func: 'playVideo',
              args: []
            }), '*');
            setIsPlaying(true);
          }, 200);
        } catch (error) {
          console.error('无法控制YouTube视频:', error);
        }
      } else {
        // 本地视频，使用与playOriginalAudio相同的逻辑
        setPlayingSubtitleIndex(index);
        
        // 给字幕时间添加足够的缓冲，避免边缘重叠
        const startTime = subtitle.time + 0.05;
        const endTime = subtitle.endTime ? (subtitle.endTime - 0.1) : (startTime + 4.9);
        
        let stopAtEndTime = null;
        let seekComplete = false;
        
        // 清理之前的监听器
        videoElement.removeEventListener('seeked', () => {});
        videoElement.removeEventListener('timeupdate', () => {});
        
        // 监听seeked事件，确保seek完成后再播放
        const handleSeeked = () => {
          seekComplete = true;
          videoElement.removeEventListener('seeked', handleSeeked);
          videoElement.play().catch(error => {
            console.error('播放视频失败:', error);
            if (stopAtEndTime) {
              videoElement.removeEventListener('timeupdate', stopAtEndTime);
            }
          });
          setIsPlaying(true);
        };
        
        // 监听timeupdate事件，在到达结束时间时暂停
        stopAtEndTime = () => {
          if (seekComplete && videoElement.currentTime >= endTime) {
            videoElement.pause();
            setIsPlaying(false);
            setPlayingSubtitleIndex(null);
            videoElement.removeEventListener('timeupdate', stopAtEndTime);
          }
        };
        
        videoElement.addEventListener('seeked', handleSeeked);
        videoElement.addEventListener('timeupdate', stopAtEndTime);
        
        // 跳转到开始时间
        videoElement.currentTime = startTime;
      }
    }
  };

  // 跟读练习：开始/停止录音
  const toggleRecording = async (subtitleIndex) => {
    if (recordingIndex === subtitleIndex) {
      // 停止录音
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      setRecordingIndex(null);
    } else {
      // 开始录音
      // 先暂停视频
      let video = null;
      if (isFloatingPlayerMode) {
        video = floatingVideoRef.current;
      } else {
        video = videoRef.current;
      }
      if (video && !video.paused) {
        video.pause();
        setIsPlaying(false);
      }
      try {
        // 检查浏览器是否支持 getUserMedia
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          alert('您的浏览器不支持录音功能，请使用现代浏览器');
          return;
        }
        
        // 检查是否支持MediaRecorder
        if (typeof MediaRecorder === 'undefined') {
          alert('您的浏览器不支持MediaRecorder，请使用Chrome、Firefox或Safari 14.1+');
          return;
        }
        
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        
        // 检测支持的音频格式 - 优先顺序适配iPad/Safari
        let mimeType = '';
        const mimeTypesToTry = [
          'audio/mp4',
          'audio/mp4; codecs=mp4a.40.2',
          'audio/mp4; codecs=mp4a',
          'audio/webm; codecs=opus',
          'audio/webm',
          'audio/ogg; codecs=opus',
          'audio/ogg'
        ];
        
        for (const type of mimeTypesToTry) {
          if (MediaRecorder.isTypeSupported(type)) {
            mimeType = type;
            break;
          }
        }
        
        // 如果都不支持，使用默认值
        if (!mimeType && MediaRecorder.prototype.mimeType) {
          mimeType = MediaRecorder.prototype.mimeType;
        }
        
        console.log('使用的音频格式:', mimeType);
        
        mediaRecorderRef.current = new MediaRecorder(stream, mimeType ? { mimeType } : {});
        audioChunksRef.current = [];
        
        mediaRecorderRef.current.ondataavailable = (event) => {
          if (event.data && event.data.size > 0) {
            audioChunksRef.current.push(event.data);
          }
        };
        
        mediaRecorderRef.current.onerror = (error) => {
          console.error('MediaRecorder错误:', error);
        };
        
        mediaRecorderRef.current.onstop = () => {
          console.log('录音停止，数据块数量:', audioChunksRef.current.length);
          if (audioChunksRef.current.length > 0) {
            const audioBlob = new Blob(audioChunksRef.current, { type: mimeType || 'audio/mp4' });
            const audioUrl = URL.createObjectURL(audioBlob);
            recordedAudioUrlRef.current[subtitleIndex] = audioUrl;
            setHasRecording(prev => ({ ...prev, [subtitleIndex]: true }));
            console.log('录音URL已生成:', audioUrl);
          }
          // 停止所有轨道
          stream.getTracks().forEach(track => track.stop());
        };
        
        mediaRecorderRef.current.start();
        setRecordingIndex(subtitleIndex);
      } catch (error) {
        console.error('无法开始录音:', error);
        if (error.name === 'NotAllowedError') {
          alert('无法开始录音，请在设备设置中授予麦克风权限');
        } else if (error.name === 'NotFoundError') {
          alert('未找到麦克风设备，请确保设备已连接');
        } else if (error.name === 'NotReadableError') {
          alert('麦克风被其他应用占用，请关闭其他使用麦克风的应用');
        } else if (error.name === 'OverconstrainedError') {
          alert('设备不满足要求的音频约束条件');
        } else {
          alert('无法开始录音，请重试');
        }
      }
    }
  };

  // 跟读练习：播放录音
  const playRecordedAudio = (subtitleIndex) => {
    try {
      const audioUrl = recordedAudioUrlRef.current[subtitleIndex];
      if (!audioUrl) {
        alert('还没有录音，请先录音');
        return;
      }
      
      // 如果正在播放同一个录音，则暂停
      if (playingRecordingIndex === subtitleIndex && audioPlaybackRef.current && !audioPlaybackRef.current.paused) {
        audioPlaybackRef.current.pause();
        setPlayingRecordingIndex(null);
        return;
      }
      
      // 停止当前播放的音频
      if (audioPlaybackRef.current) {
        audioPlaybackRef.current.pause();
        audioPlaybackRef.current.currentTime = 0;
      }
      
      audioPlaybackRef.current = new Audio(audioUrl);
      // 设置音量为最大，确保能听到声音
      audioPlaybackRef.current.volume = 1.0;
      
      audioPlaybackRef.current.play().catch(error => {
        console.error('播放录音失败:', error);
        if (error.name === 'NotAllowedError') {
          alert('无法播放录音，请在设备设置中授予音频播放权限');
        } else {
          alert('播放录音失败，请重试');
        }
      });
      setPlayingRecordingIndex(subtitleIndex);
      
      // 播放结束后重置状态
      audioPlaybackRef.current.onended = () => {
        setPlayingRecordingIndex(null);
      };
    } catch (e) {
      console.error('播放录音失败:', e);
      alert('播放录音失败，请重试');
    }
  };

  // 从字幕文件URL加载字幕
  const loadSubtitlesFromFile = async (subtitleUrl, currentLessonId) => {
    try {
      console.log('从字幕文件加载:', subtitleUrl);
      
      // 尝试从后端获取视频详细信息
      let videoId = currentLessonId;
      if (videoId.includes('uploaded_')) {
        videoId = videoId.replace('uploaded_', '');
      }
      
      try {
        const videoResponse = await videoAPI.getVideo(videoId);
        if (videoResponse.data.success && videoResponse.data.video) {
          const video = videoResponse.data.video;
          console.log('从后端获取视频信息成功:', video);
          
          if (video.subtitleFiles && video.subtitleFiles.length > 0) {
            // 使用后端返回的字幕文件
            const subtitleFile = video.subtitleFiles[0];
            // 如果已经是完整URL，直接使用
            if (subtitleFile.startsWith('http')) {
              subtitleUrl = getMediaUrl(subtitleFile);
            } else {
              // 后端已经保存了 /uploads/ 前缀，直接使用
              subtitleUrl = getMediaUrl(subtitleFile.startsWith('/uploads') ? subtitleFile : `/uploads/${subtitleFile}`);
            }
            console.log('使用后端返回的字幕文件:', subtitleUrl);
          }
        }
      } catch (error) {
        console.log('从后端获取视频信息失败:', error);
        // 继续使用原始字幕URL
      }
      
      // 清理URL，去除可能的重复 /uploads/ 前缀
      let cleanedSubtitleUrl = subtitleUrl;
      if (cleanedSubtitleUrl.includes('/uploads//uploads/')) {
        cleanedSubtitleUrl = cleanedSubtitleUrl.replace('/uploads//uploads/', '/uploads/');
      }
      
      console.log('loadSubtitlesFromFile - 原始subtitleUrl:', subtitleUrl);
      console.log('loadSubtitlesFromFile - 清理后的cleanedSubtitleUrl:', cleanedSubtitleUrl);
      
      // 生成多种可能的URL格式
      const possibleUrls = [];
      
      // 优先使用getMediaUrl处理字幕URL（最优先）
      if (!cleanedSubtitleUrl.startsWith('http')) {
        const processedSubtitleUrl = getMediaUrl(cleanedSubtitleUrl);
        console.log('loadSubtitlesFromFile - cleanedSubtitleUrl:', cleanedSubtitleUrl);
        console.log('loadSubtitlesFromFile - processedSubtitleUrl:', processedSubtitleUrl);
        if (processedSubtitleUrl && processedSubtitleUrl !== cleanedSubtitleUrl) {
          possibleUrls.push(processedSubtitleUrl);
        }
        // 使用当前页面的origin作为后端URL
        const backendUrl = `${window.location.origin}${cleanedSubtitleUrl.startsWith('/') ? '' : '/'}${cleanedSubtitleUrl}`;
        console.log('loadSubtitlesFromFile - 直接构建的后端URL:', backendUrl);
        possibleUrls.push(backendUrl);
      }
      
      console.log('loadSubtitlesFromFile - possibleUrls:', possibleUrls);
      
      // 优先使用相对路径
      if (cleanedSubtitleUrl.startsWith('/')) {
        possibleUrls.push(cleanedSubtitleUrl);
      } else if (!cleanedSubtitleUrl.startsWith('http')) {
        possibleUrls.push(`/${cleanedSubtitleUrl}`);
      }
      
      // 原始URL
      possibleUrls.push(cleanedSubtitleUrl);
      
      // 如果不是完整URL，尝试添加完整的本地开发服务器路径
      if (!cleanedSubtitleUrl.startsWith('http')) {
        possibleUrls.push(`${window.location.origin}${cleanedSubtitleUrl.startsWith('/') ? '' : '/'}${cleanedSubtitleUrl}`);
      }
      
      // 尝试所有可能的URL格式
      let loaded = false;
      let content = null;
      let finalUrl = null;
      
      for (const url of possibleUrls) {
        let encodedUrl = url;
        if (url.startsWith('http')) {
          try {
            const urlObj = new URL(url);
            if (!urlObj.pathname.includes('%20')) {
              urlObj.pathname = encodeURI(urlObj.pathname);
            }
            encodedUrl = urlObj.toString();
          } catch (e) {
            encodedUrl = url;
          }
        } else {
          if (!url.includes('%20')) {
            encodedUrl = encodeURI(url);
          }
        }
        console.log('尝试加载字幕URL:', encodedUrl);
        try {
          const response = await fetch(encodedUrl, {
            mode: 'cors',
            headers: {
              'Accept': 'text/plain, text/srt, text/vtt'
            }
          });
          
          if (response.ok) {
            content = await response.text();
            finalUrl = encodedUrl;
            loaded = true;
            console.log('字幕文件加载成功:', encodedUrl);
            console.log('字幕文件内容长度:', content.length);
            console.log('字幕文件前100字符:', content.substring(0, 100));
            break;
          } else {
            console.log('字幕文件加载失败，状态码:', response.status, 'URL:', encodedUrl);
          }
        } catch (error) {
          console.log('加载字幕文件时出错:', error, 'URL:', encodedUrl);
        }
      }
      
      if (!loaded || !content) {
        console.log('所有URL格式都加载失败，使用默认字幕');
        // 设置默认字幕，避免显示"加载中..."
        const defaultSubtitles = [
          { time: 0, endTime: 2, english: 'Welcome to this video!', chinese: '欢迎观看这个视频！' },
          { time: 2, endTime: 4, english: 'This is a test subtitle.', chinese: '这是一个测试字幕。' },
          { time: 4, endTime: 10, english: 'Subtitles are working!', chinese: '字幕正在工作！' }
        ];
        setSubtitles(defaultSubtitles);
        return false;
      }
      
      let parsedSubtitles = [];
      
      // 根据文件扩展名或内容尝试解析
      if (finalUrl.toLowerCase().endsWith('.srt')) {
        parsedSubtitles = parseSRT(content);
        console.log('SRT字幕解析完成，数量:', parsedSubtitles.length);
      } else if (finalUrl.toLowerCase().endsWith('.vtt')) {
        parsedSubtitles = parseVTT(content);
        console.log('VTT字幕解析完成，数量:', parsedSubtitles.length);
      } else {
        console.log('未知字幕格式，尝试两种格式解析');
        parsedSubtitles = parseSRT(content);
        if (parsedSubtitles.length === 0) {
          parsedSubtitles = parseVTT(content);
        }
        console.log('字幕解析完成，数量:', parsedSubtitles.length);
      }
      
      if (parsedSubtitles.length > 0) {
        console.log('准备更新字幕，数量:', parsedSubtitles.length);
        console.log('前3个字幕:', parsedSubtitles.slice(0, 3));
        const subtitlesWithEndTime = addEndTimeToSubtitles(parsedSubtitles);
        setSubtitles(subtitlesWithEndTime);
        console.log('字幕已更新');
        
        // 分析字幕词汇
        const subtitleContent = subtitlesWithEndTime.map(sub => sub.english).join(' ');
        analyzeVocabulary(subtitleContent);
        
        // 使用共享的词汇提取函数（与英语卡片页面保持一致）
        console.log('使用共享的词汇提取函数...');
        
        // 清理videoId，移除可能存在的uploaded_前缀
        let cleanVideoId = currentLessonId;
        if (cleanVideoId.includes('uploaded_')) {
          cleanVideoId = cleanVideoId.replace('uploaded_', '');
        }
        console.log('清理后的videoId:', cleanVideoId, '原始videoId:', currentLessonId);
        
        // 优先使用 extractAndProcessVocabulary，优先从后端获取已保存的词汇数据
        const vocabulary = await extractAndProcessVocabulary(finalUrl, cleanVideoId, false);
        
        // 更新 localStorage 缓存，使用cleanVideoId
        const vocabularyKey = `adminVocabulary_${cleanVideoId}`;
        localStorage.setItem(vocabularyKey, JSON.stringify(vocabulary));
        console.log('词汇数据已更新到缓存:', vocabulary);
        
        console.log('最终词汇数据:', {
          words: vocabulary.words.length,
          phrases: vocabulary.phrases.length,
          expressions: vocabulary.expressions.length
        });
        
        if (vocabulary.words.length > 0) {
          console.log('第一个单词数据:', vocabulary.words[0]);
          console.log('第一个单词的sentence:', vocabulary.words[0].sentence);
          console.log('第一个单词的sentenceChinese:', vocabulary.words[0].sentenceChinese);
        }
        
        // 使用统一的状态管理工具加载词汇状态，使用cleanVideoId
        const vocabularyWithStatus = enrichVocabularyWithStatus(cleanVideoId, vocabulary);
        
        console.log('vocabularyWithStatus.words:', vocabularyWithStatus.words);
        if (vocabularyWithStatus.words.length > 0) {
          console.log('vocabularyWithStatus第一个单词:', vocabularyWithStatus.words[0]);
        }
        
        // 使用全新的简单工具处理词汇和例句
        const processedVocabulary = processVocabularyWithExamples(vocabularyWithStatus, subtitlesWithEndTime);
        
        // 立即显示单词、短语和地道表达列表（不等待翻译完成）
        setWordsData([...processedVocabulary.words]);
        setPhrasesData([...processedVocabulary.phrases]);
        setExpressionsData([...processedVocabulary.expressions]);
        
        // 异步获取单词的定义和翻译（并行处理）- 确保不覆盖例句
        const fetchWordDefinitionsAsync = async () => {
          const words = [...processedVocabulary.words];
          const promises = words.map(async (wordData, index) => {
            // 如果是智能提取的词汇，已经包含所有字段，不需要再获取
            if (wordData.source === 'smart') {
              return wordData;
            }
            
            if (!wordData.phonetic || !wordData.definition) {
              try {
                const definitionData = await fetchWordDefinition(wordData.word);
                let definitionText = wordData.definition;
                if (definitionData.definition && !wordData.definition) {
                  if (definitionData.partOfSpeech) {
                    definitionText = definitionData.partOfSpeech + '. ' + definitionData.definition;
                  } else {
                    definitionText = definitionData.definition;
                  }
                }
                return {
                  ...wordData,
                  phonetic: wordData.phonetic || definitionData.phonetic,
                  definition: definitionText,
                  partOfSpeech: wordData.partOfSpeech || definitionData.partOfSpeech,
                  chineseTranslation: wordData.chineseTranslation || definitionData.definition,
                  sentence: wordData.sentence,
                  sentenceChinese: wordData.sentenceChinese
                };
              } catch (error) {
                console.log('获取单词定义失败:', wordData.word, error);
                return wordData;
              }
            }
            return wordData;
          });
          
          const updatedWords = await Promise.all(promises);
          setWordsData(updatedWords);
        };
        
        // 异步获取短语的中文翻译（并行处理）- 确保不覆盖例句
        const fetchPhraseTranslationsAsync = async () => {
          const phrases = [...processedVocabulary.phrases];
          const promises = phrases.map(async (phraseData, index) => {
            // 如果是智能提取的短语，已经包含所有字段，不需要再获取
            if (phraseData.source === 'smart') {
              return phraseData;
            }
            
            if (!phraseData.chineseTranslation && !phraseData.definition) {
              try {
                const translation = await fetchPhraseTranslation(phraseData.phrase);
                return {
                  ...phraseData,
                  definition: translation,
                  sentence: phraseData.sentence,
                  sentenceChinese: phraseData.sentenceChinese
                };
              } catch (error) {
                console.log('获取短语翻译失败:', phraseData.phrase, error);
                return phraseData;
              }
            }
            return phraseData;
          });
          
          const updatedPhrases = await Promise.all(promises);
          setPhrasesData(updatedPhrases);
        };
        
        // 异步获取地道表达的中文翻译（并行处理）- 确保不覆盖例句
        const fetchExpressionTranslationsAsync = async () => {
          const expressions = [...processedVocabulary.expressions];
          const promises = expressions.map(async (exprData, index) => {
            // 如果是智能提取的自然科普词汇，已经包含所有字段，不需要再获取
            if (exprData.source === 'smart') {
              return exprData;
            }
            
            if (!exprData.content) {
              try {
                const translation = await fetchPhraseTranslation(exprData.expression);
                return {
                  ...exprData,
                  content: translation,
                  sentence: exprData.sentence,
                  sentenceChinese: exprData.sentenceChinese
                };
              } catch (error) {
                console.log('获取地道表达翻译失败:', exprData.expression, error);
                return exprData;
              }
            }
            return exprData;
          });
          
          const updatedExpressions = await Promise.all(promises);
          setExpressionsData(updatedExpressions);
        };
        
        // 延迟启动异步任务，让界面先显示出来
        setTimeout(() => {
          fetchWordDefinitionsAsync();
          fetchPhraseTranslationsAsync();
          fetchExpressionTranslationsAsync();
        }, 100);
        
        // 强制重新渲染
        setTimeout(() => {
          console.log('检查字幕状态:', subtitles);
        }, 1000);
        return true;
      } else {
        console.log('解析到的字幕为空，使用默认字幕');
        // 设置默认字幕，避免显示"加载中..."
        const defaultSubtitles = [
          { time: 0, endTime: 2, english: 'Welcome to this video!', chinese: '欢迎观看这个视频！' },
          { time: 2, endTime: 4, english: 'This is a test subtitle.', chinese: '这是一个测试字幕。' },
          { time: 4, endTime: 10, english: 'Subtitles are working!', chinese: '字幕正在工作！' }
        ];
        setSubtitles(defaultSubtitles);
        return false;
      }
    } catch (error) {
      console.error('加载字幕文件失败:', error);
      // 字幕加载失败时，设置默认字幕
      const defaultSubtitles = [
        { time: 0, endTime: 2, english: 'Welcome to this video!', chinese: '欢迎观看这个视频！' },
        { time: 2, endTime: 4, english: 'This is a test subtitle.', chinese: '这是一个测试字幕。' },
        { time: 4, endTime: 10, english: 'Subtitles are working!', chinese: '字幕正在工作！' }
      ];
      setSubtitles(defaultSubtitles);
      return false;
    }
  };

  // 从视频中提取字幕的功能
  const extractSubtitlesFromVideo = async () => {
    console.log('=== extractSubtitlesFromVideo 函数被调用 ===');
    if (!lesson) {
      console.log('Lesson is null, cannot extract subtitles');
      return;
    }
    
    console.log('=== 开始提取字幕 ===');
    console.log('Lesson:', lesson);
    console.log('Lesson content:', lesson.content);
    console.log('Video URL:', lesson.content?.videoUrl);
    console.log('Subtitle URL:', lesson.content?.subtitleUrl);

    // 首先尝试从字幕文件加载
    if (lesson.content?.subtitleUrl) {
      console.log('发现字幕文件，尝试加载:', lesson.content.subtitleUrl);
      
      try {
        console.log('调用 loadSubtitlesFromFile 函数');
        const loaded = await loadSubtitlesFromFile(lesson.content.subtitleUrl, lesson._id);
        console.log('loadSubtitlesFromFile 返回:', loaded);
        // 无论成功还是失败，loadSubtitlesFromFile都会设置字幕
        // 所以这里直接返回，不继续执行
        console.log('字幕文件处理完成，不使用模拟字幕');
        return;
      } catch (error) {
        console.error('加载字幕文件时出错:', error);
        // 出错时，loadSubtitlesFromFile已经设置了默认字幕
        return;
      }
    } else {
      console.log('没有字幕文件URL，尝试自动匹配字幕文件');
      
      // 尝试根据视频文件名自动匹配字幕文件
      if (lesson.content?.videoUrl) {
        const videoUrl = lesson.content.videoUrl;
        let videoFileName = videoUrl;
        
        // 处理完整URL的情况
        if (videoFileName.includes('http://localhost:50001')) {
          videoFileName = videoFileName.replace('http://localhost:50001', '');
        }
        if (videoFileName.includes('${window.location.origin}')) {
          videoFileName = videoFileName.replace('${window.location.origin}', '');
        }
        
        // 提取完整文件名
        const fullFileName = videoFileName.split('/').pop();
        console.log('视频完整文件名:', fullFileName);
        
        // 提取原始文件名（去掉时间戳和扩展名）
        // 时间戳格式：数字-原始文件名
        const originalFileNameMatch = fullFileName.match(/\d+-(.+?)\.[^/.]+$/);
        let originalFileName = '';
        if (originalFileNameMatch && originalFileNameMatch[1]) {
          originalFileName = originalFileNameMatch[1];
        } else {
          // 如果没有时间戳，使用完整文件名（不含扩展名）
          originalFileName = fullFileName.replace(/\.[^/.]+$/, '');
        }
        console.log('视频原始文件名:', originalFileName);
        
        // 直接尝试加载字幕文件 - 使用已知的字幕文件列表
        const knownSubtitleFiles = [
          '1773022576918-Overview of Cell Structure.srt',
          '1773022147862-Overview of Cell Structure.srt',
          '1773021792112-Overview of Cell Structure.srt',
          '1773019944802-Overview of Cell Structure.srt',
          '1773018060058-Overview of Cell Structure.srt',
          '1773014871535-Overview of Cell Structure.srt',
          '1772975091169-How your digestive system works - Emma Bryce.srt'
        ];
        
        // 首先尝试匹配已知的字幕文件
        for (const subtitleFile of knownSubtitleFiles) {
          if (subtitleFile.includes(originalFileName) || originalFileName.includes(subtitleFile.replace(/\d+-/, '').replace('.srt', ''))) {
            console.log('找到匹配的字幕文件:', subtitleFile);
            try {
              const loaded = await loadSubtitlesFromFile(`/uploads/${subtitleFile}`, lesson._id);
              if (loaded) {
                console.log('字幕文件加载成功:', subtitleFile);
                return;
              }
            } catch (error) {
              console.log('加载字幕文件失败:', error);
            }
          }
        }
        
        // 首先尝试从localStorage中查找匹配的字幕文件
        try {
          const uploadedVideos = JSON.parse((localStorage.getItem('uploadedVideos') || '[]').replace(/^"undefined"$/, '[]'));
          console.log('从localStorage查找匹配的字幕文件, 视频数量:', uploadedVideos.length);
          
          // 查找与当前视频标题或原始文件名匹配的视频数据，同时确保分类相同
          const matchedVideo = uploadedVideos.find(video => {
            // 首先确保分类相同
            if (course?.category && video.category !== course.category) {
              return false;
            }
            // 检查视频标题是否匹配
            if (video.title === lesson.title) {
              return true;
            }
            // 检查视频文件名是否包含相同的原始文件名
            if (video.fileUrl && video.fileUrl.includes(originalFileName)) {
              return true;
            }
            return false;
          });
          
          if (matchedVideo && matchedVideo.subtitleUrl) {
            console.log('从localStorage找到匹配的字幕文件:', matchedVideo.subtitleUrl);
            try {
              const loaded = await loadSubtitlesFromFile(matchedVideo.subtitleUrl, lesson._id);
              console.log('localStorage字幕文件加载结果:', loaded);
              if (loaded) {
                console.log('localStorage字幕文件加载成功');
                return;
              }
            } catch (error) {
              console.log('加载localStorage字幕文件时出错:', error);
            }
          }
        } catch (error) {
          console.log('从localStorage查找字幕文件时出错:', error);
        }
        
        // 生成可能的字幕文件路径 - 优先使用不考虑时间戳的方式
        const possibleSubtitlePaths = [];
        
        // 1. 首先尝试任何时间戳的字幕文件（通过服务器端API查找）
        // 但是浏览器无法直接列出目录，所以我们尝试更广泛的搜索
        // 从视频时间戳中提取日期部分（前10位），并尝试更大的范围
        const timestampMatch = fullFileName.match(/^(\d+)-/);
        if (timestampMatch) {
          const videoTimestamp = timestampMatch[1];
          const ts = parseInt(videoTimestamp);
          
          // 尝试更大的时间戳范围（前后2000毫秒，步长为10毫秒）
          // 这样可以覆盖上传时间差异
          for (let i = -200; i <= 200; i++) {
            if (i !== 0) {
              possibleSubtitlePaths.push(
                `/uploads/${ts + i * 10}-${originalFileName}.srt`,
                `/uploads/${ts + i * 10}-${originalFileName}.vtt`
              );
            }
          }
          
          // 也尝试相同的时间戳
          possibleSubtitlePaths.push(
            `/uploads/${videoTimestamp}-${originalFileName}.srt`,
            `/uploads/${videoTimestamp}-${originalFileName}.vtt`
          );
        }
        
        // 2. 最后尝试不带时间戳的字幕文件
        possibleSubtitlePaths.push(
          `/uploads/${originalFileName}.srt`,
          `/uploads/${originalFileName}.vtt`
        );
        
        // 3. 对于我们知道的具体文件，直接尝试
        if (originalFileName === 'Overview of Cell Structure') {
          possibleSubtitlePaths.push(
            '/uploads/1772626289226-Overview of Cell Structure.srt'
          );
        }
        
        console.log('尝试自动匹配的字幕文件路径（前10个）:', possibleSubtitlePaths.slice(0, 10));
        console.log('总共尝试的字幕文件路径数量:', possibleSubtitlePaths.length);
        
        // 尝试加载自动匹配的字幕文件
        for (const subtitlePath of possibleSubtitlePaths) {
          try {
            console.log('尝试加载自动匹配的字幕文件:', subtitlePath);
            const loaded = await loadSubtitlesFromFile(subtitlePath, lesson._id);
            console.log('自动匹配字幕文件加载结果:', loaded);
            if (loaded) {
              console.log('自动匹配字幕文件成功');
              return;
            }
          } catch (error) {
            console.log('加载自动匹配字幕文件时出错:', error);
          }
        }
      }
      
      console.log('所有字幕文件路径都加载失败，使用默认字幕');
      // 设置默认字幕
      const defaultSubtitles = [
        { time: 0, endTime: 2, english: 'Welcome to this video!', chinese: '欢迎观看这个视频！' },
        { time: 2, endTime: 4, english: 'This is a test subtitle.', chinese: '这是一个测试字幕。' },
        { time: 4, endTime: 10, english: 'Subtitles are working!', chinese: '字幕正在工作！' }
      ];
      setSubtitles(defaultSubtitles);
      
      // 即使使用默认字幕，也提取词汇
      console.log('使用默认字幕，开始自动提取词汇...');
      const vocabulary = extractVocabularyFromSubtitles(defaultSubtitles);
      // 过滤虚拟词汇
      const virtualWords = ['pensive', 'introspective', 'journal', 'break down', 'analytical'];
      const virtualPhrases = ['in my head', 'thought processes'];
      const virtualExpressions = ['overall'];
      vocabulary.words = vocabulary.words.filter(word => !virtualWords.includes(word.word));
      vocabulary.phrases = vocabulary.phrases.filter(phrase => !virtualPhrases.includes(phrase.phrase));
      vocabulary.expressions = vocabulary.expressions.filter(expr => !virtualExpressions.includes(expr.expression));
      
      console.log('最终词汇数据:', {
        words: vocabulary.words.length,
        phrases: vocabulary.phrases.length,
        expressions: vocabulary.expressions.length
      });
      
      // 使用统一的状态管理工具加载词汇状态
      const vocabularyWithStatus = enrichVocabularyWithStatus(lesson._id, vocabulary);
      
      // 使用全新的简单工具处理词汇和例句
      const processedVocabulary = processVocabularyWithExamples(vocabularyWithStatus, defaultSubtitles);
      
      // 立即显示单词、短语和地道表达列表（不等待翻译完成）
      setWordsData([...processedVocabulary.words]);
      setPhrasesData([...processedVocabulary.phrases]);
      setExpressionsData([...processedVocabulary.expressions]);
      return;
    }
  };



  useEffect(() => {
    // 预加载语音合成的 voices
    if (window.speechSynthesis) {
      // 检查是否已经加载了 voices
      const initialVoices = window.speechSynthesis.getVoices();
      if (initialVoices && initialVoices.length > 0) {
        setVoicesLoaded(true);
      }
      // 监听 voiceschanged 事件
      window.speechSynthesis.onvoiceschanged = () => {
        console.log('Voices loaded');
        setVoicesLoaded(true);
      };
    }
  }, []);

  // 监听窗口大小变化，更新响应式状态
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1200);
      setIsDesktop(width >= 1200);
      setIsLandscape(width > height);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 监听鼠标和触摸事件用于拖动
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handlePlayerMouseMove);
      window.addEventListener('mouseup', handlePlayerMouseUp);
      window.addEventListener('touchmove', handlePlayerTouchMove);
      window.addEventListener('touchend', handlePlayerTouchEnd);
    }
    
    return () => {
      window.removeEventListener('mousemove', handlePlayerMouseMove);
      window.removeEventListener('mouseup', handlePlayerMouseUp);
      window.removeEventListener('touchmove', handlePlayerTouchMove);
      window.removeEventListener('touchend', handlePlayerTouchEnd);
    };
  }, [isDragging, handlePlayerMouseMove, handlePlayerMouseUp, handlePlayerTouchMove, handlePlayerTouchEnd]);

  useEffect(() => {
    // 打印本地存储的内容
    console.log('=== 组件加载，开始获取数据 ===');
    const uploadedVideos = JSON.parse((localStorage.getItem('uploadedVideos') || '[]').replace(/^"undefined"$/, '[]'));
    console.log('Local storage uploadedVideos:', uploadedVideos);
    fetchData();
  }, [courseId, lessonId, id]);
  
  // 当字幕索引从URL参数中获取时，自动播放对应句子
  useEffect(() => {
    if (id && subtitles.length > 0) {
      // 从URL参数中获取subtitleIndex
      const urlParams = new URLSearchParams(window.location.search);
      const subtitleIndex = urlParams.get('subtitleIndex');
      if (subtitleIndex) {
        const index = parseInt(subtitleIndex);
        if (!isNaN(index) && index >= 0 && index < subtitles.length) {
          // 延迟执行，确保视频已经加载
          setTimeout(() => {
            playOriginalAudio(index);
          }, 1000);
        }
      }
    }
  }, [id, subtitles.length]);

  // 监听视频播放时间，更新当前字幕并自动滚动
  useEffect(() => {
    const video = isFloatingPlayerMode ? floatingVideoRef.current : videoRef.current;
    if (!video) return;

    const updateSubtitle = () => {
      // 如果是练习模式或精读模式，不执行自动播放相关逻辑
      if (exerciseMode !== 'none' || intensiveMode !== 'none') {
        return;
      }
      
      let currentTime = 0;
      
      // 检查video是否为null
      if (!video) return;
      
      // 检查是否是YouTube视频（iframe）
      if (video.tagName === 'IFRAME') {
        // 对于YouTube视频，我们使用模拟的时间更新
        // 实际项目中，可以使用YouTube Player API获取真实的播放时间
        currentTime = Date.now() / 1000 % 30; // 模拟30秒的视频
      } else if (video && !isNaN(video.currentTime)) {
        // 对于本地上传的视频，使用真实的播放时间
        currentTime = video.currentTime;
      }

      if (currentTime > 0) {
        // 检查视频播放进度，当达到50%时标记为已学习
        if (video.duration && !isNaN(video.duration)) {
          const progress = (currentTime / video.duration) * 100;
          if (progress >= 50) {
            if (lesson && lesson._id) {
              try {
                const learnedCourses = JSON.parse((localStorage.getItem('learnedCourses') || '[]').replace(/^"undefined"$/, '[]'));
                if (!learnedCourses.includes(lesson._id)) {
                  learnedCourses.push(lesson._id);
                  localStorage.setItem('learnedCourses', JSON.stringify(learnedCourses));
                  console.log('视频已标记为已学习（播放进度达到50%）:', lesson._id);
                }
              } catch (error) {
                console.error('标记视频为已学习失败:', error);
              }
            }
          }
        }

        const currentSubtitleIndex = subtitles.findIndex((subtitle, index) => {
          return currentTime >= subtitle.time && (index === subtitles.length - 1 || currentTime < subtitles[index + 1].time);
        });

        if (currentSubtitleIndex !== -1) {
          // 检查是否切换了新的字幕
          if (currentSubtitleIndex !== lastProcessedSubtitleRef.current) {
            lastProcessedSubtitleRef.current = currentSubtitleIndex;
            setCurrentSubtitle(currentSubtitleIndex);
            // 重置当前句子播放次数和结束状态
            currentSentencePlayCountRef.current = 0;
            isCurrentSentenceEndedRef.current = false;
            setCurrentPlayCountDisplay(0);
            console.log('切换到新字幕:', currentSubtitleIndex, '当前时间:', currentTime);
          }
          
          // 检查当前句子是否播放结束（单句循环模式）
          if (playMode === 'sentenceLoop') {
            const currentSubtitleData = subtitles[currentSubtitleIndex];
            let endTime;
            
            // 优先使用字幕自身的 endTime，如果没有则使用下一个字幕的开始时间
            if (currentSubtitleData.endTime) {
              endTime = currentSubtitleData.endTime - 0.05;
            } else {
              const nextSubtitle = subtitles[currentSubtitleIndex + 1];
              endTime = nextSubtitle ? nextSubtitle.time - 0.05 : (video.duration ? video.duration - 0.05 : Infinity);
            }
            
            console.log('当前时间:', currentTime, '结束时间:', endTime, '已结束:', isCurrentSentenceEndedRef.current, '当前播放次数:', currentSentencePlayCountRef.current, '目标次数:', loopCount);
            
            // 检查是否到达句子结束时间
            if (currentTime >= endTime && !isCurrentSentenceEndedRef.current) {
              isCurrentSentenceEndedRef.current = true;
              currentSentencePlayCountRef.current += 1;
              setCurrentPlayCountDisplay(currentSentencePlayCountRef.current);
              
              console.log('句子播放结束，当前播放次数:', currentSentencePlayCountRef.current, '目标次数:', loopCount, 'autoNext:', autoNext);
              
              if (currentSentencePlayCountRef.current < loopCount) {
                // 继续循环当前句子
                console.log('继续循环当前句子，跳转到:', currentSubtitleData.time);
                // 先重置结束状态
                isCurrentSentenceEndedRef.current = false;
                // 设置视频时间
                video.currentTime = currentSubtitleData.time;
                // 确保时间设置完成后再播放
                setTimeout(() => {
                  video.play().catch(e => console.error('播放失败:', e));
                }, 20);
              } else if (autoNext && currentSubtitleIndex < subtitles.length - 1) {
                // 自动播放下一句
                console.log('自动播放下一句');
                const nextIndex = currentSubtitleIndex + 1;
                lastProcessedSubtitleRef.current = nextIndex;
                setCurrentSubtitle(nextIndex);
                currentSentencePlayCountRef.current = 0;
                isCurrentSentenceEndedRef.current = false;
                setCurrentPlayCountDisplay(0);
                video.currentTime = subtitles[nextIndex].time;
                video.play().catch(e => console.error('播放失败:', e));
              } else {
                // 达到循环次数且不自动下一句，暂停
                console.log('达到循环次数，暂停');
                video.pause();
              }
            }
          }
        }
      }
    };
    
    // 处理视频播放的函数
    const handlePlay = () => {
      // 视频开始播放时的处理
    };
    
    // 处理视频暂停的函数
    const handlePause = () => {
      // 暂停时保持当前状态
    };
    
    // 处理视频结束的函数
    const handleEnded = () => {
      // 如果是精读模式或练习模式，不自动播放
      if (intensiveMode !== 'none' || exerciseMode !== 'none') {
        return;
      }
      if (playMode === 'singleLoop') {
        video.play().catch(e => console.error('播放失败:', e));
      }
    };
    
    // 对于所有类型的视频，都使用定时器来更新字幕和检查单句循环状态
    // 这样可以确保响应速度一致，避免timeupdate事件触发频率不足的问题
    const intervalId = setInterval(updateSubtitle, 30); // 每30毫秒更新一次，提高响应速度
    
    // 同时保留必要的事件监听器
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('seeked', () => {
      // 当用户跳转时，重置当前句子的播放状态
      isCurrentSentenceEndedRef.current = false;
    });
    
    return () => {
      clearInterval(intervalId);
      if (video) {
        video.removeEventListener('play', handlePlay);
        video.removeEventListener('pause', handlePause);
        video.removeEventListener('ended', handleEnded);
      }
    };
  }, [subtitles, playMode, loopCount, autoNext, isFloatingPlayerMode, exerciseMode, intensiveMode]);

  // 监听当前字幕变化，实现动态跟随
  useEffect(() => {
    console.log('动态跟随触发:', {
      followMode,
      subtitleContainerRefCurrent: !!subtitleContainerRef.current,
      currentSubtitle,
      subtitlesLength: subtitles.length
    });
    
    if (followMode && subtitleContainerRef.current) {
      // 使用requestAnimationFrame确保DOM更新后再滚动
      requestAnimationFrame(() => {
        if (!subtitleContainerRef.current) return;
        
        const subtitleElements = subtitleContainerRef.current.querySelectorAll('.subtitle-item');
        console.log('字幕元素数量:', subtitleElements.length);
        
        if (subtitleElements[currentSubtitle]) {
          console.log('滚动到字幕:', currentSubtitle);
          
          try {
            // 使用scrollIntoView方法，更可靠
            subtitleElements[currentSubtitle].scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
            console.log('使用scrollIntoView滚动成功');
          } catch (error) {
            console.error('scrollIntoView失败，使用备用方法:', error);
            
            // 备用方法：直接设置滚动位置
            if (!subtitleContainerRef.current) return;
            const containerHeight = subtitleContainerRef.current.clientHeight;
            const subtitleTop = subtitleElements[currentSubtitle].offsetTop;
            const subtitleHeight = subtitleElements[currentSubtitle].offsetHeight;
            const scrollPosition = subtitleTop - (containerHeight / 2) + (subtitleHeight / 2);
            
            const maxScroll = subtitleContainerRef.current.scrollHeight - containerHeight;
            const finalScrollPosition = Math.max(0, Math.min(scrollPosition, maxScroll));
            
            subtitleContainerRef.current.scrollTop = finalScrollPosition;
            console.log('备用滚动方法完成，滚动位置:', finalScrollPosition);
          }
        } else {
          console.log('字幕元素不存在:', currentSubtitle);
        }
      });
    }
  }, [currentSubtitle, followMode, subtitles]);

  // 组件加载时尝试提取字幕
  useEffect(() => {
    console.log('=== useEffect 触发提取字幕 ===');
    console.log('Lesson:', lesson);
    console.log('Lesson content:', lesson?.content);
    console.log('Video URL:', lesson?.content?.videoUrl);
    console.log('Subtitle URL:', lesson?.content?.subtitleUrl);
    if (lesson && lesson.content?.videoUrl) {
      console.log('调用 extractSubtitlesFromVideo');
      extractSubtitlesFromVideo();
    } else {
      console.log('不满足提取字幕的条件');
    }
  }, [lesson, lesson?.content?.subtitleUrl]);

  // 组件加载后不自动播放，让用户手动点击播放（加载更快）
  useEffect(() => {
    console.log('视频已准备好，请手动点击播放');
  }, [lesson]);

  // 点击外部关闭下拉框
  useEffect(() => {
    const handleClickOutside = (event) => {
      // 检查点击是否发生在下拉框外部
      const dropdownContainers = document.querySelectorAll('.dropdown-container');
      
      let isClickInside = false;
      dropdownContainers.forEach(container => {
        if (container.contains(event.target)) {
          isClickInside = true;
        }
      });
      
      if (!isClickInside) {
        setDropdownOpen({
          subtitle: false,
          play: false,
          exercise: false,
          intensive: false
        });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 加载收藏的视频
  useEffect(() => {
    try {
      const collected = JSON.parse(localStorage.getItem('collectedVideos') || '[]');
      setCollectedVideos(collected);
    } catch (error) {
      console.error('Failed to load collected videos:', error);
      setCollectedVideos([]);
    }
  }, []);

  // 切换收藏状态
  const toggleCollect = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const videoId = lesson._id;
    const isCollected = collectedVideos.includes(videoId);
    let newCollected;
    
    if (isCollected) {
      newCollected = collectedVideos.filter(id => id !== videoId);
    } else {
      newCollected = [...collectedVideos, videoId];
    }
    
    setCollectedVideos(newCollected);
    localStorage.setItem('collectedVideos', JSON.stringify(newCollected));
  };

  // 添加CSS样式来禁用画中画功能和添加脉冲动画
  useEffect(() => {
    // 创建style元素
    const style = document.createElement('style');
    style.textContent = `
      /* 隐藏本地视频的画中画按钮 */
      video::-webkit-media-controls-picture-in-picture-button {
        display: none !important;
      }
      video::-webkit-media-controls {
        overflow: hidden !important;
      }
      /* 隐藏Firefox中的画中画按钮 */
      video::-moz-media-controls-picture-in-picture-button {
        display: none !important;
      }
      /* 隐藏IE/Edge中的画中画按钮 */
      video::-ms-media-controls-picture-in-picture-button {
        display: none !important;
      }
      /* 脉冲动画 */
      @keyframes pulse {
        0%, 100% {
          opacity: 1;
          transform: scale(1);
        }
        50% {
          opacity: 0.5;
          transform: scale(1.2);
        }
      }
    `;
    
    // 添加到head
    document.head.appendChild(style);
    
    // 清理函数
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const fetchData = async () => {
    console.log('=== fetchData 函数被调用 ===');
    setLoading(true);
    try {
      console.log('=== 开始获取数据 ===');
      console.log('路由参数:', { courseId, lessonId, id });
      
      // 优先从后端API获取所有视频数据
      let uploadedVideos = [];
      try {
        const response = await fetch('/api/videos');
        const data = await response.json();
        
        if (data.success && data.videos) {
          // 转换数据格式以匹配前端期望的格式
          uploadedVideos = data.videos.map(video => ({
            id: video.videoId,
            title: video.title,
            description: video.description || '',
            category: video.category,
            subcategory: video.subcategory || '',
            level: 'Starters',
            duration: video.duration || 0,
            fileUrl: getMediaUrl(video.videoUrl),
            subtitleUrl: video.subtitleFiles && video.subtitleFiles.length > 0 
              ? getMediaUrl(video.subtitleFiles[0])
              : '',
            posterUrl: getMediaUrl(video.posterUrl || ''),
            fileType: video.fileType || 'video',
            hidden: false,
            uploadedAt: video.uploadTime,
            subtitleFiles: video.subtitleFiles || []
          }));
          console.log('从后端API获取的上传视频:', uploadedVideos);
        }
      } catch (apiError) {
        console.log('从后端API获取视频失败，尝试从localStorage获取:', apiError);
        // 如果API失败，回退到localStorage
        uploadedVideos = JSON.parse(localStorage.getItem('uploadedVideos') || '[]');
        console.log('从本地存储获取的上传视频:', uploadedVideos);
      }
      
      let courseData = {};
      let lessonData = null;
      
      // 检查是否直接通过视频ID访问
      if (id) {
        console.log('直接通过视频ID访问:', id);
        // 找到对应的视频
        const targetVideo = uploadedVideos.find(v => v.id === id);
        console.log('找到的目标视频:', targetVideo);
        
        if (targetVideo) {
          // 尝试从后端获取视频详细信息
          try {
            console.log('尝试从后端获取视频详细信息，videoId:', id);
            const videoResponse = await videoAPI.getVideo(id);
            if (videoResponse.data.success && videoResponse.data.video) {
              const videoFromDB = videoResponse.data.video;
              console.log('从后端获取视频信息成功:', videoFromDB);
              
              // 使用后端返回的视频信息
              courseData = {
                _id: `uploaded_${id}`,
                title: videoFromDB.title || targetVideo.title,
                category: videoFromDB.category || targetVideo.category,
                lessons: [{
                  _id: videoFromDB.videoId || id,
                  title: videoFromDB.title || targetVideo.title,
                  type: videoFromDB.fileType || targetVideo.fileType || 'video',
                  duration: videoFromDB.duration || targetVideo.duration,
                  description: videoFromDB.description || targetVideo.description,
                  content: {
                    videoUrl: getMediaUrl(videoFromDB.videoUrl || targetVideo.fileUrl),
                    subtitleUrl: videoFromDB.subtitleFiles && videoFromDB.subtitleFiles.length > 0 
                      ? getMediaUrl(videoFromDB.subtitleFiles[0])
                      : targetVideo.subtitleUrl,
                    posterUrl: videoFromDB.posterUrl 
                      ? getMediaUrl(videoFromDB.posterUrl)
                      : (targetVideo.posterUrl 
                          ? getMediaUrl(targetVideo.posterUrl)
                          : '')
                  }
                }]
              };
              console.log('使用后端数据构建的课程数据:', courseData);
            } else {
              throw new Error('后端返回数据格式不正确');
            }
          } catch (error) {
            console.log('从后端获取视频信息失败，使用localStorage数据:', error);
            // 使用localStorage数据
            courseData = {
            _id: `uploaded_${id}`,
            title: targetVideo.title,
            category: targetVideo.category,
            lessons: [{
              _id: targetVideo.id,
              title: targetVideo.title,
              type: targetVideo.fileType,
              duration: targetVideo.duration,
              description: targetVideo.description,
              content: {
                videoUrl: targetVideo.fileUrl,
                subtitleUrl: targetVideo.subtitleUrl,
                posterUrl: targetVideo.posterUrl 
                  ? getMediaUrl(targetVideo.posterUrl)
                  : ''
              }
            }]
          };
          }
          
          console.log('构建的直接访问课程数据:', courseData);
          lessonData = courseData.lessons[0];
        } else {
          // 如果找不到，使用默认数据
          console.log('未找到对应的视频数据，使用默认数据');
          courseData = {
            _id: `uploaded_${id}`,
            title: '上传的视频课程',
            lessons: uploadedVideos.map(video => ({
              _id: video.id,
              title: video.title,
              type: video.fileType,
              duration: video.duration,
              description: video.description,
              content: {
                videoUrl: video.fileUrl,
                subtitleUrl: video.subtitleUrl,
                posterUrl: video.posterUrl 
                  ? getMediaUrl(video.posterUrl)
                  : ''
              }
            }))
          };
          
          // 使用第一个视频作为默认
          if (courseData.lessons.length > 0) {
            lessonData = courseData.lessons[0];
            console.log('使用第一个视频作为默认lesson:', lessonData);
          }
        }
      } else {
      
      // 检查是否是分类课程
      if (courseId.includes('category_')) {
        const category = courseId.replace('category_', '');
        console.log('当前分类:', category);
        
        // 过滤出属于该分类的视频
        const categoryVideos = uploadedVideos.filter(v => v.category === category);
        console.log('属于该分类的视频:', categoryVideos);
        
        // 构建课程数据，只包含该分类的视频
        courseData = {
          _id: courseId,
          title: '上传的视频课程',
          category: category,
          lessons: categoryVideos.map(video => ({
            _id: video.id,
            title: video.title,
            type: video.fileType,
            duration: video.duration,
            content: {
              videoUrl: video.fileUrl,
              subtitleUrl: video.subtitleUrl,
              posterUrl: video.posterUrl
            }
          }))
        };
        
        console.log('构建的分类课程数据:', courseData);
        
        // 尝试通过lessonId找到对应的视频
        if (lessonId) {
          lessonData = courseData.lessons.find(l => l._id === lessonId);
        }
        
        // 如果找不到，使用该分类的第一个视频
        if (!lessonData && courseData.lessons.length > 0) {
          lessonData = courseData.lessons[0];
          console.log('使用该分类的第一个视频作为默认lesson:', lessonData);
        }
      } else if (courseId.includes('uploaded_')) {
        // 上传的课程，提取视频ID
        const videoId = courseId.replace('uploaded_', '');
        console.log('上传的课程视频ID:', videoId);
        
        // 找到对应的视频
        const targetVideo = uploadedVideos.find(v => v.id === videoId);
        console.log('找到的目标视频:', targetVideo);
        
        if (targetVideo) {
          // 构建课程数据，只包含该视频
          courseData = {
            _id: courseId,
            title: targetVideo.title,
            category: targetVideo.category,
            lessons: [{
              _id: targetVideo.id,
              title: targetVideo.title,
              type: targetVideo.fileType,
              duration: targetVideo.duration,
              description: targetVideo.description,
              content: {
                videoUrl: targetVideo.fileUrl,
                subtitleUrl: targetVideo.subtitleUrl,
                posterUrl: targetVideo.posterUrl 
                  ? getMediaUrl(targetVideo.posterUrl)
                  : ''
              }
            }]
          };
          
          console.log('构建的上传课程数据:', courseData);
          lessonData = courseData.lessons[0];
        } else {
          // 如果找不到，使用所有视频
          courseData = {
            _id: courseId,
            title: '上传的视频课程',
            lessons: uploadedVideos.map(video => ({
              _id: video.id,
              title: video.title,
              type: video.fileType,
              duration: video.duration,
              description: video.description,
              content: {
                videoUrl: video.fileUrl,
                subtitleUrl: video.subtitleUrl,
                posterUrl: video.posterUrl 
                  ? getMediaUrl(video.posterUrl)
                  : ''
              }
            }))
          };
          
          console.log('构建的非分类课程数据:', courseData);
          
          // 尝试通过lessonId找到对应的视频
          lessonData = courseData.lessons.find(l => l._id === lessonId);
          
          // 如果找不到，使用第一个视频
          if (!lessonData && courseData.lessons.length > 0) {
            lessonData = courseData.lessons[0];
            console.log('使用第一个视频作为默认lesson:', lessonData);
          }
        }
      } else {
        // 其他情况，使用所有视频
        courseData = {
          _id: courseId,
          title: '上传的视频课程',
          lessons: uploadedVideos.map(video => ({
            _id: video.id,
            title: video.title,
            type: video.fileType,
            duration: video.duration,
            content: {
              videoUrl: video.fileUrl,
              subtitleUrl: video.subtitleUrl,
              posterUrl: video.posterUrl
            }
          }))
        };
        
        console.log('构建的非分类课程数据:', courseData);
        
        // 尝试通过lessonId找到对应的视频
        lessonData = courseData.lessons.find(l => l._id === lessonId);
        
        // 如果找不到，使用第一个视频
        if (!lessonData && courseData.lessons.length > 0) {
          lessonData = courseData.lessons[0];
          console.log('使用第一个视频作为默认lesson:', lessonData);
        }
      }
      }
      
      
      console.log('找到的lesson数据:', lessonData);
      
      // 重置字幕状态，确保切换视频时字幕也会更新
      setSubtitles([{ time: 0, english: '加载字幕中...', chinese: 'Loading subtitles...' }]);
      
      setCourse(courseData);
      setLesson(lessonData);
      setCurrentIndex(courseData.lessons.findIndex(l => l._id === lessonData._id));
      console.log('Using video data:', lessonData);
      
      // 不再立即加载字幕，让useEffect处理
    } catch (error) {
      console.error('Failed to fetch course data, trying to use localStorage data:', error);
      
      try {
        const uploadedVideos = JSON.parse((localStorage.getItem('uploadedVideos') || '[]').replace(/^"undefined"$/, '[]'));
        console.log('从localStorage获取视频数据（错误恢复）:', uploadedVideos);
        
        let courseData = {};
        let lessonData = null;
        
        if (id) {
          const targetVideo = uploadedVideos.find(v => v.id === id);
          if (targetVideo) {
            courseData = {
            _id: `uploaded_${id}`,
            title: targetVideo.title,
            category: targetVideo.category,
            lessons: [{
              _id: targetVideo.id,
              title: targetVideo.title,
              type: targetVideo.fileType,
              duration: targetVideo.duration,
              description: targetVideo.description,
              content: {
                videoUrl: targetVideo.fileUrl,
                subtitleUrl: targetVideo.subtitleUrl,
                posterUrl: targetVideo.posterUrl 
                  ? getMediaUrl(targetVideo.posterUrl)
                  : ''
              }
            }]
          };
            lessonData = courseData.lessons[0];
          } else if (uploadedVideos.length > 0) {
            courseData = {
              _id: courseId,
              title: '上传的视频课程',
              lessons: uploadedVideos.map(video => ({
                _id: video.id,
                title: video.title,
                type: video.fileType,
                duration: video.duration,
                description: video.description,
                content: {
                  videoUrl: video.fileUrl,
                  subtitleUrl: video.subtitleUrl,
                  posterUrl: video.posterUrl
                }
              }))
            };
            lessonData = courseData.lessons[0];
          }
        } else if (courseId.includes('category_')) {
          const category = courseId.replace('category_', '');
          const categoryVideos = uploadedVideos.filter(v => v.category === category);
          if (categoryVideos.length > 0) {
            courseData = {
              _id: courseId,
              title: '上传的视频课程',
              category: category,
              lessons: categoryVideos.map(video => ({
                _id: video.id,
                title: video.title,
                type: video.fileType,
                duration: video.duration,
                description: video.description,
                content: {
                  videoUrl: video.fileUrl,
                  subtitleUrl: video.subtitleUrl,
                  posterUrl: video.posterUrl
                }
              }))
            };
            lessonData = courseData.lessons[0];
          } else if (uploadedVideos.length > 0) {
            courseData = {
              _id: courseId,
              title: '上传的视频课程',
              lessons: uploadedVideos.map(video => ({
                _id: video.id,
                title: video.title,
                type: video.fileType,
                duration: video.duration,
                description: video.description,
                content: {
                  videoUrl: video.fileUrl,
                  subtitleUrl: video.subtitleUrl,
                  posterUrl: video.posterUrl
                }
              }))
            };
            lessonData = courseData.lessons[0];
          }
        } else if (uploadedVideos.length > 0) {
          courseData = {
            _id: courseId,
            title: '上传的视频课程',
            lessons: uploadedVideos.map(video => ({
              _id: video.id,
              title: video.title,
              type: video.fileType,
              duration: video.duration,
              description: video.description,
              content: {
                videoUrl: video.fileUrl,
                subtitleUrl: video.subtitleUrl,
                posterUrl: video.posterUrl 
                  ? getMediaUrl(video.posterUrl)
                  : ''
              }
            }))
          };
          lessonData = courseData.lessons[0];
        }
        
        if (lessonData) {
          setSubtitles([{ time: 0, english: '加载字幕中...', chinese: 'Loading subtitles...' }]);
          setCourse(courseData);
          setLesson(lessonData);
          setCurrentIndex(courseData.lessons.findIndex(l => l._id === lessonData._id));
          console.log('Using localStorage video data after error:', lessonData);
        } else {
          console.error('No video data found in localStorage either');
        }
      } catch (fallbackError) {
        console.error('Failed to use localStorage data, no fallback available:', fallbackError);
      }
    } finally {
      setLoading(false);
      console.log('=== fetchData 函数执行完成 ===');
    }
  };



  const handleAnswerSelect = (option) => {
    if (showResult) return;
    setSelectedAnswer(option);
  };

  const checkAnswer = () => {
    if (!selectedAnswer || !lesson.exercises || !lesson.exercises[currentExercise]) return;
    
    const isCorrect = selectedAnswer === lesson.exercises[currentExercise].correctAnswer;
    setShowResult(true);
    
    if (isCorrect) {
      setExerciseScore(prev => prev + 10);
    }
  };

  const nextExercise = () => {
    if (!lesson.exercises || currentExercise >= lesson.exercises.length - 1) return;
    
    setCurrentExercise(prev => prev + 1);
    setSelectedAnswer(null);
    setShowResult(false);
  };

  // 切换下拉菜单
  const toggleDropdown = (type) => {
    // 如果点击精读按钮，停止播放
    if (type === 'intensive') {
      const videoElement = isFloatingPlayerMode ? floatingVideoRef.current : videoRef.current;
      if (videoElement && !videoElement.paused) {
        videoElement.pause();
      }
      setIsPlaying(false);
      setPlayingSubtitleIndex(null);
    }
    
    setDropdownOpen(prev => {
      // 关闭所有其他下拉框，只切换当前点击的下拉框
      const newState = {
        subtitle: false,
        play: false,
        bottomPlay: false,
        exercise: false,
        intensive: false,
        speed: false,
        bottomSpeed: false
      };
      // 切换当前点击的下拉框状态
      newState[type] = !prev[type];
      return newState;
    });
  };

  // 选择选项
  const selectOption = (type, value) => {
    if (type === 'subtitle') {
      setSubtitleMode(value);
      localStorage.setItem('subtitleMode', value);
    }
    if (type === 'play' || type === 'bottomPlay') setPlayMode(value);
    if (type === 'exercise') {
      setExerciseMode(value);
      if (value === 'fill' || value === 'repeat') {
        const videoElement = isFloatingPlayerMode ? floatingVideoRef.current : videoRef.current;
        if (videoElement && !videoElement.paused) {
          videoElement.pause();
        }
        setIsPlaying(false);
        setPlayingSubtitleIndex(null);
      }
      if (value === 'none') {
        setRevealedWords({});
        setPlayingSubtitleIndex(null);
      }
    }
    if (type === 'intensive') {
      const videoElement = isFloatingPlayerMode ? floatingVideoRef.current : videoRef.current;
      if (value !== 'none') {
        if (videoElement && !videoElement.paused) {
          videoElement.pause();
        }
        setIsPlaying(false);
        setPlayingSubtitleIndex(null);
      }
      setIntensiveMode(value);
    }
    // 选择选项后关闭下拉框，但播放模式和字幕模式的下拉框保持打开状态
    if (type !== 'play' && type !== 'bottomPlay' && type !== 'subtitle') {
      setDropdownOpen(prev => ({
        ...prev,
        [type]: false
      }));
    }
  };

  // 更新播放速度
  const updatePlaybackSpeed = (speed) => {
    setPlaybackSpeed(speed);
    const video = isFloatingPlayerMode ? floatingVideoRef.current : videoRef.current;
    if (video) {
      if (video.tagName === 'VIDEO') {
        video.playbackRate = speed;
      } else if (video.tagName === 'IFRAME' && video.src.includes('youtube.com')) {
        // 对于YouTube视频，我们需要使用YouTube Player API来控制播放速度
        // 这里我们可以通过postMessage与iframe通信
        try {
          video.contentWindow.postMessage(JSON.stringify({
            event: 'command',
            func: 'setPlaybackRate',
            args: [speed, true]
          }), '*');
        } catch (error) {
          console.error('无法控制YouTube视频速度:', error);
        }
      }
    }
  };

  // 应用播放模式
  const applyPlayMode = () => {
    const video = isFloatingPlayerMode ? floatingVideoRef.current : videoRef.current;
    if (!video) return;

    switch (playMode) {
      case 'singleLoop':
        video.loop = true;
        break;
      case 'single':
      case 'continuous':
      case 'sentenceLoop':
      default:
        video.loop = false;
        break;
    }
  };

  // 当播放模式改变时应用
  useEffect(() => {
    applyPlayMode();
  }, [playMode]);

  // 视频学习状态
  const [hasWatched50Percent, setHasWatched50Percent] = useState(false);
  const [watchedDuration, setWatchedDuration] = useState(0);
  const lastWatchedTimeRef = useRef(0);

  // 更新学习统计数据
  const updateLearningStats = (timeSpent, completed = false) => {
    try {
      const savedStats = localStorage.getItem('learningStats');
      let stats = savedStats ? JSON.parse(savedStats) : {
        totalMinutes: 0,
        completedVideos: [],
        consecutiveDays: 0,
        lastLearningDate: null,
        learningDays: []
      };
      
      // 确保completedVideos是数组
      if (!Array.isArray(stats.completedVideos)) {
        stats.completedVideos = [];
      }
      // 确保learningDays是数组
      if (!Array.isArray(stats.learningDays)) {
        stats.learningDays = [];
      }
      
      // 更新学习时长
      stats.totalMinutes += timeSpent;
      
      // 如果视频完成（观看超过50%），更新已学习视频数
      if (completed && !stats.completedVideos.includes(lessonId)) {
        stats.completedVideos.push(lessonId);
      }
      
      // 更新最后学习日期和学习天数
      const today = new Date().toDateString();
      if (!stats.learningDays.includes(today)) {
        stats.learningDays.push(today);
      }
      stats.lastLearningDate = today;
      
      localStorage.setItem('learningStats', JSON.stringify(stats));
      console.log('学习统计已更新:', stats);
    } catch (error) {
      console.error('Failed to update learning stats:', error);
    }
  };

  // 确保videoRef获取到视频元素后再设置定时器
  useEffect(() => {
    let intervalId;
    const startTime = Date.now();

    // 直接设置定时器，每次都检查最新的videoRef.current
    intervalId = setInterval(() => {
      const video = isFloatingPlayerMode ? floatingVideoRef.current : videoRef.current;
      if (video) {
        console.log('Checking video progress:', video.tagName);
        
        if (video.tagName === 'VIDEO') {
          // 对于本地视频，使用真实的时间
          if (!isNaN(video.currentTime) && !isNaN(video.duration)) {
            const progressPercentage = (video.currentTime / video.duration) * 100;
            console.log('Video progress:', video.currentTime, '/', video.duration, '->', progressPercentage);
            setCurrentProgress(progressPercentage);
            
            // 同步播放状态
            if (video.paused && isPlaying) {
              setIsPlaying(false);
            } else if (!video.paused && !isPlaying) {
              setIsPlaying(true);
            }
            
            // 计算观看时长（只在视频播放时统计）
            if (!video.paused) {
              const currentTime = video.currentTime;
              if (lastWatchedTimeRef.current > 0) {
                const timeDiff = currentTime - lastWatchedTimeRef.current;
                if (timeDiff > 0 && timeDiff < 1) { // 防止跳帧导致的异常值
                  setWatchedDuration(prev => prev + timeDiff);
                  // 每10秒更新一次学习时长
              if (Math.floor(watchedDuration / 10) !== Math.floor((watchedDuration + timeDiff) / 10)) {
                updateLearningStats(Math.floor(timeDiff / 60)); // 转换为分钟并取整
              }
                }
              }
              lastWatchedTimeRef.current = currentTime;
            } else {
              lastWatchedTimeRef.current = 0;
            }
            
            // 检查是否观看超过50%
            if (progressPercentage >= 50 && !hasWatched50Percent) {
              console.log('Video watched more than 50%, marking as completed');
              setHasWatched50Percent(true);
              updateLearningStats(Math.floor(video.duration / 60), true); // 记录完整时长并标记为完成
            }
          }
        } else if (video.tagName === 'IFRAME') {
          // 对于YouTube视频，使用模拟的时间
          const currentTime = Date.now() / 1000 % 60;
          const progressPercentage = (currentTime / 60) * 100;
          console.log('YouTube progress:', progressPercentage);
          setCurrentProgress(progressPercentage);
          
          // 模拟YouTube视频的观看统计
          const elapsedTime = (Date.now() - startTime) / 1000;
          if (elapsedTime > 0 && !hasWatched50Percent) {
            setWatchedDuration(elapsedTime);
            // 每10秒更新一次学习时长
              if (Math.floor(elapsedTime / 10) !== Math.floor((elapsedTime - 0.1) / 10)) {
                updateLearningStats(Math.floor(10 / 60)); // 每10秒更新一次
              }
            
            // 检查是否观看超过50%
            if (progressPercentage >= 50) {
              console.log('YouTube video watched more than 50%, marking as completed');
              setHasWatched50Percent(true);
              updateLearningStats(Math.floor(60 / 60), true); // 记录60秒时长并标记为完成
            }
          }
        }
      } else {
        console.log('Video element not found yet...');
      }
    }, 100); // 每100毫秒更新一次

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
      // 组件卸载时更新最后一次学习时长
      if (watchedDuration > 0) {
        updateLearningStats(Math.floor(watchedDuration / 60));
      }
    };
  }, [lessonId, hasWatched50Percent, watchedDuration]);

  // 测试进度条更新
  useEffect(() => {
    console.log('Current progress updated:', currentProgress);
  }, [currentProgress]);

  // 更新单词状态
  const updateWordStatus = (id, status) => {
    if (!lesson?._id) return;
    
    // 使用统一的状态管理工具保存状态
    updateItemStatus(lesson._id, id, 'word', status);
    
    // 更新本地状态
    setWordsData(prevWords => 
      prevWords.map(word => {
        if (word.id === id) {
          const newStatus = getItemStatus(lesson._id, id, 'word');
          return { ...word, status: newStatus };
        }
        return word;
      })
    );
  };

  // 更新短语状态
  const updatePhraseStatus = (id, status) => {
    if (!lesson?._id) return;
    
    // 使用统一的状态管理工具保存状态
    updateItemStatus(lesson._id, id, 'phrase', status);
    
    // 更新本地状态
    setPhrasesData(prevPhrases => 
      prevPhrases.map(phrase => {
        if (phrase.id === id) {
          const newStatus = getItemStatus(lesson._id, id, 'phrase');
          return { ...phrase, status: newStatus };
        }
        return phrase;
      })
    );
  };

  // 更新地道表达状态
  const updateExpressionStatus = (id, status) => {
    if (!lesson?._id) return;
    
    // 使用统一的状态管理工具保存状态
    updateItemStatus(lesson._id, id, 'expression', status);
    
    // 更新本地状态
    setExpressionsData(prevExpressions => 
      prevExpressions.map(expression => {
        if (expression.id === id) {
          const newStatus = getItemStatus(lesson._id, id, 'expression');
          return { ...expression, status: newStatus };
        }
        return expression;
      })
    );
  };

  // 监听词汇状态更新事件，实现跨页面同步
  useEffect(() => {
    const handleStatusUpdate = (event) => {
      const { videoId, itemId, itemType, status } = event.detail;
      
      // 只处理当前课程的状态更新
      if (lesson && lesson._id === videoId) {
        // 根据类型更新对应的数据
        if (itemType === 'word') {
          setWordsData(prevWords => 
            prevWords.map(word => 
              word.id === itemId ? { ...word, status } : word
            )
          );
        } else if (itemType === 'phrase') {
          setPhrasesData(prevPhrases => 
            prevPhrases.map(phrase => 
              phrase.id === itemId ? { ...phrase, status } : phrase
            )
          );
        } else if (itemType === 'expression') {
          setExpressionsData(prevExpressions => 
            prevExpressions.map(expression => 
              expression.id === itemId ? { ...expression, status } : expression
            )
          );
        }
      }
    };

    window.addEventListener('vocabularyStatusUpdated', handleStatusUpdate);
    
    return () => {
      window.removeEventListener('vocabularyStatusUpdated', handleStatusUpdate);
    };
  }, [lesson]);

  // 过滤单词
  const filteredWords = wordsData.filter(word => {
    if (wordFilter === 'all') return true;
    if (wordFilter === 'unmarked') return word.status === 'unmarked';
    if (wordFilter === 'known') return word.status === 'known';
    if (wordFilter === 'unknown') return word.status === 'unknown';
    return true;
  });

  // 计算各分类的单词数量
  const wordCounts = {
    all: wordsData.length,
    unmarked: wordsData.filter(w => w.status === 'unmarked').length,
    known: wordsData.filter(w => w.status === 'known').length,
    unknown: wordsData.filter(w => w.status === 'unknown').length
  };

  // 过滤短语
  const filteredPhrases = phrasesData.filter(phrase => {
    if (phraseFilter === 'all') return true;
    if (phraseFilter === 'unmarked') return phrase.status === 'unmarked';
    if (phraseFilter === 'known') return phrase.status === 'known';
    if (phraseFilter === 'unknown') return phrase.status === 'unknown';
    return true;
  });

  // 计算各分类的短语数量
  const phraseCounts = {
    all: phrasesData.length,
    unmarked: phrasesData.filter(p => p.status === 'unmarked').length,
    known: phrasesData.filter(p => p.status === 'known').length,
    unknown: phrasesData.filter(p => p.status === 'unknown').length
  };

  // 过滤地道表达（自然科普词汇）
  const filteredExpressions = expressionsData.filter(expression => {
    if (expressionFilter === 'all') return true;
    if (expressionFilter === 'unmarked') return expression.status === 'unmarked';
    if (expressionFilter === 'known') return expression.status === 'known';
    if (expressionFilter === 'unknown') return expression.status === 'unknown';
    return true;
  });

  // 计算各分类的地道表达数量
  const expressionCounts = {
    all: expressionsData.length,
    unmarked: expressionsData.filter(e => e.status === 'unmarked').length,
    known: expressionsData.filter(e => e.status === 'known').length,
    unknown: expressionsData.filter(e => e.status === 'unknown').length
  };

  // 导出字幕
  const exportSubtitles = () => {
    const subtitleContent = subtitles.map(sub => {
      const timeStr = `${Math.floor(sub.time / 60).toString().padStart(2, '0')}:${Math.floor(sub.time % 60).toString().padStart(2, '0')}`;
      return `
        <p style="margin-bottom: 12pt; padding-bottom: 6pt; border-bottom: 1pt solid #e0e0e0;">
          <span style="color: #666666; font-size: 10pt; display: block; margin-bottom: 3pt;">${timeStr}</span>
          <span style="font-size: 14pt; font-weight: 500; display: block; margin-bottom: 3pt;">${sub.english}</span>
          <span style="font-size: 12pt; color: #444444; display: block;">${sub.chinese}</span>
        </p>
      `;
    }).join('');

    const rtfContent = `{\\rtf1\\ansi\\deff0
{\\fonttbl{\\f0\\fnil Microsoft YaHei;}{\\f1\\fnil SimSun;}{\\f2\\fnil Arial;}}
{\\colortbl;\\red0\\green0\\blue0;\\red102\\green102\\blue102;\\red68\\green68\\blue68;}
\\paperw12240\\paperh15840\\margl1440\\margr1440\\margt1440\\margb1440
\\widowctrl\\hyphauto

{\\pard\\qc\\fs48\\b ${lesson.title}\\par}
\\pard\\sa180

${subtitleContent}

}`;

    const htmlContent = `
      <!DOCTYPE html>
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word">
      <head>
        <meta charset="utf-8">
        <title>${lesson.title}</title>
        <!--[if gte mso 9]>
        <xml>
          <w:WordDocument>
            <w:View>Print</w:View>
            <w:Zoom>100</w:Zoom>
            <w:DoNotOptimizeForBrowser/>
          </w:WordDocument>
        </xml>
        <![endif]-->
        <style>
          @page {
            size: 21cm 29.7cm;
            margin: 2.54cm;
            mso-page-orientation: portrait;
          }
          @page Section1 {
            size: 21cm 29.7cm;
            margin: 2.54cm;
            mso-page-orientation: portrait;
            mso-header-margin: .5in;
            mso-footer-margin: .5in;
            mso-paper-source: 0;
          }
          div.Section1 {page:Section1;}
          body {
            font-family: "Microsoft YaHei", "SimSun", Arial, sans-serif;
            line-height: 1.6;
            font-size: 12pt;
          }
          .header {
            text-align: center;
            font-size: 24pt;
            font-weight: bold;
            margin-bottom: 30pt;
            padding-top: 20pt;
          }
          .subtitle-item {
            margin-bottom: 20pt;
            padding-bottom: 10pt;
            border-bottom: 1pt solid #e0e0e0;
          }
          .time {
            color: #666;
            font-size: 10pt;
            margin-bottom: 5pt;
          }
          .english {
            font-size: 14pt;
            font-weight: 500;
            margin-bottom: 5pt;
          }
          .chinese {
            font-size: 12pt;
            color: #444;
          }
        </style>
      </head>
      <body>
        <div class="Section1">
          <div class="header">${lesson.title}</div>
          ${subtitles.map(sub => {
            const timeStr = `${Math.floor(sub.time / 60).toString().padStart(2, '0')}:${Math.floor(sub.time % 60).toString().padStart(2, '0')}`;
            return `
              <div class="subtitle-item">
                <div class="time">${timeStr}</div>
                <div class="english">${sub.english}</div>
                <div class="chinese">${sub.chinese}</div>
              </div>
            `;
          }).join('')}
        </div>
      </body>
      </html>
    `;
    
    const blob = new Blob(['\ufeff', htmlContent], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${lesson.title}_subtitles.doc`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#ffffff', color: '#333333', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>⏳</div>
          <h2 style={{ fontSize: '2rem', marginBottom: '1rem' }}>加载中</h2>
          <p style={{ fontSize: '1.1rem', color: 'rgba(0,0,0,0.6)' }}>正在加载视频，请稍候...</p>
        </div>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#ffffff', color: '#333333', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>❌</div>
          <h2 style={{ fontSize: '2rem', marginBottom: '1rem' }}>视频不存在</h2>
          <p style={{ fontSize: '1.1rem', marginBottom: '2rem', color: 'rgba(0,0,0,0.6)' }}>抱歉，您请求的视频不存在或已被删除。</p>
          <button 
            onClick={() => navigate('/courses')}
            style={{
              background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
              border: 'none',
              color: 'white',
              padding: '1rem 2rem',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            返回课程列表
          </button>
        </div>
      </div>
    );
  }

  const exercises = lesson.exercises || [];
  const currentExerciseData = exercises[currentExercise];
  const isLastLesson = currentIndex === course.lessons.length - 1;

  return (
    <div style={{ height: '100vh', backgroundColor: '#ffffff', color: '#333333', width: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <style>{`
        @media (max-width: 1366px) {
          .word-card-bottom-nav {
            display: none !important;
          }
          
          .lesson-bottom-button &gt; div[style*="position: absolute"] {
            left: 50% !important;
            transform: translateX(-50%) !important;
            width: calc(100vw - 3rem) !important;
            max-width: 500px !important;
            border-radius: 20px 20px 0 0 !important;
            bottom: 100% !important;
            margin: 0 !important;
            padding-top: 1.75rem !important;
            padding-bottom: 2rem !important;
            box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15) !important;
            position: fixed !important;
          }
          
          .lesson-bottom-button &gt; div[style*="position: absolute"]::before {
            content: '';
            position: absolute;
            top: 0.75rem;
            left: 50%;
            transform: translateX(-50%);
            width: 40px;
            height: 4px;
            background-color: #d1d5db;
            border-radius: 2px;
            z-index: 1001;
          }
        }
        
        /* 桌面端下拉框宽度调整 */
        .dropdown-container &gt; div[style*="position: absolute"] {
          width: 280px !important;
          min-width: 280px !important;
          max-width: 90vw !important;
        }
        
        @media (max-width: 599px) {
          .lesson-bottom-controls {
            gap: 0.75rem !important;
            padding: 0.75rem 1rem !important;
          }
          
          .lesson-bottom-btn {
            padding: 0.5rem 0.75rem !important;
            font-size: 0.75rem !important;
          }
          
          .lesson-bottom-play-btn {
            width: 50px !important;
            height: 50px !important;
            font-size: 1.5rem !important;
          }
        }
      `}</style>
      <div style={{ flex: 1, overflowY: 'hidden', paddingTop: '0px', backgroundColor: '#f8f9fa', width: '100%' }}>
        <div className="container" style={{
          width: '100%',
          maxWidth: '1600px',
          margin: '0 auto',
          padding: '0 1.5rem',
          height: '100%',
          overflow: 'hidden'
        }}>

          


          {/* 桌面端布局 */}
          {isDesktop && (
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'nowrap', justifyContent: 'flex-start', width: '100%', boxSizing: 'border-box', height: '100%', overflow: 'hidden' }}>
              {/* 左侧视频 - 桌面端 */}
              <div style={{ width: intensiveMode === 'none' ? '60%' : '45%', maxWidth: '800px', minWidth: '360px', flexShrink: 1, flexGrow: 0, overflowY: 'auto', paddingBottom: '2rem' }}>
              {/* 视频标题和播放器组合容器 */}
              <div style={{
                borderRadius: '12px',
                overflow: 'hidden',
                backgroundColor: 'white',
                marginBottom: '1.5rem'
              }}>
                {/* 视频标题和信息 - 包含返回分类按钮 */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '1rem',
                  borderBottom: '1px solid #e0e0e0'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    {/* 返回按钮 */}
                    <button 
                      onClick={() => navigate(-1)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#0066ff',
                        textDecoration: 'none',
                        padding: '0.5rem',
                        borderRadius: '8px',
                        backgroundColor: '#f0f7ff',
                        border: '1px solid rgba(0, 102, 255, 0.2)',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        fontSize: '1.2rem',
                        width: '36px',
                        height: '36px'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#e6f0ff';
                        e.target.style.boxShadow = '0 2px 10px rgba(0, 102, 255, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#f0f7ff';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      ←
                    </button>
                    <h2 style={{
                      color: '#333333',
                      fontSize: '1.5rem',
                      fontWeight: '600',
                      margin: 0
                    }}>{lesson?.title || '视频标题'}</h2>
                  </div>
                  <div style={{
                    display: 'flex',
                    gap: '1rem',
                    alignItems: 'center'
                  }}>
                    <span style={{ color: 'rgba(0,0,0,0.6)' }}>时长: {formatTime(videoDuration || 0)}</span>
                    <span style={{
                      backgroundColor: 'rgba(26, 115, 232, 0.1)',
                      color: '#1a73e8',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '4px',
                      fontSize: '0.9rem',
                      border: '1px solid rgba(26, 115, 232, 0.2)'
                    }}>
                      {(() => {
                        if (vocabularyAnalysis) {
                          return vocabularyAnalysis.recommendedLevel || '未知';
                        }
                        return '分析中...';
                      })()}
                    </span>
                    <button
                      onClick={(e) => toggleCollect(e)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#ff4757',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'scale(1.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'scale(1)';
                      }}
                      title={collectedVideos.includes(lesson._id) ? '取消收藏' : '收藏'}
                    >
                      <FiHeart 
                        size={20} 
                        fill={collectedVideos.includes(lesson._id) ? '#ff4757' : 'none'}
                      />
                    </button>
                  </div>
                </div>
                <div style={{ position: 'relative', width: '100%', aspectRatio: '16/9' }}>
                {/* 视频播放器 */}
                {lesson?.content?.videoUrl && (lesson.content.videoUrl.includes('youtube.com') || lesson.content.videoUrl.includes('youtu.be')) ? (
                  <div 
                    style={{
                      width: '100%',
                      height: '100%',
                      overflow: 'hidden',
                      position: 'relative'
                    }}
                    onMouseEnter={() => setIsPlayerHovered(true)}
                    onMouseLeave={() => setIsPlayerHovered(false)}
                  >
                    {/* YouTube视频播放 - 隐藏默认控制栏 */}
                    {(() => {
                      let videoId = '';
                      let originalUrl = lesson.content.videoUrl;
                      console.log('Original YouTube URL:', originalUrl);
                      
                      // 提取YouTube视频ID
                      if (originalUrl.includes('youtu.be')) {
                        videoId = originalUrl.split('/').pop().split('?')[0];
                      } else if (originalUrl.includes('youtube.com/watch')) {
                        try {
                          const urlParams = new URLSearchParams(new URL(originalUrl).search);
                          videoId = urlParams.get('v');
                        } catch (error) {
                          console.error('Error parsing YouTube URL:', error);
                        }
                      } else if (originalUrl.includes('youtube.com/embed/')) {
                        videoId = originalUrl.split('/embed/')[1].split('?')[0];
                      }
                      
                      console.log('Extracted video ID:', videoId);
                      
                      // 转换为嵌入链接，禁用所有默认控件
                      let embedUrl = '';
                      if (videoId) {
                        embedUrl = `https://www.youtube.com/embed/${videoId}`;
                      } else {
                        // 如果无法提取视频ID，使用默认视频
                        embedUrl = 'https://www.youtube.com/embed/3tisOnOkwzo';
                        console.log('Using default video since video ID extraction failed');
                      }
                      
                      // 确保包含必要的参数，禁用所有默认控件，不自动播放（加载更快）
                      if (!embedUrl.includes('?')) {
                        embedUrl += '?';
                      } else if (!embedUrl.endsWith('?')) {
                        embedUrl += '&';
                      }
                      // 不自动播放，让用户手动点击播放
                      embedUrl += `rel=0&showinfo=0&playsinline=1&enablejsapi=1&controls=0&disablekb=0&modestbranding=1&iv_load_policy=3&fs=0&loop=0&cc_load_policy=3`;
                      
                      console.log('Final embed URL:', embedUrl);
                      
                      return (
                        <iframe
                          ref={videoRef}
                          width="100%"
                          height="100%"
                          src={embedUrl}
                          title={lesson.title}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
                          allowFullScreen
                          style={{ 
                            width: '100%', 
                            height: '100%',
                            display: 'block',
                            cursor: 'pointer'
                          }}
                          onClick={() => {
                            if (videoRef.current) {
                              const video = videoRef.current;
                              try {
                                // 切换播放/暂停状态
                                if (isPlaying) {
                                  video.contentWindow.postMessage(JSON.stringify({
                                    event: 'command',
                                    func: 'pauseVideo',
                                    args: []
                                  }), '*');
                                  setIsPlaying(false);
                                } else {
                                  video.contentWindow.postMessage(JSON.stringify({
                                    event: 'command',
                                    func: 'playVideo',
                                    args: []
                                  }), '*');
                                  setIsPlaying(true);
                                }
                              } catch (error) {
                                console.error('无法控制YouTube视频:', error);
                              }
                            }
                          }}
                        ></iframe>
                      );
                    })()}
                    
                    {/* 自定义控制栏 - 绝对定位在视频容器底部 */}
                    <div style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      backgroundColor: 'transparent',
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 10px',
                      gap: '10px',
                      opacity: (isPlaying && !isPlayerHovered) ? 0 : 1,
                      transition: 'opacity 0.3s ease'
                    }}>
                      {/* 播放/暂停按钮 */}
                      <button
                        onClick={() => {
                          if (videoRef.current) {
                            const video = videoRef.current;
                            try {
                              // 切换播放/暂停状态
                              if (isPlaying) {
                                video.contentWindow.postMessage(JSON.stringify({
                                  event: 'command',
                                  func: 'pauseVideo',
                                  args: []
                                }), '*');
                                setIsPlaying(false);
                              } else {
                                video.contentWindow.postMessage(JSON.stringify({
                                  event: 'command',
                                  func: 'playVideo',
                                  args: []
                                }), '*');
                                setIsPlaying(true);
                              }
                            } catch (error) {
                              console.error('无法控制YouTube视频:', error);
                            }
                          }
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'white',
                          fontSize: '16px',
                          cursor: 'pointer',
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {isPlaying ? '⏸' : '▶'}
                      </button>
                      
                      {/* 进度条 */}
                      <div 
                        style={{
                          flex: 1,
                          height: '6px',
                          backgroundColor: '#e0e0e0',
                          borderRadius: '3px',
                          position: 'relative',
                          cursor: 'pointer',
                          touchAction: 'none'
                        }}
                        onMouseDown={(e) => {
                          setIsProgressDragging(true);
                          // 处理初始点击位置
                          const rect = e.currentTarget.getBoundingClientRect();
                          const x = e.clientX - rect.left;
                          const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
                          setCurrentProgress(percentage);
                          
                          // 控制视频进度
                          const video = isFloatingPlayerMode ? floatingVideoRef.current : videoRef.current;
                          if (video) {
                            const duration = video.duration || 60; // 默认60秒
                            const seekTime = (percentage / 100) * duration;
                            video.currentTime = seekTime;
                          }
                        }}
                        onMouseMove={(e) => {
                          // 只有在拖动状态下才响应
                          if (isProgressDragging) {
                            const rect = e.currentTarget.getBoundingClientRect();
                            const x = e.clientX - rect.left;
                            const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
                            setCurrentProgress(percentage);
                            
                            // 控制视频进度
                            const video = isFloatingPlayerMode ? floatingVideoRef.current : videoRef.current;
                            if (video) {
                              const duration = video.duration || 60; // 默认60秒
                              const seekTime = (percentage / 100) * duration;
                              video.currentTime = seekTime;
                            }
                          }
                        }}
                        onMouseUp={() => {
                          setIsProgressDragging(false);
                        }}
                        onMouseLeave={() => {
                          setIsProgressDragging(false);
                        }}
                        onTouchStart={(e) => {
                          setIsProgressDragging(true);
                          // 处理初始触摸位置
                          const rect = e.currentTarget.getBoundingClientRect();
                          const touch = e.touches[0];
                          const x = touch.clientX - rect.left;
                          const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
                          setCurrentProgress(percentage);
                          
                          // 控制视频进度
                          const video = isFloatingPlayerMode ? floatingVideoRef.current : videoRef.current;
                          if (video) {
                            const duration = video.duration || 60; // 默认60秒
                            const seekTime = (percentage / 100) * duration;
                            video.currentTime = seekTime;
                          }
                        }}
                        onTouchMove={(e) => {
                          // 只有在拖动状态下才响应
                          if (isProgressDragging) {
                            const rect = e.currentTarget.getBoundingClientRect();
                            const touch = e.touches[0];
                            const x = touch.clientX - rect.left;
                            const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
                            setCurrentProgress(percentage);
                            
                            // 控制视频进度
                            const video = isFloatingPlayerMode ? floatingVideoRef.current : videoRef.current;
                            if (video) {
                              const duration = video.duration || 60; // 默认60秒
                              const seekTime = (percentage / 100) * duration;
                              video.currentTime = seekTime;
                            }
                          }
                        }}
                        onTouchEnd={() => {
                          setIsProgressDragging(false);
                        }}
                      >
                        <div style={{
                          height: '100%',
                          width: `${currentProgress}%`,
                          backgroundColor: '#0066ff',
                          borderRadius: '3px'
                        }}></div>
                        <div style={{
                          position: 'absolute',
                          left: `${currentProgress}%`,
                          top: '50%',
                          transform: 'translate(-50%, -50%)',
                          width: '12px',
                          height: '12px',
                          backgroundColor: '#0066ff',
                          borderRadius: '50%',
                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)'
                        }}></div>
                      </div>
                      
                      {/* 时间显示 */}
                      <div style={{
                        color: 'white',
                        fontSize: '12px',
                        fontFamily: 'Arial, sans-serif',
                        minWidth: '80px'
                      }}>
                        {formatTime(currentTime)} / {formatTime(videoDuration || 0)}
                      </div>
                      
                      {/* 全屏按钮 */}
                      <button
                        onClick={() => {
                          if (videoRef.current) {
                            const video = videoRef.current;
                            try {
                              video.contentWindow.postMessage(JSON.stringify({
                                event: 'command',
                                func: 'requestFullscreen',
                                args: []
                              }), '*');
                            } catch (error) {
                              console.error('无法控制YouTube视频全屏:', error);
                            }
                          }
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '500'
                        }}
                      >
                        ⛶
                      </button>
                    </div>
                  </div>
                ) : (
                  <div 
                    style={{
                      width: '100%',
                      height: '100%',
                      overflow: 'hidden',
                      position: 'relative'
                    }}
                    onMouseEnter={() => setIsPlayerHovered(true)}
                    onMouseLeave={() => setIsPlayerHovered(false)}
                  >
                    {/* 确定文件类型 */}
                    {(() => {
                      const fileType = lesson?.fileType || lesson?.type || 'video';
                      const isAudio = fileType === 'audio' || fileType === '音频';
                      const mediaUrl = lesson?.content?.videoUrl 
                        ? (lesson.content.videoUrl.startsWith('http') 
                            ? lesson.content.videoUrl 
                            : getMediaUrl(lesson.content.videoUrl))
                        : (isAudio 
                            ? '' 
                            : 'https://www.w3schools.com/html/mov_bbb.mp4');
                      const posterUrl = lesson?.content?.posterUrl 
                        ? getMediaUrl(lesson.content.posterUrl)
                        : 'https://images.unsplash.com/photo-1516110833967-8c716bf8273c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80';

                      if (isAudio) {
                        // 音频播放模式：显示封面图 + 音频播放器
                        return (
                          <>
                            {/* 封面图片 */}
                            <div style={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#000'
                            }}>
                              <img 
                                src={posterUrl}
                                alt="音频封面"
                                style={{
                                  maxWidth: '100%',
                                  maxHeight: '100%',
                                  objectFit: 'contain'
                                }}
                              />
                            </div>
                            {/* 隐藏的音频播放器，用于控制播放 */}
                            <audio
                              ref={videoRef}
                              style={{ display: 'none' }}
                              src={mediaUrl}
                              onTimeUpdate={() => {
                                if (videoRef.current) {
                                  setCurrentTime(videoRef.current.currentTime);
                                  if (videoRef.current.duration) {
                                    setCurrentProgress((videoRef.current.currentTime / videoRef.current.duration) * 100);
                                  }
                                }
                              }}
                              onLoadedMetadata={() => {
                                if (videoRef.current) {
                                  setVideoDuration(videoRef.current.duration);
                                  setCurrentProgress(0);
                                }
                              }}
                              onLoadedData={(e) => {
                                console.log('音频加载完成');
                              }}
                            />
                            {/* 点击封面区域控制播放 */}
                            <div 
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                cursor: 'pointer'
                              }}
                              onClick={() => {
                                if (videoRef.current) {
                                  const audio = videoRef.current;
                                  if (isPlaying) {
                                    audio.pause();
                                    setIsPlaying(false);
                                  } else {
                                    audio.play().catch(error => {
                                      console.error('播放音频失败:', error);
                                    });
                                    setIsPlaying(true);
                                  }
                                }
                              }}
                            />
                          </>
                        );
                      } else {
                        // 视频播放模式
                        return (
                          <video
                            ref={videoRef}
                            style={{
                              width: '100%',
                              height: '100%',
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              if (videoRef.current) {
                                const video = videoRef.current;
                                if (isPlaying) {
                                  video.pause();
                                  setIsPlaying(false);
                                } else {
                                  video.play().catch(error => {
                                    console.error('播放视频失败:', error);
                                  });
                                  setIsPlaying(true);
                                }
                              }
                            }}
                            playsInline
                            disablePictureInPicture
                            controlsList="nodownload nofullscreen noremoteplayback"
                            poster={posterUrl}
                            onTimeUpdate={() => {
                              if (videoRef.current) {
                                setCurrentTime(videoRef.current.currentTime);
                                if (videoRef.current.duration) {
                                  setCurrentProgress((videoRef.current.currentTime / videoRef.current.duration) * 100);
                                }
                              }
                            }}
                            onLoadedMetadata={() => {
                              if (videoRef.current) {
                                setVideoDuration(videoRef.current.duration);
                                setCurrentProgress(0);
                              }
                            }}
                            onLoadedData={(e) => {
                              console.log('视频加载完成，触发自动播放');
                              const video = e.target;
                              // 禁用所有字幕轨道
                              if (video.textTracks) {
                                for (let i = 0; i < video.textTracks.length; i++) {
                                  video.textTracks[i].mode = 'hidden';
                                }
                              }
                              video.play().then(() => {
                                // 检查视频元素是否仍然存在
                                if (document.contains(video)) {
                                  console.log('视频自动播放成功');
                                  // 1秒后取消静音
                                  setTimeout(() => {
                                    if (document.contains(video)) {
                                      video.muted = false;
                                      video.volume = currentVolume / 100;
                                      setIsMuted(false);
                                      console.log('视频已取消静音');
                                    }
                                  }, 1000);
                                }
                              }).catch(error => {
                                console.error('视频自动播放失败:', error);
                              });
                            }}
                          >
                            {/* 使用本地上传的视频 */}
                            <source src={mediaUrl} type="video/mp4" />
                            <source src="https://www.w3schools.com/html/mov_bbb.mp4" type="video/mp4" />
                            Your browser does not support the video tag.
                          </video>
                        );
                      }
                    })()}
                    
                    {/* 自定义控制栏 - 绝对定位在视频容器底部 */}
                    <div style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      backgroundColor: 'transparent',
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 10px',
                      gap: '10px',
                      opacity: (isPlaying && !isPlayerHovered) ? 0 : 1,
                      transition: 'opacity 0.3s ease'
                    }}>
                      {/* 播放/暂停按钮 */}
                      <button
                        onClick={() => {
                          if (videoRef.current) {
                            const video = videoRef.current;
                            if (isPlaying) {
                              video.pause();
                              setIsPlaying(false);
                            } else {
                              video.play().catch(error => {
                                console.error('播放视频失败:', error);
                              });
                              setIsPlaying(true);
                            }
                          }
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'white',
                          fontSize: '16px',
                          cursor: 'pointer',
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {isPlaying ? '⏸' : '▶'}
                      </button>
                      
                      {/* 音量控制 */}
                      <div 
                        style={{ 
                          position: 'relative',
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px',
                          padding: '4px 0'
                        }}
                      >
                        <button
                          onClick={() => {
                            // 点击时显示音量滑块
                            setShowVolumeSlider(true);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'white',
                            cursor: 'pointer',
                            width: '24px',
                            height: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {(isMuted || currentVolume <= 0) ? (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                              <line x1="23" y1="9" x2="17" y2="15"></line>
                              <line x1="17" y1="9" x2="23" y2="15"></line>
                            </svg>
                          ) : (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                              <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                            </svg>
                          )}
                        </button>
                        {/* 音量滑块 - 仅在鼠标悬停时显示 */}
                        {showVolumeSlider && (
                          <div 
                            style={{
                              position: 'absolute',
                              top: '-120px',
                              right: '-10px',
                              backgroundColor: 'transparent',
                              padding: '10px',
                              borderRadius: '6px',
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                              zIndex: 1000,
                              height: '100px'
                            }}
                            onMouseDown={(e) => {
                              setIsVolumeDragging(true);
                              // 处理初始点击位置
                              const rect = e.currentTarget.getBoundingClientRect();
                              const y = e.clientY - rect.top;
                              const percentage = Math.max(0, Math.min(100, 100 - (y / rect.height) * 100));
                              setCurrentVolume(percentage);
                              
                              // 当音量为0时，设置为静音状态
                              if (percentage <= 1) {
                                setIsMuted(true);
                              } else if (isMuted) {
                                setIsMuted(false);
                              }
                              
                              if (videoRef.current) {
                                const video = videoRef.current;
                                video.volume = percentage / 100;
                                video.muted = percentage <= 1;
                              }
                            }}
                            onMouseMove={(e) => {
                              // 只有在拖动状态下才响应
                              if (isVolumeDragging) {
                                // 计算鼠标在滑块上的位置（竖向）
                                const rect = e.currentTarget.getBoundingClientRect();
                                const y = e.clientY - rect.top;
                                // 反转百分比，因为滑块是从上到下
                                const percentage = Math.max(0, Math.min(100, 100 - (y / rect.height) * 100));
                                setCurrentVolume(percentage);
                                // 当音量为0时，设置为静音状态
                                if (percentage <= 1) {
                                  setIsMuted(true);
                                } else if (isMuted) {
                                  setIsMuted(false);
                                }
                                
                                // 设置视频音量
                                if (videoRef.current) {
                                  const video = videoRef.current;
                                  video.volume = percentage / 100;
                                  video.muted = percentage <= 1;
                                }
                              }
                            }}
                            onMouseUp={() => {
                              setIsVolumeDragging(false);
                            }}
                            onMouseLeave={() => {
                              setIsVolumeDragging(false);
                              // 延迟关闭，确保用户有时间操作
                              setTimeout(() => {
                                setShowVolumeSlider(false);
                              }, 200);
                            }}
                          >
                            <div style={{
                              height: '100%',
                              width: '4px',
                              backgroundColor: '#e0e0e0',
                              borderRadius: '2px',
                              position: 'relative',
                              cursor: 'pointer',
                              margin: '0 auto'
                            }}>
                              <div style={{
                                position: 'absolute',
                                bottom: 0,
                                width: '100%',
                                height: `${currentVolume}%`,
                                backgroundColor: '#0066ff',
                                borderRadius: '2px'
                              }}></div>
                              <div style={{
                                position: 'absolute',
                                left: '50%',
                                bottom: `${currentVolume}%`,
                                transform: 'translate(-50%, 50%)',
                                width: '12px',
                                height: '12px',
                                backgroundColor: '#0066ff',
                                borderRadius: '50%',
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)'
                              }}></div>
                            </div>

                          </div>
                        )}
                      </div>
                      
                      {/* 进度条 */}
                      <div 
                        style={{
                          flex: 1,
                          height: '6px',
                          backgroundColor: '#e0e0e0',
                          borderRadius: '3px',
                          position: 'relative',
                          cursor: 'pointer',
                          touchAction: 'none'
                        }}
                        onMouseDown={(e) => {
                          setIsProgressDragging(true);
                          // 处理初始点击位置
                          const rect = e.currentTarget.getBoundingClientRect();
                          const x = e.clientX - rect.left;
                          const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
                          setCurrentProgress(percentage);
                          
                          // 控制视频进度
                          const video = isFloatingPlayerMode ? floatingVideoRef.current : videoRef.current;
                          if (video) {
                            const duration = video.duration || 60; // 默认60秒
                            const seekTime = (percentage / 100) * duration;
                            video.currentTime = seekTime;
                          }
                        }}
                        onMouseMove={(e) => {
                          // 只有在拖动状态下才响应
                          if (isProgressDragging) {
                            const rect = e.currentTarget.getBoundingClientRect();
                            const x = e.clientX - rect.left;
                            const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
                            setCurrentProgress(percentage);
                            
                            // 控制视频进度
                            const video = isFloatingPlayerMode ? floatingVideoRef.current : videoRef.current;
                            if (video) {
                              const duration = video.duration || 60; // 默认60秒
                              const seekTime = (percentage / 100) * duration;
                              video.currentTime = seekTime;
                            }
                          }
                        }}
                        onMouseUp={() => {
                          setIsProgressDragging(false);
                        }}
                        onMouseLeave={() => {
                          setIsProgressDragging(false);
                        }}
                        onTouchStart={(e) => {
                          setIsProgressDragging(true);
                          // 处理初始触摸位置
                          const rect = e.currentTarget.getBoundingClientRect();
                          const touch = e.touches[0];
                          const x = touch.clientX - rect.left;
                          const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
                          setCurrentProgress(percentage);
                          
                          // 控制视频进度
                          const video = isFloatingPlayerMode ? floatingVideoRef.current : videoRef.current;
                          if (video) {
                            const duration = video.duration || 60; // 默认60秒
                            const seekTime = (percentage / 100) * duration;
                            video.currentTime = seekTime;
                          }
                        }}
                        onTouchMove={(e) => {
                          // 只有在拖动状态下才响应
                          if (isProgressDragging) {
                            const rect = e.currentTarget.getBoundingClientRect();
                            const touch = e.touches[0];
                            const x = touch.clientX - rect.left;
                            const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
                            setCurrentProgress(percentage);
                            
                            // 控制视频进度
                            const video = isFloatingPlayerMode ? floatingVideoRef.current : videoRef.current;
                            if (video) {
                              const duration = video.duration || 60; // 默认60秒
                              const seekTime = (percentage / 100) * duration;
                              video.currentTime = seekTime;
                            }
                          }
                        }}
                        onTouchEnd={() => {
                          setIsProgressDragging(false);
                        }}
                      >
                        <div style={{
                          height: '100%',
                          width: `${currentProgress}%`,
                          backgroundColor: '#0066ff',
                          borderRadius: '3px'
                        }}></div>
                        <div style={{
                          position: 'absolute',
                          left: `${currentProgress}%`,
                          top: '50%',
                          transform: 'translate(-50%, -50%)',
                          width: '12px',
                          height: '12px',
                          backgroundColor: '#0066ff',
                          borderRadius: '50%',
                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)'
                        }}></div>
                      </div>
                      
                      {/* 时间显示 */}
                      <div style={{
                        color: 'white',
                        fontSize: '12px',
                        fontFamily: 'Arial, sans-serif',
                        minWidth: '80px'
                      }}>
                        {formatTime(currentTime)} / {formatTime(videoDuration || 0)}
                      </div>
                      
                      {/* 倍速控制 */}
                      <div className="dropdown-container" style={{ position: 'relative' }}>
                        <button
                          onClick={() => toggleDropdown('speed')}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '16px',
                            fontFamily: 'Arial, sans-serif',
                            fontWeight: '600'
                          }}
                        >
                          <span>{playbackSpeed}x</span>
                        </button>
                        {dropdownOpen.speed && (
                          <div style={{
                            position: 'absolute',
                            bottom: '100%',
                            right: 0,
                            marginBottom: '5px',
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            borderRadius: '8px',
                            padding: '8px',
                            zIndex: 1000,
                            minWidth: '90px',
                            color: 'white',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                          }}>
                            {[0.5, 0.75, 1, 1.25, 1.5, 2].map(speed => (
                              <div 
                                key={speed}
                                style={{
                                  marginBottom: '4px',
                                  padding: '8px 12px',
                                  cursor: 'pointer',
                                  fontSize: '16px',
                                  fontFamily: 'Arial, sans-serif',
                                  backgroundColor: playbackSpeed === speed ? '#1a73e8' : 'transparent',
                                  borderRadius: '6px',
                                  color: 'white',
                                  fontWeight: playbackSpeed === speed ? '600' : 'normal'
                                }}
                                onClick={() => updatePlaybackSpeed(speed)}
                              >
                                {speed}x
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* 全屏按钮 */}
                      <button
                        onClick={() => {
                          if (videoRef.current) {
                            const video = videoRef.current;
                            if (video.requestFullscreen) {
                              video.requestFullscreen();
                            } else if (video.webkitRequestFullscreen) {
                              video.webkitRequestFullscreen();
                            } else if (video.msRequestFullscreen) {
                              video.msRequestFullscreen();
                            }
                          }
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '18px',
                          fontWeight: '600'
                        }}
                      >
                        ⛶
                      </button>
                    </div>
                  </div>
                )}
                </div>
              </div>
              
              {/* 词汇统计表格 */}
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '0.75rem',
                border: '1px solid #e8eef4',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                marginTop: '1rem'
              }}>
                {analyzingVocabulary ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '0.75rem',
                    color: '#666',
                    fontSize: '0.9rem'
                  }}>
                    正在分析词汇...
                  </div>
                ) : vocabularyError ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '0.75rem',
                    color: '#dc3545',
                    fontSize: '0.9rem'
                  }}>
                    {vocabularyError}
                  </div>
                ) : vocabularyAnalysis ? (
                  <div>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      textAlign: 'center',
                      fontSize: '0.8rem',
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                      tableLayout: 'fixed'
                    }}>
                      <thead>
                        {(() => {
                          const levels = ['Starters', 'Movers', 'Flyers', 'KET', 'PET', 'FCE'];
                          const maxCount = Math.max(...levels.map(level => vocabularyAnalysis.levelCounts[level] || 0));
                          const maxLevel = levels.find(level => vocabularyAnalysis.levelCounts[level] === maxCount);
                          return (
                            <tr style={{
                              backgroundColor: '#f8fafc'
                            }}>
                              {levels.map(level => {
                                const isMax = level === maxLevel && maxCount > 0;
                                return (
                                  <th key={level} style={{
                                    padding: '0.3rem',
                                    border: 'none',
                                    backgroundColor: isMax ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.08)',
                                    color: isMax ? '#2563eb' : '#475569',
                                    fontWeight: isMax ? '700' : '600',
                                    transition: 'all 0.2s ease'
                                  }}>
                                    {level}
                                  </th>
                                );
                              })}
                              <th style={{
                                padding: '0.3rem',
                                border: 'none',
                                backgroundColor: 'rgba(59, 130, 246, 0.08)',
                                color: '#475569',
                                fontWeight: '600'
                              }}>其他</th>
                            </tr>
                          );
                        })()}
                      </thead>
                      <tbody>
                        <tr>
                          {(() => {
                            const levels = ['Starters', 'Movers', 'Flyers', 'KET', 'PET', 'FCE'];
                            const maxCount = Math.max(...levels.map(level => vocabularyAnalysis.levelCounts[level] || 0));
                            const rows = levels.map(level => {
                              const count = vocabularyAnalysis.levelCounts[level] || 0;
                              const isMax = count === maxCount && maxCount > 0;
                              return (
                                <td key={level} style={{
                                  padding: '0.3rem',
                                  backgroundColor: isMax ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.08)',
                                  color: isMax ? '#2563eb' : '#334155',
                                  fontWeight: isMax ? '600' : 'normal',
                                  transition: 'all 0.2s ease'
                                }}>
                                  {count}
                                </td>
                              );
                            });
                            // 添加"其他"列
                            rows.push(
                              <td key="Other" style={{
                                padding: '0.3rem',
                                backgroundColor: 'rgba(59, 130, 246, 0.08)',
                                color: '#334155',
                                fontWeight: 'normal',
                                transition: 'all 0.2s ease'
                              }}>
                                {vocabularyAnalysis.levelCounts.Other || 0}
                              </td>
                            );
                            return rows;
                          })()}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={{
                    textAlign: 'center',
                    padding: '0.75rem',
                    color: '#666',
                    fontSize: '0.9rem'
                  }}>
                    正在加载词汇分析...
                  </div>
                )}
              </div>
              
              {/* 简介 */}
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '1.5rem',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                marginTop: '0.8rem'
              }}>
                <h3 style={{
                  color: '#333333',
                  fontSize: '1.2rem',
                  fontWeight: '600',
                  margin: '0 0 1rem 0'
                }}>简介</h3>
                <p style={{
                  color: 'rgba(0,0,0,0.7)',
                  lineHeight: '1.6',
                  margin: 0
                }}>
                  {lesson.description || '这是一个科学视频，通过生动的讲解和实验，帮助孩子们了解科学知识。'}
                </p>
              </div>
            </div>
            
            {/* 右侧动态字幕 */}
            <div style={{ flex: 1, minWidth: isMobile ? 0 : '300px', maxWidth: isMobile ? '100%' : '600px', flexShrink: 1, height: '100%' }}>
              <div style={{
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                padding: '1.5rem',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '1.5rem',
                  boxSizing: 'border-box',
                  paddingRight: '0.5rem',
                  flexShrink: 0
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: 0 }}>
                    <h3 style={{
                      color: '#333333',
                      fontSize: intensiveMode === 'none' ? '1.2rem' : '1rem',
                      fontWeight: '600',
                      margin: 0,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      flexShrink: 1
                    }}>动态字幕</h3>
                  </div>
                  <div style={{
                    display: 'flex',
                    gap: '0.5rem',
                    alignItems: 'center',
                    flexShrink: 0
                  }}>
                      {/* 重置功能 - 已隐藏 */}
                    {/* <div className="dropdown-container" style={{ position: 'relative' }} onMouseEnter={(e) => {
                      const tooltip = e.currentTarget.querySelector('.tooltip');
                      if (tooltip) {
                        tooltip.style.opacity = '1';
                        tooltip.style.visibility = 'visible';
                      }
                    }} onMouseLeave={(e) => {
                      const tooltip = e.currentTarget.querySelector('.tooltip');
                      if (tooltip) {
                        tooltip.style.opacity = '0';
                        tooltip.style.visibility = 'hidden';
                      }
                    }}>
                      <button
                        onClick={() => setShowResetConfirm(true)}
                        style={{
                          background: 'rgba(255, 255, 255, 0.95)',
                          border: '1px solid rgba(0, 0, 0, 0.2)',
                          color: 'white',
                          padding: '0.5rem',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '40px',
                          height: '40px',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = 'rgba(255, 100, 100, 0.1)';
                          e.target.style.borderColor = 'rgba(255, 100, 100, 0.3)';
                          e.target.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
                          e.target.style.borderColor = 'rgba(0, 0, 0, 0.2)';
                          e.target.style.transform = 'scale(1)';
                        }}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"></path>
                        </svg>
                      </button>
                      <div className="tooltip" style={{
                        position: 'absolute',
                        bottom: '100%',
                        right: 0,
                        marginBottom: '8px',
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        whiteSpace: 'nowrap',
                        opacity: 0,
                        visibility: 'hidden',
                        transition: 'opacity 0.2s, visibility 0.2s',
                        pointerEvents: 'none'
                      }}>
                        重置进度
                      </div>
                    </div> */}
                    
                      {/* 回到首页 */}
                    <div className="dropdown-container" style={{ position: 'relative', flexShrink: 0 }} onMouseEnter={(e) => {
                      const tooltip = e.currentTarget.querySelector('.tooltip');
                      if (tooltip) {
                        tooltip.style.opacity = '1';
                        tooltip.style.visibility = 'visible';
                      }
                    }} onMouseLeave={(e) => {
                      const tooltip = e.currentTarget.querySelector('.tooltip');
                      if (tooltip) {
                        tooltip.style.opacity = '0';
                        tooltip.style.visibility = 'hidden';
                      }
                    }}>
                      <button
                        onClick={() => navigate('/')}
                        style={{
                          background: 'rgba(255, 255, 255, 0.95)',
                          border: '1px solid rgba(0, 0, 0, 0.2)',
                          color: '#333333',
                          padding: '0.5rem',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '40px',
                          height: '40px',
                          transition: 'all 0.3s ease',
                          flexShrink: 0
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = 'rgba(0, 102, 255, 0.1)';
                          e.target.style.borderColor = 'rgba(0, 102, 255, 0.3)';
                          e.target.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
                          e.target.style.borderColor = 'rgba(0, 0, 0, 0.2)';
                          e.target.style.transform = 'scale(1)';
                        }}
                      >
                        <FiHome style={{ fontSize: '1.2rem' }} />
                      </button>
                      <div className="tooltip" style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        marginTop: '8px',
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        whiteSpace: 'nowrap',
                        opacity: 0,
                        visibility: 'hidden',
                        transition: 'opacity 0.2s, visibility 0.2s',
                        pointerEvents: 'none',
                        zIndex: 9999
                      }}>
                        回到首页
                      </div>
                    </div>
                    
                      {/* 字幕控制 */}
                    <div className="dropdown-container" style={{ position: 'relative' }} onMouseEnter={(e) => {
                      const tooltip = e.currentTarget.querySelector('.tooltip');
                      if (tooltip) {
                        tooltip.style.opacity = '1';
                        tooltip.style.visibility = 'visible';
                      }
                    }} onMouseLeave={(e) => {
                      const tooltip = e.currentTarget.querySelector('.tooltip');
                      if (tooltip) {
                        tooltip.style.opacity = '0';
                        tooltip.style.visibility = 'hidden';
                      }
                    }}>
                      <button
                        onClick={() => toggleDropdown('subtitle')}
                        style={{
                          background: 'rgba(255, 255, 255, 0.95)',
                          border: '1px solid rgba(0, 0, 0, 0.2)',
                          color: '#333333',
                          padding: '0.5rem',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '40px',
                          height: '40px',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = 'rgba(26, 115, 232, 0.1)';
                          e.target.style.borderColor = 'rgba(26, 115, 232, 0.3)';
                          e.target.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
                          e.target.style.borderColor = 'rgba(0, 0, 0, 0.2)';
                          e.target.style.transform = 'scale(1)';
                        }}
                      >
                        <div style={{ fontSize: '1rem', position: 'relative', width: '24px', height: '26px' }}>
                          <span style={{ position: 'absolute', top: 0, left: 0, fontWeight: 'bold' }}>文</span>
                          <span style={{ position: 'absolute', bottom: '-2px', right: '-1px', fontSize: '0.8em', fontWeight: 'bold' }}>A</span>
                        </div>
                      </button>
                      <div className="tooltip" style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        marginTop: '8px',
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        whiteSpace: 'nowrap',
                        opacity: 0,
                        visibility: 'hidden',
                        transition: 'opacity 0.2s, visibility 0.2s',
                        pointerEvents: 'none',
                        zIndex: 9999
                      }}>
                        字幕
                      </div>
                      {dropdownOpen.subtitle && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          right: 0,
                          marginTop: '0.5rem',
                          backgroundColor: 'white',
                          border: '1px solid rgba(0, 0, 0, 0.15)',
                          borderRadius: '12px',
                          padding: '0.75rem',
                          zIndex: 1000,
                          minWidth: '120px',
                          width: 'fit-content',
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
                        }}>
                          <div 
                            style={{ marginBottom: '0.5rem', padding: '0.75rem', borderRadius: '8px', backgroundColor: subtitleMode === 'bilingual' ? 'rgba(26, 115, 232, 0.1)' : 'transparent', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', transition: 'all 0.2s ease' }}
                            onClick={() => selectOption('subtitle', 'bilingual')}
                            onMouseEnter={(e) => {
                              if (subtitleMode !== 'bilingual') {
                                e.currentTarget.style.backgroundColor = 'rgba(26, 115, 232, 0.05)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (subtitleMode !== 'bilingual') {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }
                            }}
                          >
                            双语 {subtitleMode === 'bilingual' && <span style={{ color: 'green', fontWeight: 'bold' }}>✓</span>}
                          </div>
                          <div 
                            style={{ marginBottom: '0.5rem', padding: '0.75rem', borderRadius: '8px', backgroundColor: subtitleMode === 'english' ? 'rgba(26, 115, 232, 0.1)' : 'transparent', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', transition: 'all 0.2s ease' }}
                            onClick={() => selectOption('subtitle', 'english')}
                            onMouseEnter={(e) => {
                              if (subtitleMode !== 'english') {
                                e.currentTarget.style.backgroundColor = 'rgba(26, 115, 232, 0.05)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (subtitleMode !== 'english') {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }
                            }}
                          >
                            英文 {subtitleMode === 'english' && <span style={{ color: 'green', fontWeight: 'bold' }}>✓</span>}
                          </div>
                          <div 
                            style={{ padding: '0.75rem', borderRadius: '8px', backgroundColor: subtitleMode === 'chinese' ? 'rgba(26, 115, 232, 0.1)' : 'transparent', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', transition: 'all 0.2s ease' }}
                            onClick={() => selectOption('subtitle', 'chinese')}
                            onMouseEnter={(e) => {
                              if (subtitleMode !== 'chinese') {
                                e.currentTarget.style.backgroundColor = 'rgba(26, 115, 232, 0.05)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (subtitleMode !== 'chinese') {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }
                            }}
                          >
                            中文 {subtitleMode === 'chinese' && <span style={{ color: 'green', fontWeight: 'bold' }}>✓</span>}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* 播放模式控制 */}
                    <div className="dropdown-container" style={{ position: 'relative' }} onMouseEnter={(e) => {
                      const tooltip = e.currentTarget.querySelector('.tooltip');
                      if (tooltip) {
                        tooltip.style.opacity = '1';
                        tooltip.style.visibility = 'visible';
                      }
                    }} onMouseLeave={(e) => {
                      const tooltip = e.currentTarget.querySelector('.tooltip');
                      if (tooltip) {
                        tooltip.style.opacity = '0';
                        tooltip.style.visibility = 'hidden';
                      }
                    }}>
                      <button
                        onClick={() => toggleDropdown('play')}
                        style={{
                          background: 'rgba(255, 255, 255, 0.95)',
                          border: '1px solid rgba(0, 0, 0, 0.2)',
                          color: '#333333',
                          padding: '0.5rem',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '40px',
                          height: '40px',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = 'rgba(26, 115, 232, 0.1)';
                          e.target.style.borderColor = 'rgba(26, 115, 232, 0.3)';
                          e.target.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
                          e.target.style.borderColor = 'rgba(0, 0, 0, 0.2)';
                          e.target.style.transform = 'scale(1)';
                        }}
                      >
                        <FiRepeat size={24} />
                      </button>
                      <div className="tooltip" style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        marginTop: '8px',
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        whiteSpace: 'nowrap',
                        opacity: 0,
                        visibility: 'hidden',
                        transition: 'opacity 0.2s, visibility 0.2s',
                        pointerEvents: 'none',
                        zIndex: 9999
                      }}>
                        播放模式
                      </div>
                      {dropdownOpen.play && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          right: 0,
                          marginTop: '0.5rem',
                          backgroundColor: 'white',
                          border: '1px solid rgba(0, 0, 0, 0.15)',
                          borderRadius: '12px',
                          padding: '1rem',
                          zIndex: 1000,
                          minWidth: '250px',
                          color: 'rgba(0, 0, 0, 0.8)',
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
                        }}>
                          <div style={{ marginBottom: '1rem' }}>
                            <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '0.5rem', color: 'rgba(0, 0, 0, 0.7)' }}>视听</div>
                            <div style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', backgroundColor: playMode === 'single' ? 'rgba(0, 0, 0, 0.05)' : 'transparent', padding: '0.25rem', borderRadius: '4px', transition: 'all 0.2s ease' }} onClick={() => selectOption('play', 'single')} onMouseEnter={(e) => {
                              if (playMode !== 'single') {
                                e.currentTarget.style.backgroundColor = 'rgba(26, 115, 232, 0.05)';
                              }
                            }} onMouseLeave={(e) => {
                              if (playMode !== 'single') {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span>▶</span>
                                <span>单集播放</span>
                              </div>
                              {playMode === 'single' && <span style={{ color: 'green' }}>✓</span>}
                            </div>
                            <div style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', backgroundColor: playMode === 'singleLoop' ? 'rgba(0, 0, 0, 0.05)' : 'transparent', padding: '0.25rem', borderRadius: '4px', transition: 'all 0.2s ease' }} onClick={() => selectOption('play', 'singleLoop')} onMouseEnter={(e) => {
                              if (playMode !== 'singleLoop') {
                                e.currentTarget.style.backgroundColor = 'rgba(26, 115, 232, 0.05)';
                              }
                            }} onMouseLeave={(e) => {
                              if (playMode !== 'singleLoop') {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span>🔁</span>
                                <span>单集循环</span>
                              </div>
                              {playMode === 'singleLoop' && <span style={{ color: 'green' }}>✓</span>}
                            </div>
                          </div>
                          <div style={{ borderTop: '1px solid rgba(0, 0, 0, 0.1)', paddingTop: '1rem' }}>
                            <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '0.5rem', color: 'rgba(0, 0, 0, 0.7)' }}>句子</div>
                            <div style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', backgroundColor: playMode === 'continuous' ? 'rgba(0, 0, 0, 0.05)' : 'transparent', padding: '0.25rem', borderRadius: '4px', transition: 'all 0.2s ease' }} onClick={() => selectOption('play', 'continuous')} onMouseEnter={(e) => {
                              if (playMode !== 'continuous') {
                                e.currentTarget.style.backgroundColor = 'rgba(26, 115, 232, 0.05)';
                              }
                            }} onMouseLeave={(e) => {
                              if (playMode !== 'continuous') {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span>▶</span>
                                <span>连续播放</span>
                              </div>
                              {playMode === 'continuous' && <span style={{ color: 'green' }}>✓</span>}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', backgroundColor: playMode === 'sentenceLoop' ? 'rgba(0, 0, 0, 0.05)' : 'transparent', padding: '0.25rem', borderRadius: '4px', transition: 'all 0.2s ease' }} onClick={() => selectOption('play', 'sentenceLoop')} onMouseEnter={(e) => {
                              if (playMode !== 'sentenceLoop') {
                                e.currentTarget.style.backgroundColor = 'rgba(26, 115, 232, 0.05)';
                              }
                            }} onMouseLeave={(e) => {
                              if (playMode !== 'sentenceLoop') {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span>🔁</span>
                                <span>单句循环</span>
                              </div>
                              {playMode === 'sentenceLoop' && <span style={{ color: 'green' }}>✓</span>}
                            </div>
                            
                            {playMode === 'sentenceLoop' && (
                              <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
                                <div style={{ marginBottom: '0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span>当前进度：</span>
                                  <span style={{ fontWeight: 'bold', color: '#1a73e8' }}>
                                    {currentPlayCountDisplay} / {loopCount}
                                  </span>
                                </div>
                                <div style={{ marginBottom: '0.75rem' }}>
                                  <div style={{ marginBottom: '0.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span>单句循环次数</span>
                                    <span>{loopCount}次</span>
                                  </div>
                                  <input 
                                    type="range" 
                                    min="1" 
                                    max="10" 
                                    value={loopCount} 
                                    onChange={(e) => {
                                      const newCount = parseInt(e.target.value) || 1;
                                      setLoopCount(newCount);
                                      // 如果设置的次数小于当前播放次数，重置播放计数
                                      if (newCount <= currentSentencePlayCountRef.current) {
                                        currentSentencePlayCountRef.current = 0;
                                        isCurrentSentenceEndedRef.current = false;
                                        setCurrentPlayCountDisplay(0);
                                      }
                                    }} 
                                    style={{
                                      width: '100%',
                                      height: '4px',
                                      backgroundColor: '#e0e0e0',
                                      borderRadius: '2px',
                                      outline: 'none',
                                      WebkitAppearance: 'none',
                                      margin: '0.5rem 0'
                                    }}
                                  />
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span>自动播放下句</span>
                                  <label style={{ position: 'relative', display: 'inline-block', width: '50px', height: '24px' }}>
                                    <input 
                                      type="checkbox" 
                                      checked={autoNext} 
                                      onChange={() => setAutoNext(!autoNext)} 
                                      style={{ opacity: 0, width: 0, height: 0 }}
                                    />
                                    <span style={{
                                      position: 'absolute',
                                      cursor: 'pointer',
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      bottom: 0,
                                      backgroundColor: autoNext ? '#00ffc3' : '#ccc',
                                      transition: '.4s',
                                      borderRadius: '24px'
                                    }}>
                                    </span>
                                    <span style={{
                                      position: 'absolute',
                                      content: '',
                                      height: '16px',
                                      width: '16px',
                                      left: '4px',
                                      bottom: '4px',
                                      backgroundColor: 'white',
                                      transition: '.4s',
                                      borderRadius: '50%',
                                      transform: autoNext ? 'translateX(26px)' : 'translateX(0)'
                                    }}>
                                    </span>
                                  </label>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* 英语练习控制 */}
                    <div className="dropdown-container" style={{ position: 'relative' }} onMouseEnter={(e) => {
                      const tooltip = e.currentTarget.querySelector('.tooltip');
                      if (tooltip) {
                        tooltip.style.opacity = '1';
                        tooltip.style.visibility = 'visible';
                      }
                    }} onMouseLeave={(e) => {
                      const tooltip = e.currentTarget.querySelector('.tooltip');
                      if (tooltip) {
                        tooltip.style.opacity = '0';
                        tooltip.style.visibility = 'hidden';
                      }
                    }}>
                      <button
                        onClick={() => toggleDropdown('exercise')}
                        style={{
                          background: 'rgba(255, 255, 255, 0.95)',
                          border: '1px solid rgba(0, 0, 0, 0.2)',
                          color: '#333333',
                          padding: '0',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '40px',
                          height: '40px',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = 'rgba(26, 115, 232, 0.1)';
                          e.target.style.borderColor = 'rgba(26, 115, 232, 0.3)';
                          e.target.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
                          e.target.style.borderColor = 'rgba(0, 0, 0, 0.2)';
                          e.target.style.transform = 'scale(1)';
                        }}
                      >
                        <FiMenu size={24} />
                      </button>
                      <div className="tooltip" style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        marginTop: '8px',
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        whiteSpace: 'nowrap',
                        opacity: 0,
                        visibility: 'hidden',
                        transition: 'opacity 0.2s, visibility 0.2s',
                        pointerEvents: 'none',
                        zIndex: 9999
                      }}>
                        英语练习
                      </div>
                      {dropdownOpen.exercise && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          right: 0,
                          marginTop: '0.5rem',
                          backgroundColor: 'white',
                          border: '1px solid rgba(0, 0, 0, 0.15)',
                          borderRadius: '12px',
                          padding: '1rem',
                          zIndex: 1000,
                          minWidth: '250px',
                          color: 'rgba(0, 0, 0, 0.8)',
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
                        }}>
                          <div 
                            style={{ 
                              marginBottom: '0.5rem',
                              padding: '0.75rem',
                              borderRadius: '6px',
                              backgroundColor: exerciseMode === 'repeat' ? 'rgba(26, 115, 232, 0.1)' : 'transparent',
                              cursor: 'pointer',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              transition: 'all 0.2s ease',
                              touchAction: 'manipulation',
                              WebkitTapHighlightColor: 'transparent'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              selectOption('exercise', 'repeat');
                            }}
                            onTouchStart={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                            }}
                            onTouchEnd={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              selectOption('exercise', 'repeat');
                            }}
                            onMouseEnter={(e) => {
                              if (exerciseMode !== 'repeat') {
                                e.currentTarget.style.backgroundColor = 'rgba(26, 115, 232, 0.05)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (exerciseMode !== 'repeat') {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                              <span style={{ fontSize: '1.5rem' }}>🎤</span>
                              <span style={{ fontSize: '1rem' }}>跟读练习</span>
                            </div>
                            {exerciseMode === 'repeat' && <span style={{ color: 'green', fontSize: '1.2rem' }}>✓</span>}
                          </div>
                          <div 
                            style={{ 
                              marginBottom: '0.5rem',
                              padding: '0.75rem',
                              borderRadius: '6px',
                              backgroundColor: exerciseMode === 'fill' ? 'rgba(26, 115, 232, 0.1)' : 'transparent',
                              cursor: 'pointer',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              transition: 'all 0.2s ease',
                              touchAction: 'manipulation',
                              WebkitTapHighlightColor: 'transparent'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              selectOption('exercise', 'fill');
                            }}
                            onTouchStart={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                            }}
                            onTouchEnd={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              selectOption('exercise', 'fill');
                            }}
                            onMouseEnter={(e) => {
                              if (exerciseMode !== 'fill') {
                                e.currentTarget.style.backgroundColor = 'rgba(26, 115, 232, 0.05)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (exerciseMode !== 'fill') {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                              <span style={{ fontSize: '1.5rem' }}>🔤</span>
                              <span style={{ fontSize: '1rem' }}>填空练习</span>
                            </div>
                            {exerciseMode === 'fill' && <span style={{ color: 'green', fontSize: '1.2rem' }}>✓</span>}
                          </div>
                          <div 
                            style={{ 
                              padding: '0.75rem',
                              borderRadius: '6px',
                              backgroundColor: exerciseMode === 'none' ? 'rgba(26, 115, 232, 0.1)' : 'transparent',
                              cursor: 'pointer',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              transition: 'all 0.2s ease',
                              touchAction: 'manipulation',
                              WebkitTapHighlightColor: 'transparent'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              selectOption('exercise', 'none');
                              setIntensiveMode('none');
                            }}
                            onTouchStart={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                            }}
                            onTouchEnd={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              selectOption('exercise', 'none');
                              setIntensiveMode('none');
                            }}
                            onMouseEnter={(e) => {
                              if (exerciseMode !== 'none') {
                                e.currentTarget.style.backgroundColor = 'rgba(26, 115, 232, 0.05)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (exerciseMode !== 'none') {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                              <span style={{ fontSize: '1.5rem' }}>❌</span>
                              <span style={{ fontSize: '1rem' }}>关闭练习</span>
                            </div>
                            {exerciseMode === 'none' && <span style={{ color: 'green', fontSize: '1.2rem' }}>✓</span>}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* 内容精读控制 */}
                    <div style={{ position: 'relative' }} onMouseEnter={(e) => {
                      const tooltip = e.currentTarget.querySelector('.tooltip');
                      if (tooltip) {
                        tooltip.style.opacity = '1';
                        tooltip.style.visibility = 'visible';
                      }
                    }} onMouseLeave={(e) => {
                      const tooltip = e.currentTarget.querySelector('.tooltip');
                      if (tooltip) {
                        tooltip.style.opacity = '0';
                        tooltip.style.visibility = 'hidden';
                      }
                    }}>
                      <button
                        onClick={() => {
                          const newMode = intensiveMode === 'none' ? 'words' : 'none';
                          const videoElement = isFloatingPlayerMode ? floatingVideoRef.current : videoRef.current;
                          if (newMode !== 'none') {
                            if (videoElement && !videoElement.paused) {
                              videoElement.pause();
                            }
                            setIsPlaying(false);
                            setPlayingSubtitleIndex(null);
                          }
                          setIntensiveMode(newMode);
                        }}
                        style={{
                          background: intensiveMode !== 'none' ? 'linear-gradient(45deg, #1a73e8, #0056b3)' : 'rgba(255, 255, 255, 0.95)',
                          border: intensiveMode !== 'none' ? 'none' : '1px solid rgba(0, 0, 0, 0.2)',
                          color: intensiveMode !== 'none' ? 'white' : '#333333',
                          padding: '0.5rem',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '40px',
                          height: '40px',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (intensiveMode === 'none') {
                            e.target.style.backgroundColor = 'rgba(26, 115, 232, 0.1)';
                            e.target.style.borderColor = 'rgba(26, 115, 232, 0.3)';
                            e.target.style.transform = 'scale(1.05)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (intensiveMode === 'none') {
                            e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
                            e.target.style.borderColor = 'rgba(0, 0, 0, 0.2)';
                            e.target.style.transform = 'scale(1)';
                          }
                        }}
                      >
                        <FiBookOpen size={24} />
                      </button>
                      <div className="tooltip" style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        marginTop: '8px',
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        whiteSpace: 'nowrap',
                        opacity: 0,
                        visibility: 'hidden',
                        transition: 'opacity 0.2s, visibility 0.2s',
                        pointerEvents: 'none',
                        zIndex: 9999
                      }}>
                        内容精读
                      </div>
                    </div>
                    
                    {/* 字幕导出 - 已隐藏 */}
                    {/* <div style={{ position: 'relative' }} onMouseEnter={(e) => {
                      const tooltip = e.currentTarget.querySelector('.tooltip');
                      if (tooltip) {
                        tooltip.style.opacity = '1';
                        tooltip.style.visibility = 'visible';
                      }
                    }} onMouseLeave={(e) => {
                      const tooltip = e.currentTarget.querySelector('.tooltip');
                      if (tooltip) {
                        tooltip.style.opacity = '0';
                        tooltip.style.visibility = 'hidden';
                      }
                    }}>
                      <button
                        onClick={exportSubtitles}
                        style={{
                          background: 'rgba(255, 255, 255, 0.95)',
                          border: '1px solid rgba(0, 0, 0, 0.2)',
                          color: 'white',
                          padding: '0.5rem',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '40px',
                          height: '40px',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = 'rgba(26, 115, 232, 0.1)';
                          e.target.style.borderColor = 'rgba(26, 115, 232, 0.3)';
                          e.target.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
                          e.target.style.borderColor = 'rgba(0, 0, 0, 0.2)';
                          e.target.style.transform = 'scale(1)';
                        }}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                          <polyline points="7 10 12 15 17 10"/>
                          <line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                      </button>
                      <div className="tooltip" style={{
                        position: 'absolute',
                        bottom: '100%',
                        right: 0,
                        marginBottom: '8px',
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        whiteSpace: 'nowrap',
                        opacity: 0,
                        visibility: 'hidden',
                        transition: 'opacity 0.2s, visibility 0.2s',
                        pointerEvents: 'none'
                      }}>
                        字幕导出
                      </div>
                    </div> */}
                  </div>
                </div>
                

                
                <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
                  <div ref={subtitleContainerRef} style={{
                    height: '100%',
                    overflowY: scrollLocked ? 'hidden' : 'auto',
                    paddingRight: '0',
                    backgroundColor: '#ffffff',
                    boxSizing: 'border-box'
                  }} id="subtitle-container">
                  {subtitles.map((subtitle, index) => {
                    // 找出句子中有标注的单词、短语和自然科普词汇作为填空
                    const allAnnotations = [
                      ...phrasesData.map(p => ({ text: p.phrase, type: 'phrase' })),
                      ...wordsData.map(w => ({ text: w.word, type: 'word' })),
                      ...expressionsData.map(e => ({ text: e.expression, type: 'expression' }))
                    ];
                    
                    // 按长度降序排序，优先匹配较长的短语
                    allAnnotations.sort((a, b) => b.text.length - a.text.length);
                    
                    // 找出句子中所有需要填空的词汇
                    const blankItems = [];
                    let remainingText = subtitle.english;
                    
                    // 创建正则表达式匹配所有标注词汇
                    if (allAnnotations.length > 0) {
                      const pattern = allAnnotations
                        .map(item => {
                          const escapedText = item.text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                          return `\\b${escapedText}\\b`;
                        })
                        .join('|');
                      const annotationRegex = new RegExp(pattern, 'gi');
                      
                      let match;
                      while ((match = annotationRegex.exec(subtitle.english)) !== null) {
                        blankItems.push({
                          start: match.index,
                          end: match.index + match[0].length,
                          text: match[0]
                        });
                      }
                    }
                    
                    // 构建填空内容
                    const fillBlanks = [];
                    let lastIndex = 0;
                    
                    // 按位置排序
                    blankItems.sort((a, b) => a.start - b.start);
                    
                    blankItems.forEach((item, itemIndex) => {
                      // 添加前面的文本
                      if (item.start > lastIndex) {
                        fillBlanks.push({
                          type: 'text',
                          content: subtitle.english.slice(lastIndex, item.start)
                        });
                      }
                      // 添加填空
                      fillBlanks.push({
                        type: 'blank',
                        id: `${index}-${itemIndex}`,
                        correctAnswer: item.text
                      });
                      lastIndex = item.end;
                    });
                    
                    // 添加最后的文本
                    if (lastIndex < subtitle.english.length) {
                      fillBlanks.push({
                        type: 'text',
                        content: subtitle.english.slice(lastIndex)
                      });
                    }
                    
                    // 如果没有标注词汇，显示原文
                    if (fillBlanks.length === 0) {
                      fillBlanks.push({
                        type: 'text',
                        content: subtitle.english
                      });
                    }

                    return (
                      <div key={index} 
                        style={{
                          marginBottom: '0.5rem',
                          padding: '0.75rem',
                          borderRadius: '8px',
                          backgroundColor: currentSubtitle === index ? 'rgba(255, 230, 109, 0.3)' : 'rgba(240,240,240,0.5)',
                          border: 'none',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer',
                          touchAction: 'manipulation',
                          WebkitTapHighlightColor: 'transparent'
                        }}
                        className="subtitle-item"
                        onMouseEnter={(e) => {
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                          e.currentTarget.style.backgroundColor = currentSubtitle === index ? 'rgba(255, 230, 109, 0.4)' : 'rgba(240,240,240,0.7)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.boxShadow = 'none';
                          e.currentTarget.style.backgroundColor = currentSubtitle === index ? 'rgba(255, 230, 109, 0.3)' : 'rgba(240,240,240,0.5)';
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          if (exerciseMode === 'repeat' || exerciseMode === 'fill') {
                            const videoElement = isFloatingPlayerMode ? floatingVideoRef.current : videoRef.current;
                            if (playingSubtitleIndex === index && videoElement && !videoElement.paused) {
                              videoElement.pause();
                              setIsPlaying(false);
                              setPlayingSubtitleIndex(null);
                            } else {
                              playOriginalAudio(index);
                              setPlayingSubtitleIndex(index);
                            }
                          } else {
                            handleSubtitleClick(index);
                          }
                        }}
                        onTouchStart={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                        }}
                        onTouchEnd={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          if (exerciseMode === 'repeat' || exerciseMode === 'fill') {
                            const videoElement = isFloatingPlayerMode ? floatingVideoRef.current : videoRef.current;
                            if (playingSubtitleIndex === index && videoElement && !videoElement.paused) {
                              videoElement.pause();
                              setIsPlaying(false);
                              setPlayingSubtitleIndex(null);
                            } else {
                              playOriginalAudio(index);
                              setPlayingSubtitleIndex(index);
                            }
                          } else {
                            handleSubtitleClick(index);
                          }
                        }}>

                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'flex-start',
                          marginBottom: '0.5rem'
                        }}>
                          <div style={{ fontSize: '1rem', color: 'rgba(0,0,0,0.5)' }}>
                            {Math.floor(subtitle.time / 60).toString().padStart(2, '0')}:{(Math.floor(subtitle.time % 60)).toString().padStart(2, '0')}
                          </div>
                        </div>
                        
                        {/* 跟读练习模式 */}
                        {exerciseMode === 'repeat' && (
                          <>
                            <div style={{ 
                              color: '#000000', 
                              marginBottom: '0.5rem',
                              lineHeight: '1.4',
                              fontSize: getSubtitleFontSizes().english,
                              fontWeight: '500'
                            }}>
                              {renderAnnotatedText(subtitle.english)}
                            </div>
                            <div style={{ 
                              backgroundColor: 'transparent',
                              borderRadius: '8px',
                              padding: '1rem',
                              marginTop: '0.5rem',
                              display: 'flex',
                              gap: '1rem',
                              alignItems: 'center'
                            }}>
                              {/* 播放原音按钮 */}
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  const videoElement = isFloatingPlayerMode ? floatingVideoRef.current : videoRef.current;
                                  if (playingSubtitleIndex === index && videoElement && !videoElement.paused) {
                                    videoElement.pause();
                                    setIsPlaying(false);
                                    setPlayingSubtitleIndex(null);
                                  } else {
                                    playOriginalAudio(index);
                                    setPlayingSubtitleIndex(index);
                                  }
                                }}
                                onTouchStart={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                }}
                                onTouchEnd={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  const videoElement = isFloatingPlayerMode ? floatingVideoRef.current : videoRef.current;
                                  if (playingSubtitleIndex === index && videoElement && !videoElement.paused) {
                                    videoElement.pause();
                                    setIsPlaying(false);
                                    setPlayingSubtitleIndex(null);
                                  } else {
                                    playOriginalAudio(index);
                                    setPlayingSubtitleIndex(index);
                                  }
                                }}
                                style={{
                                  background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                                  border: 'none',
                                  color: 'white',
                                  padding: '0.75rem',
                                  borderRadius: '50%',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '40px',
                                  height: '40px',
                                  transition: 'all 0.2s ease',
                                  touchAction: 'manipulation',
                                  WebkitTapHighlightColor: 'transparent',
                                  outline: 'none'
                                }}
                                title={playingSubtitleIndex === index ? "暂停" : "播放原音"}
                              >
                                {playingSubtitleIndex === index ? (
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                                    <rect x="6" y="4" width="4" height="16"/>
                                    <rect x="14" y="4" width="4" height="16"/>
                                  </svg>
                                ) : (
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                                    <path d="M8 5v14l11-7z"/>
                                  </svg>
                                )}
                              </button>
                              {/* 开始/停止录音按钮 */}
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  toggleRecording(index);
                                }}
                                onTouchStart={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                }}
                                onTouchEnd={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  toggleRecording(index);
                                }}
                                style={{
                                  background: recordingIndex === index ? 'rgba(255, 0, 0, 0.8)' : 'rgba(255, 100, 100, 0.8)',
                                  border: recordingIndex === index ? '2px solid #ff0000' : 'none',
                                  color: 'white',
                                  padding: '0.75rem',
                                  borderRadius: '50%',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '40px',
                                  height: '40px',
                                  animation: recordingIndex === index ? 'pulse 1s infinite' : 'none',
                                  transition: 'all 0.2s ease',
                                  touchAction: 'manipulation',
                                  WebkitTapHighlightColor: 'transparent',
                                  zIndex: 10,
                                  position: 'relative'
                                }}
                                title={recordingIndex === index ? "停止录音" : "开始录音"}
                              >
                                {recordingIndex === index ? '⏹' : '🎤'}
                              </button>
                              {/* 播放录音按钮 */}
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  playRecordedAudio(index);
                                }}
                                onTouchStart={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                }}
                                onTouchEnd={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  playRecordedAudio(index);
                                }}
                                style={{
                                  background: hasRecording[index] ? 'rgba(76, 175, 80, 0.8)' : 'rgba(100, 100, 100, 0.5)',
                                  border: 'none',
                                  color: 'white',
                                  padding: '0.75rem',
                                  borderRadius: '50%',
                                  cursor: hasRecording[index] ? 'pointer' : 'not-allowed',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '40px',
                                  height: '40px',
                                  transition: 'all 0.2s ease',
                                  touchAction: 'manipulation',
                                  WebkitTapHighlightColor: 'transparent',
                                  outline: 'none',
                                  zIndex: 10,
                                  position: 'relative'
                                }}
                                title={playingRecordingIndex === index ? "暂停录音" : (hasRecording[index] ? "播放录音" : "请先录音")}
                              >
                                {playingRecordingIndex === index ? (
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                                    <rect x="6" y="4" width="4" height="16"/>
                                    <rect x="14" y="4" width="4" height="16"/>
                                  </svg>
                                ) : (
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                                    <path d="M8 5v14l11-7z"/>
                                  </svg>
                                )}
                              </button>
                            </div>
                          </>
                        )}
                        
                        {/* 填空练习模式 */}
                        {exerciseMode === 'fill' && (
                          <>
                            <div style={{ 
                              color: '#000000', 
                              marginBottom: '0.5rem',
                              lineHeight: '1.4',
                              fontSize: getSubtitleFontSizes().english,
                              fontWeight: '500'
                            }}>
                              {fillBlanks.map((item, itemIndex) => {
                                if (item.type === 'text') {
                                  return (
                                    <span key={itemIndex}>{item.content} </span>
                                  );
                                } else {
                                  const isRevealed = revealedWords[item.id] || false;
                                  return (
                                    <span
                                      key={item.id}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setRevealedWords(prev => ({
                                          ...prev,
                                          [item.id]: !prev[item.id]
                                        }));
                                      }}
                                      style={{
                                        padding: '0.1rem 0.8rem',
                                        backgroundColor: isRevealed ? 'transparent' : 'rgba(26, 115, 232, 0.2)',
                                        cursor: 'pointer',
                                        borderRadius: '6px',
                                        fontWeight: '500',
                                        color: isRevealed ? '#1a73e8' : 'transparent',
                                        userSelect: 'none',
                                        display: 'inline-block',
                                        minWidth: '50px',
                                        transition: 'all 0.2s ease'
                                      }}
                                      title={isRevealed ? "点击隐藏" : "点击查看"}
                                    >
                                      {item.correctAnswer}
                                    </span>
                                  );
                                }
                              })}
                            </div>
                            <div style={{ 
                              color: 'rgba(0,0,0,0.6)', 
                              lineHeight: '1.4',
                              fontWeight: 'normal',
                              fontSize: getSubtitleFontSizes().chinese
                            }}>
                              {subtitle.chinese}
                            </div>
                          </>
                        )}
                        
                        {/* 正常模式（仅在没有练习模式时显示） */}
                        {exerciseMode === 'none' && subtitleMode === 'bilingual' && (
                          <>
                            <div style={{ 
                              color: currentSubtitle === index ? '#000000' : '#1a1a1a', 
                              marginBottom: '0.5rem', 
                              lineHeight: '1.4',
                              fontWeight: currentSubtitle === index ? 'bold' : '500',
                              fontSize: getSubtitleFontSizes().english
                            }}>
                              {renderAnnotatedText(subtitle.english)}
                            </div>
                            <div style={{ 
                              color: currentSubtitle === index ? '#000000' : 'rgba(0,0,0,0.6)', 
                              lineHeight: '1.4',
                              fontWeight: currentSubtitle === index ? 'bold' : 'normal',
                              fontSize: getSubtitleFontSizes().chinese
                            }}>
                              {subtitle.chinese}
                            </div>
                          </>
                        )}
                        {exerciseMode === 'none' && subtitleMode === 'english' && (
                          <div style={{ 
                            color: currentSubtitle === index ? '#000000' : '#1a1a1a', 
                            lineHeight: '1.4',
                            fontWeight: currentSubtitle === index ? 'bold' : '500',
                            fontSize: getSubtitleFontSizes().english
                          }}>
                            {renderAnnotatedText(subtitle.english)}
                          </div>
                        )}
                        {exerciseMode === 'none' && subtitleMode === 'chinese' && (
                          <div style={{ 
                            color: currentSubtitle === index ? '#000000' : '#333333', 
                            lineHeight: '1.4',
                            fontWeight: currentSubtitle === index ? 'bold' : 'normal',
                            fontSize: getSubtitleFontSizes().chinese
                          }}>
                            {subtitle.chinese}
                          </div>
                        )}

                      </div>
                    );
                  })}
                  </div>
                  {/* 跟随模式和滚动锁定控制 - 固定在底部居中 */}
                  <div style={{
                    position: 'absolute',
                    bottom: '10px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    gap: '0.5rem',
                    zIndex: 20
                  }}>
                    <button
                      onClick={() => setFollowMode(!followMode)}
                      style={{
                        background: followMode ? 'linear-gradient(45deg, #1a73e8, #0056b3)' : 'rgba(240, 240, 240, 0.95)',
                        border: followMode ? 'none' : '1px solid #e0e0e0',
                        color: followMode ? 'white' : '#333333',
                        padding: '0.4rem 0.8rem',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {followMode ? '跟随模式: 开启' : '跟随模式: 关闭'}
                    </button>
                    <button
                      onClick={() => setScrollLocked(!scrollLocked)}
                      style={{
                        background: scrollLocked ? 'linear-gradient(45deg, #1a73e8, #0056b3)' : 'rgba(240, 240, 240, 0.95)',
                        border: scrollLocked ? 'none' : '1px solid #e0e0e0',
                        color: scrollLocked ? 'white' : '#333333',
                        padding: '0.4rem 0.8rem',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {scrollLocked ? '滚动锁定: 开启' : '滚动锁定: 关闭'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            {/* 内容精读区域（第三个区域） - 桌面端 */}
            {intensiveMode !== 'none' && (
              <div style={{ width: '380px', flexShrink: 0 }}>
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  border: '1px solid #e0e0e0',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                  height: '100%'
                }}>
                  {/* 标签导航 */}
                  <div style={{
                    display: 'flex',
                    gap: '0.3rem',
                    marginBottom: '1rem',
                    borderBottom: '1px solid #e0e0e0',
                    paddingBottom: '0.8rem',
                    flexWrap: 'nowrap',
                    overflowX: 'auto'
                  }}>
                    <button
                      onClick={() => setCurrentWordTab('words')}
                      style={{
                        padding: '0.5rem 1rem',
                        borderRadius: '6px',
                        border: currentWordTab === 'words' ? '2px solid #1a73e8' : '1px solid #e0e0e0',
                        backgroundColor: currentWordTab === 'words' ? 'rgba(26, 115, 232, 0.1)' : 'white',
                        color: currentWordTab === 'words' ? '#1a73e8' : '#333',
                        cursor: 'pointer',
                        fontWeight: currentWordTab === 'words' ? '600' : '400',
                        fontSize: '0.9rem',
                        flex: 1,
                        whiteSpace: 'nowrap',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (currentWordTab !== 'words') {
                          e.target.style.backgroundColor = 'rgba(26, 115, 232, 0.05)';
                          e.target.style.borderColor = 'rgba(26, 115, 232, 0.3)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (currentWordTab !== 'words') {
                          e.target.style.backgroundColor = 'white';
                          e.target.style.borderColor = '#e0e0e0';
                        }
                      }}
                    >
                      单词 {wordsData.length}  
                    </button>
                    <button
                      onClick={() => setCurrentWordTab('phrases')}
                      style={{
                        padding: '0.5rem 1rem',
                        borderRadius: '6px',
                        border: currentWordTab === 'phrases' ? '2px solid #1a73e8' : '1px solid #e0e0e0',
                        backgroundColor: currentWordTab === 'phrases' ? 'rgba(26, 115, 232, 0.1)' : 'white',
                        color: currentWordTab === 'phrases' ? '#1a73e8' : '#333',
                        cursor: 'pointer',
                        fontWeight: currentWordTab === 'phrases' ? '600' : '400',
                        fontSize: '0.9rem',
                        flex: 1,
                        whiteSpace: 'nowrap',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (currentWordTab !== 'phrases') {
                          e.target.style.backgroundColor = 'rgba(26, 115, 232, 0.05)';
                          e.target.style.borderColor = 'rgba(26, 115, 232, 0.3)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (currentWordTab !== 'phrases') {
                          e.target.style.backgroundColor = 'white';
                          e.target.style.borderColor = '#e0e0e0';
                        }
                      }}
                    >
                      短语 {phrasesData.length}
                    </button>
                    <button
                      onClick={() => setCurrentWordTab('expressions')}
                      style={{
                        padding: '0.5rem 1rem',
                        borderRadius: '6px',
                        border: currentWordTab === 'expressions' ? '2px solid #1a73e8' : '1px solid #e0e0e0',
                        backgroundColor: currentWordTab === 'expressions' ? 'rgba(26, 115, 232, 0.1)' : 'white',
                        color: currentWordTab === 'expressions' ? '#1a73e8' : '#333',
                        cursor: 'pointer',
                        fontWeight: currentWordTab === 'expressions' ? '600' : '400',
                        fontSize: '0.9rem',
                        flex: 1,
                        whiteSpace: 'nowrap',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (currentWordTab !== 'expressions') {
                          e.target.style.backgroundColor = 'rgba(26, 115, 232, 0.05)';
                          e.target.style.borderColor = 'rgba(26, 115, 232, 0.3)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (currentWordTab !== 'expressions') {
                          e.target.style.backgroundColor = 'white';
                          e.target.style.borderColor = '#e0e0e0';
                        }
                      }}
                    >
                      自然科普词汇 {expressionsData.length}
                    </button>
                  </div>
                  
                  {/* 单词过滤按钮 */}
                  {currentWordTab === 'words' && (
                    <div style={{ marginBottom: '1.5rem' }}>
                      <div style={{
                        display: 'flex',
                        gap: '0.3rem',
                        flexWrap: 'nowrap',
                        marginBottom: '0.8rem',
                        overflowX: 'auto'
                      }}>
                        {['all', 'unmarked', 'known', 'unknown'].map((filter) => (
                          <button
                            key={filter}
                            onClick={() => setWordFilter(filter)}
                            style={{
                                padding: '0.4rem 0.8rem',
                                borderRadius: '20px',
                                border: wordFilter === filter ? '1px solid #1a73e8' : '1px solid #e0e0e0',
                                backgroundColor: wordFilter === filter ? 'rgba(26, 115, 232, 0.1)' : 'white',
                                color: wordFilter === filter ? '#1a73e8' : '#333',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                whiteSpace: 'nowrap',
                                transition: 'all 0.3s ease'
                              }}
                              onMouseEnter={(e) => {
                                if (wordFilter !== filter) {
                                  e.target.style.backgroundColor = 'rgba(26, 115, 232, 0.05)';
                                  e.target.style.borderColor = 'rgba(26, 115, 232, 0.3)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (wordFilter !== filter) {
                                  e.target.style.backgroundColor = 'white';
                                  e.target.style.borderColor = '#e0e0e0';
                                }
                              }}
                          >
                            {filter === 'all' && '全部 ' + wordCounts.all}
                            {filter === 'unmarked' && '未标记 ' + wordCounts.unmarked
                            }
                            {filter === 'known' && '认识 ' + wordCounts.known
                            }
                            {filter === 'unknown' && '不认识 ' + wordCounts.unknown}
                          </button>
                        ))}
                      </div>
                      
                      {/* 显示/隐藏中文和标注按钮 */}
                      <div style={{
                        display: 'flex',
                        gap: '0.5rem',
                        flexWrap: 'wrap'
                      }}>
                        <button
                          onClick={() => setShowChinese(!showChinese)}
                          style={{
                            padding: '0.4rem 0.8rem',
                            borderRadius: '6px',
                            border: '1px solid #e0e0e0',
                            backgroundColor: showChinese ? 'rgba(26, 115, 232, 0.1)' : 'white',
                            color: showChinese ? '#1a73e8' : '#333',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.3rem',
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (!showChinese) {
                              e.target.style.backgroundColor = 'rgba(26, 115, 232, 0.05)';
                              e.target.style.borderColor = 'rgba(26, 115, 232, 0.3)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!showChinese) {
                              e.target.style.backgroundColor = 'white';
                              e.target.style.borderColor = '#e0e0e0';
                            }
                          }}
                        >
                          {showChinese ? '✓' : '○'} 隐藏中文
                        </button>
                        <button
                          onClick={() => setShowAnnotations(!showAnnotations)}
                          style={{
                            padding: '0.4rem 0.8rem',
                            borderRadius: '6px',
                            border: '1px solid #e0e0e0',
                            backgroundColor: showAnnotations ? 'rgba(26, 115, 232, 0.1)' : 'white',
                            color: showAnnotations ? '#1a73e8' : '#333',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.3rem',
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (!showAnnotations) {
                              e.target.style.backgroundColor = 'rgba(26, 115, 232, 0.05)';
                              e.target.style.borderColor = 'rgba(26, 115, 232, 0.3)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!showAnnotations) {
                              e.target.style.backgroundColor = 'white';
                              e.target.style.borderColor = '#e0e0e0';
                            }
                          }}
                        >
                          {showAnnotations ? '✓' : '○'} 隐藏标注
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* 短语过滤按钮 */}
                  {currentWordTab === 'phrases' && (
                    <div style={{ marginBottom: '1.5rem' }}>
                      <div style={{
                        display: 'flex',
                        gap: '0.3rem',
                        flexWrap: 'nowrap',
                        marginBottom: '0.8rem',
                        overflowX: 'auto'
                      }}>
                        {['all', 'unmarked', 'known', 'unknown'].map((filter) => (
                          <button
                            key={filter}
                            onClick={() => setPhraseFilter(filter)}
                            style={{
                                padding: '0.4rem 0.8rem',
                                borderRadius: '20px',
                                border: phraseFilter === filter ? '1px solid #1a73e8' : '1px solid #e0e0e0',
                                backgroundColor: phraseFilter === filter ? 'rgba(26, 115, 232, 0.1)' : 'white',
                                color: phraseFilter === filter ? '#1a73e8' : '#333',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                whiteSpace: 'nowrap',
                                transition: 'all 0.3s ease'
                              }}
                              onMouseEnter={(e) => {
                                if (phraseFilter !== filter) {
                                  e.target.style.backgroundColor = 'rgba(26, 115, 232, 0.05)';
                                  e.target.style.borderColor = 'rgba(26, 115, 232, 0.3)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (phraseFilter !== filter) {
                                  e.target.style.backgroundColor = 'white';
                                  e.target.style.borderColor = '#e0e0e0';
                                }
                              }}
                          >
                            {filter === 'all' ? '全部 ' + phraseCounts.all
                            : filter === 'unmarked' ? '未标记 ' + phraseCounts.unmarked
                            : filter === 'known' ? '认识 ' + phraseCounts.known
                            : '不认识 ' + phraseCounts.unknown
                            }
                          </button>
                        ))}
                      </div>
                      
                      {/* 显示/隐藏中文和标注按钮 */}
                      <div style={{
                        display: 'flex',
                        gap: '0.5rem',
                        flexWrap: 'wrap'
                      }}>
                        <button
                          onClick={() => setShowChinese(!showChinese)}
                          style={{
                            padding: '0.4rem 0.8rem',
                            borderRadius: '6px',
                            border: '1px solid #e0e0e0',
                            backgroundColor: showChinese ? 'rgba(26, 115, 232, 0.1)' : 'white',
                            color: showChinese ? '#1a73e8' : '#333',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.3rem',
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (!showChinese) {
                              e.target.style.backgroundColor = 'rgba(26, 115, 232, 0.05)';
                              e.target.style.borderColor = 'rgba(26, 115, 232, 0.3)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!showChinese) {
                              e.target.style.backgroundColor = 'white';
                              e.target.style.borderColor = '#e0e0e0';
                            }
                          }}
                        >
                          {showChinese ? '✓' : '○'} 隐藏中文
                        </button>
                        <button
                          onClick={() => setShowAnnotations(!showAnnotations)}
                          style={{
                            padding: '0.4rem 0.8rem',
                            borderRadius: '6px',
                            border: '1px solid #e0e0e0',
                            backgroundColor: showAnnotations ? 'rgba(26, 115, 232, 0.1)' : 'white',
                            color: showAnnotations ? '#1a73e8' : '#333',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.3rem',
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (!showAnnotations) {
                              e.target.style.backgroundColor = 'rgba(26, 115, 232, 0.05)';
                              e.target.style.borderColor = 'rgba(26, 115, 232, 0.3)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!showAnnotations) {
                              e.target.style.backgroundColor = 'white';
                              e.target.style.borderColor = '#e0e0e0';
                            }
                          }}
                        >
                          {showAnnotations ? '✓' : '○'} 隐藏标注
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* 自然科普词汇过滤按钮 */}
                  {currentWordTab === 'expressions' && (
                    <div style={{ marginBottom: '1.5rem' }}>
                      <div style={{
                        display: 'flex',
                        gap: '0.3rem',
                        flexWrap: 'nowrap',
                        marginBottom: '0.8rem',
                        overflowX: 'auto'
                      }}>
                        {['all', 'unmarked', 'known', 'unknown'].map((filter) => (
                          <button
                            key={filter}
                            onClick={() => setExpressionFilter(filter)}
                            style={{
                                padding: '0.4rem 0.8rem',
                                borderRadius: '20px',
                                border: expressionFilter === filter ? '1px solid #1a73e8' : '1px solid #e0e0e0',
                                backgroundColor: expressionFilter === filter ? 'rgba(26, 115, 232, 0.1)' : 'white',
                                color: expressionFilter === filter ? '#1a73e8' : '#333',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                whiteSpace: 'nowrap',
                                transition: 'all 0.3s ease'
                              }}
                              onMouseEnter={(e) => {
                                if (expressionFilter !== filter) {
                                  e.target.style.backgroundColor = 'rgba(26, 115, 232, 0.05)';
                                  e.target.style.borderColor = 'rgba(26, 115, 232, 0.3)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (expressionFilter !== filter) {
                                  e.target.style.backgroundColor = 'white';
                                  e.target.style.borderColor = '#e0e0e0';
                                }
                              }}
                          >
                            {filter === 'all' ? '全部 ' + expressionCounts.all
                            : filter === 'unmarked' ? '未标记 ' + expressionCounts.unmarked
                            : filter === 'known' ? '认识 ' + expressionCounts.known
                            : '不认识 ' + expressionCounts.unknown
                            }
                          </button>
                        ))}
                      </div>
                      
                      {/* 显示/隐藏中文和标注按钮 */}
                      <div style={{
                        display: 'flex',
                        gap: '0.5rem',
                        flexWrap: 'wrap'
                      }}>
                        <button
                          onClick={() => setShowChinese(!showChinese)}
                          style={{
                            padding: '0.4rem 0.8rem',
                            borderRadius: '6px',
                            border: '1px solid #e0e0e0',
                            backgroundColor: showChinese ? 'rgba(26, 115, 232, 0.1)' : 'white',
                            color: showChinese ? '#1a73e8' : '#333',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.3rem',
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (!showChinese) {
                              e.target.style.backgroundColor = 'rgba(26, 115, 232, 0.05)';
                              e.target.style.borderColor = 'rgba(26, 115, 232, 0.3)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!showChinese) {
                              e.target.style.backgroundColor = 'white';
                              e.target.style.borderColor = '#e0e0e0';
                            }
                          }}
                        >
                          {showChinese ? '✓' : '○'} 隐藏中文
                        </button>
                        <button
                          onClick={() => setShowAnnotations(!showAnnotations)}
                          style={{
                            padding: '0.4rem 0.8rem',
                            borderRadius: '6px',
                            border: '1px solid #e0e0e0',
                            backgroundColor: showAnnotations ? 'rgba(26, 115, 232, 0.1)' : 'white',
                            color: showAnnotations ? '#1a73e8' : '#333',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.3rem',
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (!showAnnotations) {
                              e.target.style.backgroundColor = 'rgba(26, 115, 232, 0.05)';
                              e.target.style.borderColor = 'rgba(26, 115, 232, 0.3)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!showAnnotations) {
                              e.target.style.backgroundColor = 'white';
                              e.target.style.borderColor = '#e0e0e0';
                            }
                          }}
                        >
                          {showAnnotations ? '✓' : '○'} 隐藏标注
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* 单词列表 */}
                  <div style={{
                    maxHeight: '500px',
                    overflowY: 'auto'
                  }}>
                    {/* 显示单词 */}
                    {currentWordTab === 'words' && (
                      <>
                        {console.log('渲染单词列表，filteredWords:', filteredWords)}
                        {filteredWords.length > 0 && console.log('第一个要渲染的单词:', filteredWords[0])}
                      </>
                    )}
                    {currentWordTab === 'words' && filteredWords.map((word) => (
                      <div key={word.id} style={{
                        marginBottom: '1.5rem',
                        padding: '1.25rem',
                        borderRadius: '24px',
                        backgroundColor: word.status === 'known' ? 'rgba(40, 167, 69, 0.1)' : word.status === 'unknown' ? 'rgba(220, 53, 69, 0.35)' : 'rgba(240, 240, 240, 0.3)',
                        border: '2px solid rgba(0, 102, 255, 0.15)',
                        boxShadow: '0 6px 16px rgba(0, 102, 255, 0.08)',
                        position: 'relative',
                        overflow: 'visible',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-4px)';
                        e.currentTarget.style.boxShadow = '0 10px 24px rgba(0, 102, 255, 0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 102, 255, 0.08)';
                      }}>
                        {/* 标签容器 - 绝对定位在右上角 */}
                        <div style={{
                          position: 'absolute',
                          top: '1rem',
                          right: '1rem',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.3rem',
                          alignItems: 'center',
                          flexWrap: 'nowrap',
                          zIndex: 1
                        }}>
                                {word.cambridgeLevel && (
                                  <div style={{
                                    padding: '0.3rem 0.6rem',
                                    borderRadius: '4px',
                                    border: '1px solid #1a73e8',
                                    backgroundColor: 'rgba(26, 115, 232, 0.1)',
                                    color: '#1a73e8',
                                    fontSize: '0.7rem',
                                    fontWeight: 'bold',
                                    width: '70px',
                                    textAlign: 'center',
                                    whiteSpace: 'nowrap',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'default',
                                    transition: 'all 0.2s ease'
                                  }} onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(26, 115, 232, 0.2)';
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                  }} onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(26, 115, 232, 0.1)';
                                    e.currentTarget.style.transform = 'scale(1)';
                                  }}>
                                    {word.cambridgeLevel}
                                  </div>
                                )}
                                <button
                                  onClick={() => updateWordStatus(word.id, 'known')}
                                  style={{
                                    padding: '0.3rem 0.6rem',
                                    borderRadius: '4px',
                                    border: word.status === 'known' ? '1px solid #28a745' : '1px solid #28a745',
                                    backgroundColor: word.status === 'known' ? '#28a745' : 'white',
                                    color: word.status === 'known' ? 'white' : '#28a745',
                                    cursor: 'pointer',
                                    fontSize: '0.7rem',
                                    fontWeight: '500',
                                    width: '70px',
                                    textAlign: 'center',
                                    whiteSpace: 'nowrap',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.2s ease'
                                  }}
                                  title="认识"
                                  onMouseEnter={(e) => {
                                    if (word.status !== 'known') {
                                      e.currentTarget.style.backgroundColor = 'rgba(40, 167, 69, 0.1)';
                                    }
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = word.status === 'known' ? '#28a745' : 'white';
                                    e.currentTarget.style.transform = 'scale(1)';
                                  }}
                                >
                                  ✓ 认识
                                </button>
                                <button
                                  onClick={() => saveUnknownWord(word)}
                                  style={{
                                    padding: '0.3rem 0.6rem',
                                    borderRadius: '4px',
                                    border: word.status === 'unknown' ? '1px solid #dc3545' : '1px solid #dc3545',
                                    backgroundColor: word.status === 'unknown' ? '#dc3545' : 'white',
                                    color: word.status === 'unknown' ? 'white' : '#dc3545',
                                    cursor: 'pointer',
                                    fontSize: '0.7rem',
                                    fontWeight: '500',
                                    width: '70px',
                                    textAlign: 'center',
                                    whiteSpace: 'nowrap',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.2s ease'
                                  }}
                                  title="不认识，添加到单词卡"
                                  onMouseEnter={(e) => {
                                    if (word.status !== 'unknown') {
                                      e.currentTarget.style.backgroundColor = 'rgba(220, 53, 69, 0.1)';
                                    }
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = word.status === 'unknown' ? '#dc3545' : 'white';
                                    e.currentTarget.style.transform = 'scale(1)';
                                  }}
                                >
                                  ✗ 不认识
                                </button>
                        </div>
                        
                        {/* 单词和查词按钮 */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'flex-start',
                          alignItems: 'flex-start',
                          marginBottom: '0.7rem',
                          flexWrap: 'nowrap',
                          width: '100%',
                          paddingRight: '90px'
                        }}>
                          <div style={{
                            width: '100%',
                            minWidth: 0
                          }}>
                            {/* 单词 */}
                            <div
                              style={{
                                fontSize: '1.3rem',
                                fontWeight: 'bold',
                                color: '#1a73e8',
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                                marginBottom: '0.3rem'
                              }}
                            >
                              {word.word}
                            </div>
                            
                            {/* 音标、发音 */}
                            <div style={{
                              marginBottom: '0.7rem',
                              display: 'flex',
                              alignItems: 'center',
                              flexWrap: 'nowrap'
                            }}>
                              {word.phonetic && (
                                <div style={{
                                  fontSize: '1rem',
                                  color: 'rgba(0, 0, 0, 0.7)',
                                  fontFamily: 'Arial, sans-serif',
                                  whiteSpace: 'nowrap',
                                  marginRight: '1rem'
                                }}>
                                  {word.phonetic}
                                </div>
                              )}
                              <button
                                onClick={() => {
                                  // 创建一个辅助函数来获取高质量声音
                                  const getHighQualityVoice = () => {
                                    // 如果 voices 还没加载，尝试获取
                                    if (!voicesLoaded) {
                                      window.speechSynthesis.getVoices();
                                    }
                                    
                                    const voices = speechSynthesis.getVoices();
                                    if (!voices || voices.length === 0) return null;
                                    
                                    // 优先尝试使用 Google 女声
                                    let voice = voices.find(v => 
                                      v.lang === 'en-US' && 
                                      (v.name.includes('Google') && v.name.toLowerCase().includes('female'))
                                    );
                                    if (voice) return voice;
                                    
                                    // 其次尝试使用 Google 男声或女声
                                    voice = voices.find(v => 
                                      v.lang === 'en-US' && 
                                      v.name.includes('Google')
                                    );
                                    if (voice) return voice;
                                    
                                    // 再次尝试 Samantha 或 Alex
                                    voice = voices.find(v => 
                                      v.lang === 'en-US' && 
                                      (v.name.includes('Samantha') || v.name.includes('Alex'))
                                    );
                                    if (voice) return voice;
                                    
                                    // 最后尝试任何 en-US 的声音
                                    return voices.find(v => v.lang === 'en-US');
                                  };
                                  
                                  // 使用浏览器语音合成的备用函数
                                  const speakWithBrowser = () => {
                                    try {
                                      const utterance = new SpeechSynthesisUtterance(word.word);
                                      utterance.lang = 'en-US';
                                      utterance.rate = 0.85;
                                      const highQualityVoice = getHighQualityVoice();
                                      if (highQualityVoice) {
                                        utterance.voice = highQualityVoice;
                                      }
                                      speechSynthesis.cancel(); // 清除之前的发音队列
                                      speechSynthesis.speak(utterance);
                                    } catch (e) {
                                      console.error('浏览器发音失败:', e);
                                    }
                                  };
                                  
                                  try {
                                    // 先尝试使用有道语音合成 API
                                    const audio = new Audio(`https://dict.youdao.com/dictvoice?audio=${encodeURIComponent(word.word)}&type=2`);
                                    
                                    let played = false;
                                    
                                    // 设置较长的超时时间
                                    const timeout = setTimeout(() => {
                                      if (!played) {
                                        console.log('有道TTS响应超时，使用备用发音');
                                        speakWithBrowser();
                                      }
                                    }, 2000); // 2秒超时，比之前快一点
                                    
                                    audio.addEventListener('canplaythrough', () => {
                                      if (!played) {
                                        played = true;
                                        clearTimeout(timeout);
                                        audio.play().catch(() => {
                                          speakWithBrowser();
                                        });
                                      }
                                    });
                                    
                                    audio.onerror = () => {
                                      if (!played) {
                                        played = true;
                                        clearTimeout(timeout);
                                        console.log('有道TTS加载失败，使用浏览器发音');
                                        speakWithBrowser();
                                      }
                                    };
                                    
                                    // 尝试预加载音频
                                    audio.load();
                                    
                                    // 同时尝试播放，如果音频已经缓存了就立即播放
                                    audio.play().then(() => {
                                      if (!played) {
                                        played = true;
                                        clearTimeout(timeout);
                                      }
                                    }).catch(error => {
                                      // 如果播放失败，让 canplaythrough 事件来处理
                                      console.log('初始播放失败，等待音频加载');
                                    });
                                  } catch (e) {
                                    console.error('发音失败:', e);
                                    speakWithBrowser();
                                  }
                                }}
                                style={{
                                  width: '30px',
                                  height: '30px',
                                  borderRadius: '50%',
                                  backgroundColor: 'rgba(200, 200, 200, 0.2)',
                                  border: 'none',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.2s',
                                  whiteSpace: 'nowrap'
                                }}
                                title="听发音"
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = 'rgba(150, 150, 150, 0.3)';
                                  e.target.style.transform = 'scale(1.1)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'rgba(200, 200, 200, 0.2)';
                                  e.target.style.transform = 'scale(1)';
                                }}
                              >
                                🔊
                              </button>
                            </div>
                            
                            {/* 词性 */}
                            <div style={{
                              marginBottom: '0.5rem',
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              {word.partOfSpeech && (
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center'
                                }}>
                                  <div style={{
                                    background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                                    color: 'white',
                                    padding: '0.2rem 0.5rem',
                                    borderRadius: '4px',
                                    fontSize: '0.7rem',
                                    fontWeight: 'bold',
                                    marginRight: '0.5rem',
                                    whiteSpace: 'nowrap',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease'
                                  }} onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(45deg, #1557b0, #003a80)';
                                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
                                  }} onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(45deg, #1a73e8, #0056b3)';
                                    e.currentTarget.style.boxShadow = 'none';
                                  }}>
                                    词性
                                  </div>
                                  <div style={{
                                    fontSize: '1.1rem',
                                    color: 'rgba(0, 0, 0, 0.9)',
                                    fontWeight: '500',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {abbreviatePartOfSpeech(word.partOfSpeech)}
                                  </div>
                                </div>
                              )}
                            </div>
                            
                            {/* 中文翻译 */}
                            <div style={{
                              marginBottom: '0.7rem',
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              {word.chineseTranslation && (
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center'
                                }}>
                                  <div style={{
                                    background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                                    color: 'white',
                                    padding: '0.2rem 0.5rem',
                                    borderRadius: '4px',
                                    fontSize: '0.7rem',
                                    fontWeight: 'bold',
                                    marginRight: '0.5rem',
                                    whiteSpace: 'nowrap',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease'
                                  }} onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(45deg, #1557b0, #003a80)';
                                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
                                  }} onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(45deg, #1a73e8, #0056b3)';
                                    e.currentTarget.style.boxShadow = 'none';
                                  }}>
                                    中译
                                  </div>
                                  {showChinese && (
                                    <div style={{
                                      fontSize: '1rem',
                                      color: 'rgba(0, 0, 0, 0.9)',
                                      fontWeight: '500',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis'
                                    }} title={word.chineseTranslation}>
                                      {word.chineseTranslation}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                        
                        {/* 英文定义 - 仅显示后端提交的 */}
                        {word.definition && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            marginTop: '0.2rem', // 调整与词性之间的距离
                            marginBottom: '0.7rem'
                          }}>
                            <div style={{
                              background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                              color: 'white',
                              padding: '0.2rem 0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.7rem',
                              fontWeight: 'bold',
                              marginRight: '0.5rem',
                              whiteSpace: 'nowrap',
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease'
                            }} onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'linear-gradient(45deg, #1557b0, #003a80)';
                              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
                            }} onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'linear-gradient(45deg, #1a73e8, #0056b3)';
                              e.currentTarget.style.boxShadow = 'none';
                            }}>
                              英译
                            </div>
                            <div style={{
                              fontSize: '0.9rem',
                              color: 'rgba(0, 0, 0, 0.6)',
                              flex: 1
                            }}>
                              {word.definition}
                            </div>
                          </div>
                        )}
                        
                            {/* 联想词 - 仅显示后端提交的 */}
                            {word.synonyms && (
                              <div style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                marginBottom: '0.2rem',
                                marginTop: '0.2rem' // 调整与英译之间的距离
                              }}>
                                <div style={{
                                  background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                                  color: 'white',
                                  padding: '0.2rem 0.5rem',
                                  borderRadius: '4px',
                                  fontSize: '0.7rem',
                                  fontWeight: 'bold',
                                  marginRight: '0.5rem',
                                  whiteSpace: 'nowrap',
                                  height: '20px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  cursor: 'pointer',
                                  transition: 'all 0.3s ease'
                                }} onMouseEnter={(e) => {
                                  e.currentTarget.style.background = 'linear-gradient(45deg, #1557b0, #003a80)';
                                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
                                }} onMouseLeave={(e) => {
                                  e.currentTarget.style.background = 'linear-gradient(45deg, #1a73e8, #0056b3)';
                                  e.currentTarget.style.boxShadow = 'none';
                                }}>
                                  联想词
                                </div>
                                <div style={{
                                  fontSize: '0.85rem',
                                  color: 'rgba(0, 0, 0, 0.6)',
                                  flex: 1
                                }}>
                                  {word.synonyms}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* 白色底框 - 例句 */}
                        <div 
                          style={{
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            padding: '0.8rem',
                            marginTop: '0.7rem',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            position: 'relative',
                            width: '100%',
                            boxSizing: 'border-box',
                            touchAction: 'manipulation',
                            WebkitTapHighlightColor: 'transparent'
                          }}
                          onClick={() => {
                            let targetIndex = -1;
                            
                            // 优先使用保存的字幕索引
                            if (word.subtitleIndex !== undefined && subtitles[word.subtitleIndex]) {
                              targetIndex = word.subtitleIndex;
                              console.log('使用保存的字幕索引播放:', targetIndex);
                            } else if (word.sentence) {
                              // 如果没有保存的索引，再通过文本匹配
                              const sentenceLower = word.sentence.toLowerCase();
                              for (let i = 0; i < subtitles.length; i++) {
                                const sub = subtitles[i];
                                const subLower = sub.english.toLowerCase();
                                if (subLower.includes(sentenceLower) || sentenceLower.includes(subLower)) {
                                  targetIndex = i;
                                  console.log('通过文本匹配找到字幕索引:', targetIndex);
                                  break;
                                }
                              }
                            }
                            
                            if (targetIndex !== -1) {
                              setCurrentSubtitle(targetIndex);
                              playOriginalAudio(targetIndex);
                            }
                          }}
                          onTouchStart={(e) => {
                            e.preventDefault();
                            let targetIndex = -1;
                            
                            // 优先使用保存的字幕索引
                            if (word.subtitleIndex !== undefined && subtitles[word.subtitleIndex]) {
                              targetIndex = word.subtitleIndex;
                              console.log('使用保存的字幕索引播放:', targetIndex);
                            } else if (word.sentence) {
                              // 如果没有保存的索引，再通过文本匹配
                              const sentenceLower = word.sentence.toLowerCase();
                              for (let i = 0; i < subtitles.length; i++) {
                                const sub = subtitles[i];
                                const subLower = sub.english.toLowerCase();
                                if (subLower.includes(sentenceLower) || sentenceLower.includes(subLower)) {
                                  targetIndex = i;
                                  console.log('通过文本匹配找到字幕索引:', targetIndex);
                                  break;
                                }
                              }
                            }
                            
                            if (targetIndex !== -1) {
                              setCurrentSubtitle(targetIndex);
                              playOriginalAudio(targetIndex);
                            }
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(26, 115, 232, 0.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'white';
                          }}
                        >
                          <div style={{
                            background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                            color: 'white',
                            padding: '0.2rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.7rem',
                            fontWeight: 'bold',
                            marginBottom: '0.5rem',
                            display: 'inline-block'
                          }}>
                            原文例句
                          </div>
                          <div style={{
                            fontSize: '0.95rem',
                            color: '#333',
                            marginBottom: '0.3rem',
                            lineHeight: '1.5'
                          }}>
                            {word.sentence || '无例句'}
                          </div>
                          {(word.sentenceChinese || word.sentenceChinese === '') && (
                            <div style={{
                              fontSize: '0.85rem',
                              color: 'rgba(0, 0, 0, 0.6)',
                              lineHeight: '1.4'
                            }}>
                              {showChinese ? (word.sentenceChinese || '无中文翻译') : ''}
                            </div>
                          )}
                        </div>

                      </div>
                    ))}
                    
                    {/* 显示短语 */}
                    {currentWordTab === 'phrases' && filteredPhrases.map((phrase) => (
                      <div key={phrase.id} style={{
                        marginBottom: '1.5rem',
                        padding: '1.25rem',
                        borderRadius: '24px',
                        backgroundColor: phrase.status === 'known' ? 'rgba(40, 167, 69, 0.1)' : phrase.status === 'unknown' ? 'rgba(220, 53, 69, 0.35)' : 'rgba(240, 240, 240, 0.3)',
                        border: '2px solid rgba(0, 102, 255, 0.15)',
                        boxShadow: '0 6px 16px rgba(0, 102, 255, 0.08)',
                        position: 'relative',
                        overflow: 'visible',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-4px)';
                        e.currentTarget.style.boxShadow = '0 10px 24px rgba(0, 102, 255, 0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 102, 255, 0.08)';
                      }}>
                        {/* 标签容器 - 绝对定位在右上角 */}
                        <div style={{
                          position: 'absolute',
                          top: '1rem',
                          right: '1rem',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.3rem',
                          alignItems: 'center',
                          flexWrap: 'nowrap',
                          zIndex: 1
                        }}>
                                {phrase.cambridgeLevel && (
                                  <div style={{
                                    padding: '0.3rem 0.6rem',
                                    borderRadius: '4px',
                                    border: '1px solid #1a73e8',
                                    backgroundColor: 'rgba(26, 115, 232, 0.1)',
                                    color: '#1a73e8',
                                    fontSize: '0.7rem',
                                    fontWeight: 'bold',
                                    width: '70px',
                                    textAlign: 'center',
                                    whiteSpace: 'nowrap',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'default',
                                    transition: 'all 0.2s ease'
                                  }} onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(26, 115, 232, 0.2)';
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                  }} onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(26, 115, 232, 0.1)';
                                    e.currentTarget.style.transform = 'scale(1)';
                                  }}>
                                    {phrase.cambridgeLevel}
                                  </div>
                                )}
                                <button
                                  onClick={() => updatePhraseStatus(phrase.id, 'known')}
                                  style={{
                                    padding: '0.3rem 0.6rem',
                                    borderRadius: '4px',
                                    border: phrase.status === 'known' ? '1px solid #28a745' : '1px solid #28a745',
                                    backgroundColor: phrase.status === 'known' ? '#28a745' : 'white',
                                    color: phrase.status === 'known' ? 'white' : '#28a745',
                                    cursor: 'pointer',
                                    fontSize: '0.7rem',
                                    fontWeight: '500',
                                    width: '70px',
                                    textAlign: 'center',
                                    whiteSpace: 'nowrap',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.2s ease'
                                  }}
                                  title="认识"
                                  onMouseEnter={(e) => {
                                    if (phrase.status !== 'known') {
                                      e.currentTarget.style.backgroundColor = 'rgba(40, 167, 69, 0.1)';
                                    }
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = phrase.status === 'known' ? '#28a745' : 'white';
                                    e.currentTarget.style.transform = 'scale(1)';
                                  }}
                                >
                                  ✓ 认识
                                </button>
                                <button
                                  onClick={() => saveUnknownPhrase(phrase)}
                                  style={{
                                    padding: '0.3rem 0.6rem',
                                    borderRadius: '4px',
                                    border: phrase.status === 'unknown' ? '1px solid #dc3545' : '1px solid #dc3545',
                                    backgroundColor: phrase.status === 'unknown' ? '#dc3545' : 'white',
                                    color: phrase.status === 'unknown' ? 'white' : '#dc3545',
                                    cursor: 'pointer',
                                    fontSize: '0.7rem',
                                    fontWeight: '500',
                                    width: '70px',
                                    textAlign: 'center',
                                    whiteSpace: 'nowrap',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.2s ease'
                                  }}
                                  title="不认识，添加到短语卡"
                                  onMouseEnter={(e) => {
                                    if (phrase.status !== 'unknown') {
                                      e.currentTarget.style.backgroundColor = 'rgba(220, 53, 69, 0.1)';
                                    }
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = phrase.status === 'unknown' ? '#dc3545' : 'white';
                                    e.currentTarget.style.transform = 'scale(1)';
                                  }}
                                >
                                  ✗ 不认识
                                </button>
                        </div>
                        
                        {/* 短语和查词按钮 */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'flex-start',
                          alignItems: 'flex-start',
                          marginBottom: '0.7rem',
                          flexWrap: 'nowrap',
                          width: '100%',
                          paddingRight: '90px'
                        }}>
                          <div style={{
                            width: '100%',
                            minWidth: 0
                          }}>
                            {/* 短语 */}
                            <div
                              style={{
                                fontSize: '1.3rem',
                                fontWeight: 'bold',
                                color: '#1a73e8',
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                                marginBottom: '0.3rem'
                              }}
                            >
                              {phrase.phrase}
                            </div>
                            
                            {/* 音标、发音 */}
                            <div style={{
                              marginBottom: '0.7rem',
                              display: 'flex',
                              alignItems: 'center',
                              flexWrap: 'nowrap'
                            }}>
                              {phrase.phonetic && (
                                <div style={{
                                  fontSize: '1rem',
                                  color: 'rgba(0, 0, 0, 0.7)',
                                  fontFamily: 'Arial, sans-serif',
                                  whiteSpace: 'nowrap',
                                  marginRight: '1rem'
                                }}>
                                  {phrase.phonetic}
                                </div>
                              )}
                              <button
                                onClick={() => {
                                  // 创建一个辅助函数来获取高质量声音
                                  const getHighQualityVoice = () => {
                                    // 如果 voices 还没加载，尝试获取
                                    if (!voicesLoaded) {
                                      window.speechSynthesis.getVoices();
                                    }
                                    
                                    const voices = speechSynthesis.getVoices();
                                    if (!voices || voices.length === 0) return null;
                                    
                                    // 优先尝试使用 Google 女声
                                    let voice = voices.find(v => 
                                      v.lang === 'en-US' && 
                                      (v.name.includes('Google') && v.name.toLowerCase().includes('female'))
                                    );
                                    if (voice) return voice;
                                    
                                    // 其次尝试使用 Google 男声或女声
                                    voice = voices.find(v => 
                                      v.lang === 'en-US' && 
                                      v.name.includes('Google')
                                    );
                                    if (voice) return voice;
                                    
                                    // 再次尝试 Samantha 或 Alex
                                    voice = voices.find(v => 
                                      v.lang === 'en-US' && 
                                      (v.name.includes('Samantha') || v.name.includes('Alex'))
                                    );
                                    if (voice) return voice;
                                    
                                    // 最后尝试任何 en-US 的声音
                                    return voices.find(v => v.lang === 'en-US');
                                  };
                                  
                                  // 使用浏览器语音合成的备用函数
                                  const speakWithBrowser = () => {
                                    try {
                                      const utterance = new SpeechSynthesisUtterance(phrase.phrase);
                                      utterance.lang = 'en-US';
                                      utterance.rate = 0.85;
                                      const highQualityVoice = getHighQualityVoice();
                                      if (highQualityVoice) {
                                        utterance.voice = highQualityVoice;
                                      }
                                      speechSynthesis.cancel(); // 清除之前的发音队列
                                      speechSynthesis.speak(utterance);
                                    } catch (e) {
                                      console.error('浏览器发音失败:', e);
                                    }
                                  };
                                  
                                  try {
                                    // 先尝试使用有道语音合成 API
                                    const audio = new Audio(`https://dict.youdao.com/dictvoice?audio=${encodeURIComponent(phrase.phrase)}&type=2`);
                                    
                                    let played = false;
                                    
                                    // 设置较长的超时时间
                                    const timeout = setTimeout(() => {
                                      if (!played) {
                                        console.log('有道TTS响应超时，使用备用发音');
                                        speakWithBrowser();
                                      }
                                    }, 2000); // 2秒超时，比之前快一点
                                    
                                    audio.addEventListener('canplaythrough', () => {
                                      if (!played) {
                                        played = true;
                                        clearTimeout(timeout);
                                        audio.play().catch(() => {
                                          speakWithBrowser();
                                        });
                                      }
                                    });
                                    
                                    audio.onerror = () => {
                                      if (!played) {
                                        played = true;
                                        clearTimeout(timeout);
                                        console.log('有道TTS加载失败，使用浏览器发音');
                                        speakWithBrowser();
                                      }
                                    };
                                    
                                    // 尝试预加载音频
                                    audio.load();
                                    
                                    // 同时尝试播放，如果音频已经缓存了就立即播放
                                    audio.play().then(() => {
                                      if (!played) {
                                        played = true;
                                        clearTimeout(timeout);
                                      }
                                    }).catch(error => {
                                      // 如果播放失败，让 canplaythrough 事件来处理
                                      console.log('初始播放失败，等待音频加载');
                                    });
                                  } catch (e) {
                                    console.error('发音失败:', e);
                                    speakWithBrowser();
                                  }
                                }}
                                style={{
                                  width: '30px',
                                  height: '30px',
                                  borderRadius: '50%',
                                  backgroundColor: 'rgba(200, 200, 200, 0.2)',
                                  border: 'none',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.2s',
                                  whiteSpace: 'nowrap'
                                }}
                                title="听发音"
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = 'rgba(150, 150, 150, 0.3)';
                                  e.target.style.transform = 'scale(1.1)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'rgba(200, 200, 200, 0.2)';
                                  e.target.style.transform = 'scale(1)';
                                }}
                              >
                                🔊
                              </button>
                            </div>
                            
                            {/* 词性 */}
                            <div style={{
                              marginBottom: '0.5rem',
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              {phrase.partOfSpeech && (
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center'
                                }}>
                                  <div style={{
                                    background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                                    color: 'white',
                                    padding: '0.2rem 0.5rem',
                                    borderRadius: '4px',
                                    fontSize: '0.7rem',
                                    fontWeight: 'bold',
                                    marginRight: '0.5rem',
                                    whiteSpace: 'nowrap',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease'
                                  }} onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(45deg, #1557b0, #003a80)';
                                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
                                  }} onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(45deg, #1a73e8, #0056b3)';
                                    e.currentTarget.style.boxShadow = 'none';
                                  }}>
                                    词性
                                  </div>
                                  <div style={{
                                    fontSize: '1.1rem',
                                    color: 'rgba(0, 0, 0, 0.9)',
                                    fontWeight: '500',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {abbreviatePartOfSpeech(phrase.partOfSpeech)}
                                  </div>
                                </div>
                              )}
                            </div>
                            
                            {/* 中文翻译 */}
                            <div style={{
                              marginBottom: '0.7rem',
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              {phrase.chineseTranslation && (
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center'
                                }}>
                                  <div style={{
                                    background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                                    color: 'white',
                                    padding: '0.2rem 0.5rem',
                                    borderRadius: '4px',
                                    fontSize: '0.7rem',
                                    fontWeight: 'bold',
                                    marginRight: '0.5rem',
                                    whiteSpace: 'nowrap',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease'
                                  }} onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(45deg, #1557b0, #003a80)';
                                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
                                  }} onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(45deg, #1a73e8, #0056b3)';
                                    e.currentTarget.style.boxShadow = 'none';
                                  }}>
                                    中译
                                  </div>
                                  {showChinese && (
                                    <div style={{
                                      fontSize: '1rem',
                                      color: 'rgba(0, 0, 0, 0.9)',
                                      fontWeight: '500',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis'
                                    }} title={phrase.chineseTranslation}>
                                      {phrase.chineseTranslation}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            
                          </div>
                        </div>
                        
                        {/* 英文定义 - 仅显示后端提交的 */}
                        {phrase.definition && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            marginTop: '0.2rem', // 调整与词性之间的距离
                            marginBottom: '0.7rem'
                          }}>
                            <div style={{
                              background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                              color: 'white',
                              padding: '0.2rem 0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.7rem',
                              fontWeight: 'bold',
                              marginRight: '0.5rem',
                              whiteSpace: 'nowrap',
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease'
                            }} onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'linear-gradient(45deg, #1557b0, #003a80)';
                              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
                            }} onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'linear-gradient(45deg, #1a73e8, #0056b3)';
                              e.currentTarget.style.boxShadow = 'none';
                            }}>
                              英译
                            </div>
                            <div style={{
                              fontSize: '0.9rem',
                              color: 'rgba(0, 0, 0, 0.6)',
                              flex: 1
                            }}>
                              {phrase.definition}
                            </div>
                          </div>
                        )}
                        
                            {/* 联想词 - 仅显示后端提交的 */}
                            {phrase.synonyms && (
                              <div style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                marginBottom: '0.2rem',
                                marginTop: '0.2rem' // 调整与英译之间的距离
                              }}>
                                <div style={{
                                  background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                                  color: 'white',
                                  padding: '0.2rem 0.5rem',
                                  borderRadius: '4px',
                                  fontSize: '0.7rem',
                                  fontWeight: 'bold',
                                  marginRight: '0.5rem',
                                  whiteSpace: 'nowrap',
                                  height: '20px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  cursor: 'pointer',
                                  transition: 'all 0.3s ease'
                                }} onMouseEnter={(e) => {
                                  e.currentTarget.style.background = 'linear-gradient(45deg, #1557b0, #003a80)';
                                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
                                }} onMouseLeave={(e) => {
                                  e.currentTarget.style.background = 'linear-gradient(45deg, #1a73e8, #0056b3)';
                                  e.currentTarget.style.boxShadow = 'none';
                                }}>
                                  联想词
                                </div>
                                <div style={{
                                  fontSize: '0.85rem',
                                  color: 'rgba(0, 0, 0, 0.6)',
                                  flex: 1
                                }}>
                                  {phrase.synonyms}
                                </div>
                              </div>
                            )}
                        
                        {/* 白色底框 - 例句 */}
                        <div 
                          style={{
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            padding: '0.8rem',
                            marginTop: '0.7rem',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            position: 'relative',
                            width: '100%',
                            boxSizing: 'border-box',
                            touchAction: 'manipulation',
                            WebkitTapHighlightColor: 'transparent'
                          }}
                          onClick={() => {
                            let targetIndex = -1;
                            
                            // 优先使用保存的字幕索引
                            if (phrase.subtitleIndex !== undefined && subtitles[phrase.subtitleIndex]) {
                              targetIndex = phrase.subtitleIndex;
                              console.log('使用保存的字幕索引播放（短语）:', targetIndex);
                            } else if (phrase.sentence) {
                              // 如果没有保存的索引，再通过文本匹配
                              const sentenceLower = phrase.sentence.toLowerCase();
                              for (let i = 0; i < subtitles.length; i++) {
                                const sub = subtitles[i];
                                const subLower = sub.english.toLowerCase();
                                if (subLower.includes(sentenceLower) || sentenceLower.includes(subLower)) {
                                  targetIndex = i;
                                  console.log('通过文本匹配找到字幕索引（短语）:', targetIndex);
                                  break;
                                }
                              }
                            }
                            
                            if (targetIndex !== -1) {
                              setCurrentSubtitle(targetIndex);
                              playOriginalAudio(targetIndex);
                            }
                          }}
                          onTouchStart={(e) => {
                            e.preventDefault();
                            let targetIndex = -1;
                            
                            // 优先使用保存的字幕索引
                            if (phrase.subtitleIndex !== undefined && subtitles[phrase.subtitleIndex]) {
                              targetIndex = phrase.subtitleIndex;
                              console.log('使用保存的字幕索引播放（短语）:', targetIndex);
                            } else if (phrase.sentence) {
                              // 如果没有保存的索引，再通过文本匹配
                              const sentenceLower = phrase.sentence.toLowerCase();
                              for (let i = 0; i < subtitles.length; i++) {
                                const sub = subtitles[i];
                                const subLower = sub.english.toLowerCase();
                                if (subLower.includes(sentenceLower) || sentenceLower.includes(subLower)) {
                                  targetIndex = i;
                                  console.log('通过文本匹配找到字幕索引（短语）:', targetIndex);
                                  break;
                                }
                              }
                            }
                            
                            if (targetIndex !== -1) {
                              setCurrentSubtitle(targetIndex);
                              playOriginalAudio(targetIndex);
                            }
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(26, 115, 232, 0.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'white';
                          }}
                        >
                          <div style={{
                            background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                            color: 'white',
                            padding: '0.2rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.7rem',
                            fontWeight: 'bold',
                            marginBottom: '0.5rem',
                            display: 'inline-block'
                          }}>
                            原文例句
                          </div>
                          <div style={{
                            fontSize: '0.95rem',
                            color: '#333',
                            marginBottom: '0.3rem',
                            lineHeight: '1.5'
                          }}>
                            {phrase.sentence || '无例句'}
                          </div>
                          {(phrase.sentenceChinese || phrase.sentenceChinese === '') && (
                            <div style={{
                              fontSize: '0.85rem',
                              color: 'rgba(0, 0, 0, 0.6)',
                              lineHeight: '1.4'
                            }}>
                              {showChinese ? (phrase.sentenceChinese || '无中文翻译') : ''}
                            </div>
                          )}
                        </div>


                      </div>
                    ))}
                    
                    {/* 显示自然科普词汇 */}
                    {currentWordTab === 'expressions' && filteredExpressions.map((expression) => (
                      <div key={expression.id} style={{
                        marginBottom: '1.5rem',
                        padding: '1.25rem',
                        borderRadius: '24px',
                        backgroundColor: expression.status === 'known' ? 'rgba(40, 167, 69, 0.1)' : expression.status === 'unknown' ? 'rgba(220, 53, 69, 0.35)' : 'rgba(240, 240, 240, 0.3)',
                        border: '2px solid rgba(0, 102, 255, 0.15)',
                        boxShadow: '0 6px 16px rgba(0, 102, 255, 0.08)',
                        position: 'relative',
                        overflow: 'visible',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-4px)';
                        e.currentTarget.style.boxShadow = '0 10px 24px rgba(0, 102, 255, 0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 102, 255, 0.08)';
                      }}>
                        {/* 标签容器 - 绝对定位在右上角 */}
                        <div style={{
                          position: 'absolute',
                          top: '1rem',
                          right: '1rem',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.3rem',
                          alignItems: 'center',
                          flexWrap: 'nowrap',
                          zIndex: 1
                        }}>
                                {expression.cambridgeLevel && (
                                  <div style={{
                                    padding: '0.3rem 0.6rem',
                                    borderRadius: '4px',
                                    border: '1px solid #1a73e8',
                                    backgroundColor: 'rgba(26, 115, 232, 0.1)',
                                    color: '#1a73e8',
                                    fontSize: '0.7rem',
                                    fontWeight: 'bold',
                                    width: '70px',
                                    textAlign: 'center',
                                    whiteSpace: 'nowrap',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'default',
                                    transition: 'all 0.2s ease'
                                  }} onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(26, 115, 232, 0.2)';
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                  }} onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(26, 115, 232, 0.1)';
                                    e.currentTarget.style.transform = 'scale(1)';
                                  }}>
                                    {expression.cambridgeLevel}
                                  </div>
                                )}
                                <button
                                  onClick={() => updateExpressionStatus(expression.id, 'known')}
                                  style={{
                                    padding: '0.3rem 0.6rem',
                                    borderRadius: '4px',
                                    border: expression.status === 'known' ? '1px solid #28a745' : '1px solid #28a745',
                                    backgroundColor: expression.status === 'known' ? '#28a745' : 'white',
                                    color: expression.status === 'known' ? 'white' : '#28a745',
                                    cursor: 'pointer',
                                    fontSize: '0.7rem',
                                    fontWeight: '500',
                                    width: '70px',
                                    textAlign: 'center',
                                    whiteSpace: 'nowrap',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.2s ease'
                                  }}
                                  title="认识"
                                  onMouseEnter={(e) => {
                                    if (expression.status !== 'known') {
                                      e.currentTarget.style.backgroundColor = 'rgba(40, 167, 69, 0.1)';
                                    }
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = expression.status === 'known' ? '#28a745' : 'white';
                                    e.currentTarget.style.transform = 'scale(1)';
                                  }}
                                >
                                  ✓ 认识
                                </button>
                                <button
                                  onClick={() => saveUnknownExpression(expression)}
                                  style={{
                                    padding: '0.3rem 0.6rem',
                                    borderRadius: '4px',
                                    border: expression.status === 'unknown' ? '1px solid #dc3545' : '1px solid #dc3545',
                                    backgroundColor: expression.status === 'unknown' ? '#dc3545' : 'white',
                                    color: expression.status === 'unknown' ? 'white' : '#dc3545',
                                    cursor: 'pointer',
                                    fontSize: '0.7rem',
                                    fontWeight: '500',
                                    width: '70px',
                                    textAlign: 'center',
                                    whiteSpace: 'nowrap',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.2s ease'
                                  }}
                                  title="不认识，添加到自然科普词汇卡"
                                  onMouseEnter={(e) => {
                                    if (expression.status !== 'unknown') {
                                      e.currentTarget.style.backgroundColor = 'rgba(220, 53, 69, 0.1)';
                                    }
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = expression.status === 'unknown' ? '#dc3545' : 'white';
                                    e.currentTarget.style.transform = 'scale(1)';
                                  }}
                                >
                                  ✗ 不认识
                                </button>
                        </div>
                        
                        {/* 自然科普词汇 */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'flex-start',
                          alignItems: 'flex-start',
                          marginBottom: '0.7rem',
                          flexWrap: 'nowrap',
                          width: '100%',
                          paddingRight: '90px'
                        }}>
                          <div style={{
                            width: '100%',
                            minWidth: 0
                          }}>
                            {/* 自然科普词汇 */}
                            <div
                              style={{
                                fontSize: '1.3rem',
                                fontWeight: 'bold',
                                color: '#1a73e8',
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                                marginBottom: '0.3rem'
                              }}
                            >
                              {expression.expression}
                            </div>
                            
                            {/* 音标、发音 */}
                            <div style={{
                              marginBottom: '0.7rem',
                              display: 'flex',
                              alignItems: 'center',
                              flexWrap: 'nowrap'
                            }}>
                              {expression.phonetic && (
                                <div style={{
                                  fontSize: '1rem',
                                  color: 'rgba(0, 0, 0, 0.7)',
                                  fontFamily: 'Arial, sans-serif',
                                  whiteSpace: 'nowrap',
                                  marginRight: '1rem'
                                }}>
                                  {expression.phonetic}
                                </div>
                              )}
                              <button
                                onClick={() => {
                                  // 创建一个辅助函数来获取高质量声音
                                  const getHighQualityVoice = () => {
                                    // 如果 voices 还没加载，尝试获取
                                    if (!voicesLoaded) {
                                      window.speechSynthesis.getVoices();
                                    }
                                    
                                    const voices = speechSynthesis.getVoices();
                                    if (!voices || voices.length === 0) return null;
                                    
                                    // 优先尝试使用 Google 女声
                                    let voice = voices.find(v => 
                                      v.lang === 'en-US' && 
                                      (v.name.includes('Google') && v.name.toLowerCase().includes('female'))
                                    );
                                    if (voice) return voice;
                                    
                                    // 其次尝试使用 Google 男声或女声
                                    voice = voices.find(v => 
                                      v.lang === 'en-US' && 
                                      v.name.includes('Google')
                                    );
                                    if (voice) return voice;
                                    
                                    // 再次尝试 Samantha 或 Alex
                                    voice = voices.find(v => 
                                      v.lang === 'en-US' && 
                                      (v.name.includes('Samantha') || v.name.includes('Alex'))
                                    );
                                    if (voice) return voice;
                                    
                                    // 最后尝试任何 en-US 的声音
                                    return voices.find(v => v.lang === 'en-US');
                                  };
                                  
                                  // 使用浏览器语音合成的备用函数
                                  const speakWithBrowser = () => {
                                    try {
                                      const utterance = new SpeechSynthesisUtterance(expression.expression);
                                      utterance.lang = 'en-US';
                                      utterance.rate = 0.85;
                                      const highQualityVoice = getHighQualityVoice();
                                      if (highQualityVoice) {
                                        utterance.voice = highQualityVoice;
                                      }
                                      speechSynthesis.cancel(); // 清除之前的发音队列
                                      speechSynthesis.speak(utterance);
                                    } catch (e) {
                                      console.error('浏览器发音失败:', e);
                                    }
                                  };
                                  
                                  try {
                                    // 先尝试使用有道语音合成 API
                                    const audio = new Audio(`https://dict.youdao.com/dictvoice?audio=${encodeURIComponent(expression.expression)}&type=2`);
                                    
                                    let played = false;
                                    
                                    // 设置较长的超时时间
                                    const timeout = setTimeout(() => {
                                      if (!played) {
                                        console.log('有道TTS响应超时，使用备用发音');
                                        speakWithBrowser();
                                      }
                                    }, 2000); // 2秒超时，比之前快一点
                                    
                                    audio.addEventListener('canplaythrough', () => {
                                      if (!played) {
                                        played = true;
                                        clearTimeout(timeout);
                                        audio.play().catch(() => {
                                          speakWithBrowser();
                                        });
                                      }
                                    });
                                    
                                    audio.onerror = () => {
                                      if (!played) {
                                        played = true;
                                        clearTimeout(timeout);
                                        console.log('有道TTS加载失败，使用浏览器发音');
                                        speakWithBrowser();
                                      }
                                    };
                                    
                                    // 尝试预加载音频
                                    audio.load();
                                    
                                    // 同时尝试播放，如果音频已经缓存了就立即播放
                                    audio.play().then(() => {
                                      if (!played) {
                                        played = true;
                                        clearTimeout(timeout);
                                      }
                                    }).catch(error => {
                                      // 如果播放失败，让 canplaythrough 事件来处理
                                      console.log('初始播放失败，等待音频加载');
                                    });
                                  } catch (e) {
                                    console.error('发音失败:', e);
                                    speakWithBrowser();
                                  }
                                }}
                                style={{
                                  width: '30px',
                                  height: '30px',
                                  borderRadius: '50%',
                                  backgroundColor: 'rgba(200, 200, 200, 0.2)',
                                  border: 'none',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.2s',
                                  whiteSpace: 'nowrap'
                                }}
                                title="听发音"
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = 'rgba(150, 150, 150, 0.3)';
                                  e.target.style.transform = 'scale(1.1)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'rgba(200, 200, 200, 0.2)';
                                  e.target.style.transform = 'scale(1)';
                                }}
                              >
                                🔊
                              </button>
                            </div>
                            
                            {/* 词性 */}
                            <div style={{
                              marginBottom: '0.5rem',
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              {expression.partOfSpeech && (
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center'
                                }}>
                                  <div style={{
                                    background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                                    color: 'white',
                                    padding: '0.2rem 0.5rem',
                                    borderRadius: '4px',
                                    fontSize: '0.7rem',
                                    fontWeight: 'bold',
                                    marginRight: '0.5rem',
                                    whiteSpace: 'nowrap',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease'
                                  }} onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(45deg, #1557b0, #003a80)';
                                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
                                  }} onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(45deg, #1a73e8, #0056b3)';
                                    e.currentTarget.style.boxShadow = 'none';
                                  }}>
                                    词性
                                  </div>
                                  <div style={{
                                    fontSize: '1.1rem',
                                    color: 'rgba(0, 0, 0, 0.9)',
                                    fontWeight: '500',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {abbreviatePartOfSpeech(expression.partOfSpeech)}
                                  </div>
                                </div>
                              )}
                            </div>
                            
                            {/* 中文翻译 */}
                            <div style={{
                              marginBottom: '0.7rem',
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              {expression.chineseTranslation && (
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center'
                                }}>
                                  <div style={{
                                    background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                                    color: 'white',
                                    padding: '0.2rem 0.5rem',
                                    borderRadius: '4px',
                                    fontSize: '0.7rem',
                                    fontWeight: 'bold',
                                    marginRight: '0.5rem',
                                    whiteSpace: 'nowrap',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease'
                                  }} onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(45deg, #1557b0, #003a80)';
                                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
                                  }} onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(45deg, #1a73e8, #0056b3)';
                                    e.currentTarget.style.boxShadow = 'none';
                                  }}>
                                    中译
                                  </div>
                                  {showChinese && (
                                    <div style={{
                                      fontSize: '1rem',
                                      color: 'rgba(0, 0, 0, 0.9)',
                                      fontWeight: '500',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis'
                                    }} title={expression.chineseTranslation}>
                                      {expression.chineseTranslation}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            
                          </div>
                        </div>
                        
                        {/* 英文定义 - 仅显示后端提交的 */}
                        {expression.definition && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            marginTop: '0.2rem',
                            marginBottom: '0.7rem'
                          }}>
                            <div style={{
                              background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                              color: 'white',
                              padding: '0.2rem 0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.7rem',
                              fontWeight: 'bold',
                              marginRight: '0.5rem',
                              whiteSpace: 'nowrap',
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease'
                            }} onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'linear-gradient(45deg, #1557b0, #003a80)';
                              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
                            }} onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'linear-gradient(45deg, #1a73e8, #0056b3)';
                              e.currentTarget.style.boxShadow = 'none';
                            }}>
                              英译
                            </div>
                            <div style={{
                              fontSize: '0.9rem',
                              color: 'rgba(0, 0, 0, 0.8)',
                              flex: 1,
                              lineHeight: '1.4'
                            }}>
                              {expression.definition}
                            </div>
                          </div>
                        )}
                        
                        {/* 联想词 - 仅显示后端提交的 */}
                        {expression.synonyms && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            marginBottom: '0.2rem',
                            marginTop: '0.2rem'
                          }}>
                            <div style={{
                              background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                              color: 'white',
                              padding: '0.2rem 0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.7rem',
                              fontWeight: 'bold',
                              marginRight: '0.5rem',
                              whiteSpace: 'nowrap',
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease'
                            }} onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'linear-gradient(45deg, #1557b0, #003a80)';
                              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
                            }} onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'linear-gradient(45deg, #1a73e8, #0056b3)';
                              e.currentTarget.style.boxShadow = 'none';
                            }}>
                              联想词
                            </div>
                            <div style={{
                              fontSize: '0.85rem',
                              color: 'rgba(0, 0, 0, 0.6)',
                              flex: 1
                            }}>
                              {expression.synonyms}
                            </div>
                          </div>
                        )}
                        
                        {/* 白色底框 - 例句 */}
                        <div 
                          style={{
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            padding: '0.8rem',
                            marginTop: '0.7rem',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            position: 'relative',
                            width: '100%',
                            boxSizing: 'border-box',
                            touchAction: 'manipulation',
                            WebkitTapHighlightColor: 'transparent'
                          }}
                          onClick={() => {
                            let targetIndex = -1;
                            
                            // 优先使用保存的字幕索引
                            if (expression.subtitleIndex !== undefined && subtitles[expression.subtitleIndex]) {
                              targetIndex = expression.subtitleIndex;
                              console.log('使用保存的字幕索引播放（地道表达）:', targetIndex);
                            } else if (expression.sentence) {
                              // 如果没有保存的索引，再通过文本匹配
                              const sentenceLower = expression.sentence.toLowerCase();
                              for (let i = 0; i < subtitles.length; i++) {
                                const sub = subtitles[i];
                                const subLower = sub.english.toLowerCase();
                                if (subLower.includes(sentenceLower) || sentenceLower.includes(subLower)) {
                                  targetIndex = i;
                                  console.log('通过文本匹配找到字幕索引（地道表达）:', targetIndex);
                                  break;
                                }
                              }
                            }
                            
                            if (targetIndex !== -1) {
                              setCurrentSubtitle(targetIndex);
                              playOriginalAudio(targetIndex);
                            }
                          }}
                          onTouchStart={(e) => {
                            e.preventDefault();
                            let targetIndex = -1;
                            
                            // 优先使用保存的字幕索引
                            if (expression.subtitleIndex !== undefined && subtitles[expression.subtitleIndex]) {
                              targetIndex = expression.subtitleIndex;
                              console.log('使用保存的字幕索引播放（地道表达）:', targetIndex);
                            } else if (expression.sentence) {
                              // 如果没有保存的索引，再通过文本匹配
                              const sentenceLower = expression.sentence.toLowerCase();
                              for (let i = 0; i < subtitles.length; i++) {
                                const sub = subtitles[i];
                                const subLower = sub.english.toLowerCase();
                                if (subLower.includes(sentenceLower) || sentenceLower.includes(subLower)) {
                                  targetIndex = i;
                                  console.log('通过文本匹配找到字幕索引（地道表达）:', targetIndex);
                                  break;
                                }
                              }
                            }
                            
                            if (targetIndex !== -1) {
                              setCurrentSubtitle(targetIndex);
                              playOriginalAudio(targetIndex);
                            }
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(26, 115, 232, 0.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'white';
                          }}
                        >
                          <div style={{
                            background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                            color: 'white',
                            padding: '0.2rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.7rem',
                            fontWeight: 'bold',
                            marginBottom: '0.5rem',
                            display: 'inline-block'
                          }}>
                            原文例句
                          </div>
                          <div style={{
                            fontSize: '0.95rem',
                            color: '#333',
                            marginBottom: '0.3rem',
                            lineHeight: '1.5'
                          }}>
                            {expression.sentence || '无例句'}
                          </div>
                          {(expression.sentenceChinese || expression.sentenceChinese === '') && (
                            <div style={{
                              fontSize: '0.85rem',
                              color: 'rgba(0, 0, 0, 0.6)',
                              lineHeight: '1.4'
                            }}>
                              {showChinese ? (expression.sentenceChinese || '无中文翻译') : ''}
                            </div>
                          )}
                        </div>
                        

                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          )}
          
          {/* iPad横屏和手机横屏布局 */}
          {isFloatingPlayerMode && (
            <div style={{ position: 'relative', width: '100%', paddingBottom: '100px' }}>
              {/* 播放器悬浮在字幕表面 - 可拖动 */}
              <div 
                ref={playerRef}
                style={{ 
                  position: 'fixed', 
                  top: playerPosition.y, 
                  left: playerPosition.x, 
                  width: isMobile ? '60%' : '40%', 
                  maxWidth: isMobile ? '350px' : '500px', 
                  zIndex: 10000, // 提高z-index值，确保在导航器之上
                  cursor: isDragging ? 'grabbing' : 'grab',
                  touchAction: 'none', // 防止触摸时的默认行为
                  transform: `scale(${playerScale})`,
                  transformOrigin: 'center center',
                  transition: 'transform 0.1s ease-out'
                }}
                onMouseDown={handlePlayerMouseDown}
                onMouseLeave={handlePlayerMouseLeave}
                onTouchStart={handlePlayerTouchStart}
                onTouchMove={handlePlayerTouchMove}
                onTouchEnd={handlePlayerTouchEnd}
              >
                {/* 视频播放器 - 简化版（仅播放器） */}
                <div style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  backgroundColor: 'white',
                  boxShadow: isDragging ? '0 8px 25px rgba(0, 0, 0, 0.3)' : '0 4px 15px rgba(0, 0, 0, 0.2)',
                  transition: isDragging ? 'none' : 'box-shadow 0.3s ease'
                }}>
                  <div style={{ position: 'relative', width: '100%', aspectRatio: '16/9' }}>
                  {/* 视频播放器部分 */}
                  {lesson?.content?.videoUrl && (lesson.content.videoUrl.includes('youtube.com') || lesson.content.videoUrl.includes('youtu.be')) ? (
                    <div style={{ width: '100%', height: '100%', overflow: 'hidden', position: 'relative' }}>
                      <iframe
                        ref={floatingVideoRef}
                        width="100%"
                        height="100%"
                        src={`${(() => {
                          let videoId = '';
                          if (lesson.content.videoUrl.includes('youtu.be')) {
                            videoId = lesson.content.videoUrl.split('/').pop().split('?')[0];
                          } else if (lesson.content.videoUrl.includes('youtube.com/watch')) {
                            try {
                              const urlParams = new URLSearchParams(new URL(lesson.content.videoUrl).search);
                              videoId = urlParams.get('v');
                            } catch (error) {
                              console.error('Error parsing YouTube URL:', error);
                            }
                          } else if (lesson.content.videoUrl.includes('youtube.com/embed/')) {
                            videoId = lesson.content.videoUrl.split('/embed/')[1].split('?')[0];
                          }
                          return videoId ? `https://www.youtube.com/embed/${videoId}?rel=0&showinfo=0&playsinline=1&enablejsapi=1&controls=0&modestbranding=1` : 'https://www.youtube.com/embed/3tisOnOkwzo';
                        })()}`}
                        title={lesson.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
                        allowFullScreen
                        style={{ width: '100%', height: '100%', display: 'block' }}
                      ></iframe>
                    </div>
                  ) : (
                    <div style={{ width: '100%', height: '100%', overflow: 'hidden', position: 'relative' }}>
                      {(() => {
                        const fileType = lesson?.fileType || lesson?.type || 'video';
                        const isAudio = fileType === 'audio' || fileType === '音频';
                        const mediaUrl = lesson?.content?.videoUrl 
                          ? (lesson.content.videoUrl.startsWith('http') 
                              ? lesson.content.videoUrl 
                              : getMediaUrl(lesson.content.videoUrl))
                          : (isAudio 
                              ? '' 
                              : 'https://www.w3schools.com/html/mov_bbb.mp4');
                        const posterUrl = lesson?.content?.posterUrl 
                          ? getMediaUrl(lesson.content.posterUrl)
                          : 'https://images.unsplash.com/photo-1516110833967-8c716bf8273c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80';

                        if (isAudio) {
                          return (
                            <>
                              <div style={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#000'
                              }}>
                                <img 
                                  src={posterUrl}
                                  alt="音频封面"
                                  style={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    objectFit: 'contain'
                                  }}
                                />
                              </div>
                              <audio
                                ref={floatingVideoRef}
                                style={{ display: 'none' }}
                                src={mediaUrl}
                                onTimeUpdate={() => {
                                  if (floatingVideoRef.current) {
                                    setCurrentTime(floatingVideoRef.current.currentTime);
                                    if (floatingVideoRef.current.duration) {
                                      setCurrentProgress((floatingVideoRef.current.currentTime / floatingVideoRef.current.duration) * 100);
                                    }
                                  }
                                }}
                                onLoadedMetadata={() => {
                                  if (floatingVideoRef.current) {
                                    setVideoDuration(floatingVideoRef.current.duration);
                                  }
                                }}
                              />
                            </>
                          );
                        } else {
                          return (
                            <video
                              ref={floatingVideoRef}
                              style={{ width: '100%', height: '100%', cursor: 'pointer' }}
                              playsInline
                              disablePictureInPicture
                              poster={posterUrl}
                              onTimeUpdate={() => {
                                if (floatingVideoRef.current) {
                                  setCurrentTime(floatingVideoRef.current.currentTime);
                                  if (floatingVideoRef.current.duration) {
                                    setCurrentProgress((floatingVideoRef.current.currentTime / floatingVideoRef.current.duration) * 100);
                                  }
                                }
                              }}
                              onLoadedMetadata={() => {
                                if (floatingVideoRef.current) {
                                  setVideoDuration(floatingVideoRef.current.duration);
                                }
                              }}
                            >
                              <source src={mediaUrl} type="video/mp4" />
                            </video>
                          );
                        }
                      })()}
                    </div>
                  )}
                  </div>
                </div>
              </div>

              {/* 字幕区域 - 占据整个宽度 */}
              <div style={{ width: '100%', boxSizing: 'border-box' }}>
                {/* 视频标题 */}
                <div style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  backgroundColor: 'white',
                  marginBottom: '1.5rem',
                  padding: '1rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <button
                      onClick={() => navigate(-1)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#0066ff',
                        textDecoration: 'none',
                        padding: '0.5rem',
                        borderRadius: '8px',
                        backgroundColor: '#f0f7ff',
                        border: '1px solid rgba(0, 102, 255, 0.2)',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        fontSize: '1.2rem',
                        width: '36px',
                        height: '36px'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#e6f0ff';
                        e.target.style.boxShadow = '0 2px 10px rgba(0, 102, 255, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#f0f7ff';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      ←
                    </button>
                    <h2 style={{ color: '#333333', fontSize: '1.5rem', fontWeight: '600', margin: 0 }}>
                      {lesson?.title || '视频标题'}
                    </h2>
                  </div>
                </div>

                {/* 字幕列表 */}
                <div style={{ 
                  border: '1px solid #e0e0e0',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  backgroundColor: 'white',
                  marginBottom: '1.5rem'
                }}>


                  {/* 字幕列表 */}
                  <div style={{ position: 'relative' }}>
                    <div ref={subtitleContainerRef} style={{
                      maxHeight: '600px',
                      overflowY: scrollLocked ? 'hidden' : 'auto',
                      paddingRight: '0',
                      backgroundColor: '#ffffff',
                      boxSizing: 'border-box'
                    }}>
                    {subtitles.map((subtitle, index) => {
                      // 找出句子中最长的单词作为填空
                      const words = subtitle.english.split(' ');
                      let longestWordIndex = 0;
                      let longestWordLength = 0;
                      
                      words.forEach((word, wordIndex) => {
                        const cleanWord = word.replace(/[^a-zA-Z]/g, '');
                        if (cleanWord.length > longestWordLength) {
                          longestWordLength = cleanWord.length;
                          longestWordIndex = wordIndex;
                        }
                      });
                      
                      // 构建填空内容
                      const fillBlanks = words.map((word, wordIndex) => {
                        // 只对最长的单词设置填空
                        if (wordIndex === longestWordIndex) {
                          return {
                            type: 'blank',
                            id: `${index}-${wordIndex}`,
                            correctAnswer: word
                          };
                        } else {
                          return {
                            type: 'text',
                            content: word
                          };
                        }
                      });
                      
                      return (
                      <div key={index} 
                        style={{
                          marginBottom: '0.5rem',
                          padding: '0.75rem',
                          borderRadius: '8px',
                          backgroundColor: currentSubtitle === index ? 'rgba(255, 230, 109, 0.3)' : 'rgba(240,240,240,0.5)',
                          border: 'none',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer',
                          touchAction: 'manipulation',
                          WebkitTapHighlightColor: 'transparent'
                        }}
                        className="subtitle-item"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (exerciseMode === 'repeat' || exerciseMode === 'fill') {
                            const videoElement = isFloatingPlayerMode ? floatingVideoRef.current : videoRef.current;
                            if (playingSubtitleIndex === index && videoElement && !videoElement.paused) {
                              videoElement.pause();
                              setIsPlaying(false);
                              setPlayingSubtitleIndex(null);
                            } else {
                              playOriginalAudio(index);
                              setPlayingSubtitleIndex(index);
                            }
                          } else {
                            handleSubtitleClick(index);
                          }
                        }}>
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'flex-start',
                          marginBottom: '0.5rem'
                        }}>
                          <div style={{ fontSize: '0.9rem', color: 'rgba(0,0,0,0.5)' }}>
                            {Math.floor(subtitle.time / 60).toString().padStart(2, '0')}:{(Math.floor(subtitle.time % 60)).toString().padStart(2, '0')}
                          </div>
                        </div>
                        
                        {/* 填空练习模式 */}
                        {exerciseMode === 'fill' && (
                          <>
                            <div style={{ 
                              color: '#000000', 
                              marginBottom: '0.5rem',
                              lineHeight: '1.4',
                              fontSize: getSubtitleFontSizes().english,
                              fontWeight: '500'
                            }}>
                              {fillBlanks.map((item, itemIndex) => {
                                if (item.type === 'text') {
                                  return (
                                    <span key={itemIndex}>{item.content} </span>
                                  );
                                } else {
                                  const isRevealed = revealedWords[item.id] || false;
                                  return (
                                    <span
                                      key={item.id}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setRevealedWords(prev => ({
                                          ...prev,
                                          [item.id]: !prev[item.id]
                                        }));
                                      }}
                                      style={{
                                        padding: '0.1rem 0.8rem',
                                        backgroundColor: isRevealed ? 'transparent' : 'rgba(26, 115, 232, 0.2)',
                                        cursor: 'pointer',
                                        borderRadius: '6px',
                                        fontWeight: '500',
                                        color: isRevealed ? '#1a73e8' : 'transparent',
                                        userSelect: 'none',
                                        display: 'inline-block',
                                        minWidth: '50px',
                                        transition: 'all 0.2s ease'
                                      }}
                                      title={isRevealed ? "点击隐藏" : "点击查看"}
                                    >
                                      {item.correctAnswer}
                                    </span>
                                  );
                                }
                              })}
                            </div>
                            <div style={{ 
                              color: 'rgba(0,0,0,0.6)', 
                              lineHeight: '1.4',
                              fontWeight: 'normal',
                              fontSize: getSubtitleFontSizes().chinese
                            }}>
                              {subtitle.chinese}
                            </div>
                          </>
                        )}
                        
                        {exerciseMode === 'none' && subtitleMode === 'bilingual' && (
                          <>
                            <div style={{ 
                              color: currentSubtitle === index ? '#000000' : '#1a1a1a', 
                              marginBottom: '0.5rem', 
                              lineHeight: '1.4',
                              fontWeight: currentSubtitle === index ? 'bold' : '500',
                              fontSize: getSubtitleFontSizes().english
                            }}>
                              {renderAnnotatedText(subtitle.english)}
                            </div>
                            <div style={{ 
                              color: currentSubtitle === index ? '#000000' : 'rgba(0,0,0,0.6)', 
                              lineHeight: '1.4',
                              fontWeight: currentSubtitle === index ? 'bold' : 'normal',
                              fontSize: getSubtitleFontSizes().chinese
                            }}>
                              {subtitle.chinese}
                            </div>
                          </>
                        )}
                        {exerciseMode === 'none' && subtitleMode === 'english' && (
                          <div style={{ 
                            color: currentSubtitle === index ? '#000000' : '#1a1a1a', 
                            lineHeight: '1.4',
                            fontWeight: currentSubtitle === index ? 'bold' : '500',
                            fontSize: getSubtitleFontSizes().english
                          }}>
                            {renderAnnotatedText(subtitle.english)}
                          </div>
                        )}
                        {exerciseMode === 'none' && subtitleMode === 'chinese' && (
                          <div style={{ 
                            color: currentSubtitle === index ? '#000000' : '#333333', 
                            lineHeight: '1.4',
                            fontWeight: currentSubtitle === index ? 'bold' : 'normal',
                            fontSize: getSubtitleFontSizes().chinese
                          }}>
                            {subtitle.chinese}
                          </div>
                        )}
                        
                        {/* 跟读练习模式 */}
                        {exerciseMode === 'repeat' && (
                          <>
                            <div style={{ 
                              color: '#000000', 
                              marginBottom: '0.5rem',
                              lineHeight: '1.4',
                              fontSize: getSubtitleFontSizes().english,
                              fontWeight: '500'
                            }}>
                              {renderAnnotatedText(subtitle.english)}
                            </div>
                            <div style={{ 
                              backgroundColor: 'transparent',
                              borderRadius: '8px',
                              padding: '1rem',
                              marginTop: '0.5rem',
                              display: 'flex',
                              gap: '1rem',
                              alignItems: 'center'
                            }}>
                              {/* 播放原音按钮 */}
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  const videoElement = isFloatingPlayerMode ? floatingVideoRef.current : videoRef.current;
                                  if (playingSubtitleIndex === index && videoElement && !videoElement.paused) {
                                    videoElement.pause();
                                    setIsPlaying(false);
                                    setPlayingSubtitleIndex(null);
                                  } else {
                                    playOriginalAudio(index);
                                    setPlayingSubtitleIndex(index);
                                  }
                                }}
                                onTouchStart={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                }}
                                onTouchEnd={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  const videoElement = isFloatingPlayerMode ? floatingVideoRef.current : videoRef.current;
                                  if (playingSubtitleIndex === index && videoElement && !videoElement.paused) {
                                    videoElement.pause();
                                    setIsPlaying(false);
                                    setPlayingSubtitleIndex(null);
                                  } else {
                                    playOriginalAudio(index);
                                    setPlayingSubtitleIndex(index);
                                  }
                                }}
                                style={{
                                  background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                                  border: 'none',
                                  color: 'white',
                                  padding: '0.75rem',
                                  borderRadius: '50%',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '40px',
                                  height: '40px',
                                  transition: 'all 0.2s ease',
                                  touchAction: 'manipulation',
                                  WebkitTapHighlightColor: 'transparent',
                                  outline: 'none'
                                }}
                                title={playingSubtitleIndex === index ? "暂停" : "播放原音"}
                              >
                                {playingSubtitleIndex === index ? (
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                                    <rect x="6" y="4" width="4" height="16"/>
                                    <rect x="14" y="4" width="4" height="16"/>
                                  </svg>
                                ) : (
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                                    <path d="M8 5v14l11-7z"/>
                                  </svg>
                                )}
                              </button>
                              {/* 开始/停止录音按钮 */}
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  toggleRecording(index);
                                }}
                                onTouchStart={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                }}
                                onTouchEnd={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  toggleRecording(index);
                                }}
                                style={{
                                  background: recordingIndex === index ? 'rgba(255, 0, 0, 0.8)' : 'rgba(255, 100, 100, 0.8)',
                                  border: recordingIndex === index ? '2px solid #ff0000' : 'none',
                                  color: 'white',
                                  padding: '0.75rem',
                                  borderRadius: '50%',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '40px',
                                  height: '40px',
                                  animation: recordingIndex === index ? 'pulse 1s infinite' : 'none',
                                  transition: 'all 0.2s ease',
                                  touchAction: 'manipulation',
                                  WebkitTapHighlightColor: 'transparent',
                                  zIndex: 10,
                                  position: 'relative'
                                }}
                                title={recordingIndex === index ? "停止录音" : "开始录音"}
                              >
                                {recordingIndex === index ? '⏹' : '🎤'}
                              </button>
                              {/* 播放录音按钮 */}
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  playRecordedAudio(index);
                                }}
                                onTouchStart={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                }}
                                onTouchEnd={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  playRecordedAudio(index);
                                }}
                                style={{
                                  background: hasRecording[index] ? 'rgba(76, 175, 80, 0.8)' : 'rgba(100, 100, 100, 0.5)',
                                  border: 'none',
                                  color: 'white',
                                  padding: '0.75rem',
                                  borderRadius: '50%',
                                  cursor: hasRecording[index] ? 'pointer' : 'not-allowed',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '40px',
                                  height: '40px',
                                  transition: 'all 0.2s ease',
                                  touchAction: 'manipulation',
                                  WebkitTapHighlightColor: 'transparent',
                                  outline: 'none',
                                  zIndex: 10,
                                  position: 'relative'
                                }}
                                title={playingRecordingIndex === index ? "暂停录音" : (hasRecording[index] ? "播放录音" : "请先录音")}
                              >
                                {playingRecordingIndex === index ? (
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                                    <rect x="6" y="4" width="4" height="16"/>
                                    <rect x="14" y="4" width="4" height="16"/>
                                  </svg>
                                ) : (
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                                    <path d="M8 5v14l11-7z"/>
                                  </svg>
                                )}
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                    </div>
                  </div>
                </div>
              </div>


            </div>
          )}

          {/* iPad竖屏和手机竖屏布局 */}
          {isTopPlayerMode && (
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
              {/* 播放器在顶部铺满整个宽度 - 固定位置 */}
              <div style={{ width: '100%', flexShrink: 0, zIndex: 100 }}>
                <div style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  backgroundColor: 'white',
                  marginBottom: '0.5rem',
                  marginTop: '0.5rem'
                }}>
                  {/* 视频标题 */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '1rem',
                    borderBottom: '1px solid #e0e0e0'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      {/* 返回按钮 */}
                      <button 
                        onClick={() => navigate(-1)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#0066ff',
                          textDecoration: 'none',
                          padding: '0.5rem',
                          borderRadius: '8px',
                          backgroundColor: '#f0f7ff',
                          border: '1px solid rgba(0, 102, 255, 0.2)',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer',
                          fontSize: '1rem',
                          width: '32px',
                          height: '32px'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#e6f0ff';
                          e.target.style.boxShadow = '0 2px 10px rgba(0, 102, 255, 0.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#f0f7ff';
                          e.target.style.boxShadow = 'none';
                        }}
                      >
                        ←
                      </button>
                      <h2 style={{
                        color: '#333333',
                        fontSize: '1.2rem',
                        fontWeight: '600',
                        margin: 0
                      }}>{lesson?.title || '视频标题'}</h2>
                    </div>
                  </div>
                  {/* 视频播放器 */}
                  <div style={{ position: 'relative', width: '100%', aspectRatio: '16/9' }}>
                  {lesson?.content?.videoUrl && (lesson.content.videoUrl.includes('youtube.com') || lesson.content.videoUrl.includes('youtu.be')) ? (
                    <div style={{ width: '100%', height: '100%', overflow: 'hidden', position: 'relative' }}>
                      <iframe
                        ref={videoRef}
                        width="100%"
                        height="100%"
                        src={`${(() => {
                          let videoId = '';
                          if (lesson.content.videoUrl.includes('youtu.be')) {
                            videoId = lesson.content.videoUrl.split('/').pop().split('?')[0];
                          } else if (lesson.content.videoUrl.includes('youtube.com/watch')) {
                            try {
                              const urlParams = new URLSearchParams(new URL(lesson.content.videoUrl).search);
                              videoId = urlParams.get('v');
                            } catch (error) {
                              console.error('Error parsing YouTube URL:', error);
                            }
                          } else if (lesson.content.videoUrl.includes('youtube.com/embed/')) {
                            videoId = lesson.content.videoUrl.split('/embed/')[1].split('?')[0];
                          }
                          return videoId ? `https://www.youtube.com/embed/${videoId}?rel=0&showinfo=0&playsinline=1&enablejsapi=1&controls=0&modestbranding=1` : 'https://www.youtube.com/embed/3tisOnOkwzo';
                        })()}`}
                        title={lesson.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
                        allowFullScreen
                        style={{ width: '100%', height: '100%', display: 'block' }}
                      ></iframe>
                      
                      {/* 自定义控制栏 - 仅保留播放按钮 */}
                      <div style={{
                        display: 'none'
                      }}>
                        {/* 播放/暂停按钮 */}
                        <button
                          onClick={() => {
                            if (videoRef.current) {
                              const video = videoRef.current;
                              try {
                                // 切换播放/暂停状态
                                if (isPlaying) {
                                  video.contentWindow.postMessage(JSON.stringify({
                                    event: 'command',
                                    func: 'pauseVideo',
                                    args: []
                                  }), '*');
                                  setIsPlaying(false);
                                } else {
                                  video.contentWindow.postMessage(JSON.stringify({
                                    event: 'command',
                                    func: 'playVideo',
                                    args: []
                                  }), '*');
                                  setIsPlaying(true);
                                }
                              } catch (error) {
                                console.error('无法控制YouTube视频:', error);
                              }
                            }
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'white',
                            fontSize: '16px',
                            cursor: 'pointer',
                            width: '24px',
                            height: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {isPlaying ? '⏸' : '▶'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      style={{ width: '100%', height: '100%', overflow: 'hidden', position: 'relative' }}
                      onTouchStart={() => {
                        setIsPlayerTouched(true);
                        setTimeout(() => setIsPlayerTouched(false), 3000);
                      }}
                    >
                      {(() => {
                        const fileType = lesson?.fileType || lesson?.type || 'video';
                        const isAudio = fileType === 'audio' || fileType === '音频';
                        const mediaUrl = lesson?.content?.videoUrl 
                          ? (lesson.content.videoUrl.startsWith('http') 
                              ? lesson.content.videoUrl 
                              : getMediaUrl(lesson.content.videoUrl))
                          : (isAudio 
                              ? '' 
                              : 'https://www.w3schools.com/html/mov_bbb.mp4');
                        const posterUrl = lesson?.content?.posterUrl 
                          ? getMediaUrl(lesson.content.posterUrl)
                          : 'https://images.unsplash.com/photo-1516110833967-8c716bf8273c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80';

                        if (isAudio) {
                          return (
                            <>
                              <div style={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#000'
                              }}>
                                <img 
                                  src={posterUrl}
                                  alt="音频封面"
                                  style={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    objectFit: 'contain'
                                  }}
                                />
                              </div>
                              <audio
                                ref={videoRef}
                                style={{ display: 'none' }}
                                src={mediaUrl}
                                onTimeUpdate={() => {
                                  if (videoRef.current) {
                                    setCurrentTime(videoRef.current.currentTime);
                                    if (videoRef.current.duration) {
                                      setCurrentProgress((videoRef.current.currentTime / videoRef.current.duration) * 100);
                                    }
                                  }
                                }}
                                onLoadedMetadata={() => {
                                  if (videoRef.current) {
                                    setVideoDuration(videoRef.current.duration);
                                  }
                                }}
                              />
                            </>
                          );
                        } else {
                          return (
                            <video
                              ref={videoRef}
                              style={{
                                width: '100%',
                                height: '100%',
                                cursor: 'pointer'
                              }}
                              playsInline
                              disablePictureInPicture
                              controlsList="nodownload nofullscreen noremoteplayback"
                              poster={posterUrl}
                              onTimeUpdate={() => {
                                if (videoRef.current) {
                                  setCurrentTime(videoRef.current.currentTime);
                                  if (videoRef.current.duration) {
                                    setCurrentProgress((videoRef.current.currentTime / videoRef.current.duration) * 100);
                                  }
                                }
                              }}
                              onLoadedMetadata={() => {
                                if (videoRef.current) {
                                  setVideoDuration(videoRef.current.duration);
                                }
                              }}
                            >
                              <source src={mediaUrl} type="video/mp4" />
                            </video>
                          );
                        }
                      })()}
                      
                      {/* 自定义控制栏 - 绝对定位在视频容器底部 */}
                      <div style={{
                        display: 'none'
                      }}>
                        {/* 播放/暂停按钮 */}
                        <button
                          onClick={() => {
                            if (videoRef.current) {
                              const video = videoRef.current;
                              if (isPlaying) {
                                video.pause();
                                setIsPlaying(false);
                              } else {
                                video.play().catch(error => {
                                  console.error('播放视频失败:', error);
                                });
                                setIsPlaying(true);
                              }
                            }
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'white',
                            fontSize: '16px',
                            cursor: 'pointer',
                            width: '24px',
                            height: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {isPlaying ? '⏸' : '▶'}
                        </button>
                      </div>
                    </div>
                  )}
                  </div>
                </div>
              </div>

              {/* 字幕在播放器下方 - 可独立滚动 */}
              <div style={{ flex: 1, overflowY: 'auto', marginBottom: '100px' }}>
                <div style={{ 
                  border: '1px solid #e0e0e0',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  backgroundColor: 'white'
                }}>

                  {/* 字幕列表 */}
                  <div style={{ position: 'relative', height: '100%' }}>
                    <div ref={subtitleContainerRef} style={{
                      height: '100%',
                      overflowY: scrollLocked ? 'hidden' : 'auto',
                      paddingRight: '0',
                      backgroundColor: '#ffffff',
                      boxSizing: 'border-box'
                    }}>
                    {subtitles.map((subtitle, index) => {
                      // 找出句子中最长的单词作为填空
                      const words = subtitle.english.split(' ');
                      let longestWordIndex = 0;
                      let longestWordLength = 0;
                      
                      words.forEach((word, wordIndex) => {
                        const cleanWord = word.replace(/[^a-zA-Z]/g, '');
                        if (cleanWord.length > longestWordLength) {
                          longestWordLength = cleanWord.length;
                          longestWordIndex = wordIndex;
                        }
                      });
                      
                      // 构建填空内容
                      const fillBlanks = words.map((word, wordIndex) => {
                        // 只对最长的单词设置填空
                        if (wordIndex === longestWordIndex) {
                          return {
                            type: 'blank',
                            id: `${index}-${wordIndex}`,
                            correctAnswer: word
                          };
                        } else {
                          return {
                            type: 'text',
                            content: word
                          };
                        }
                      });
                      
                      return (
                      <div key={index} 
                        style={{
                          marginBottom: '0.5rem',
                          padding: '0.75rem',
                          borderRadius: '8px',
                          backgroundColor: currentSubtitle === index ? 'rgba(255, 230, 109, 0.3)' : 'rgba(240,240,240,0.5)',
                          border: 'none',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer',
                          touchAction: 'manipulation',
                          WebkitTapHighlightColor: 'transparent'
                        }}
                        className="subtitle-item"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (exerciseMode === 'repeat' || exerciseMode === 'fill') {
                            const videoElement = isFloatingPlayerMode ? floatingVideoRef.current : videoRef.current;
                            if (playingSubtitleIndex === index && videoElement && !videoElement.paused) {
                              videoElement.pause();
                              setIsPlaying(false);
                              setPlayingSubtitleIndex(null);
                            } else {
                              playOriginalAudio(index);
                              setPlayingSubtitleIndex(index);
                            }
                          } else {
                            handleSubtitleClick(index);
                          }
                        }}>
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'flex-start',
                          marginBottom: '0.5rem'
                        }}>
                          <div style={{ fontSize: '0.9rem', color: 'rgba(0,0,0,0.5)' }}>
                            {Math.floor(subtitle.time / 60).toString().padStart(2, '0')}:{(Math.floor(subtitle.time % 60)).toString().padStart(2, '0')}
                          </div>
                        </div>
                        
                        {/* 填空练习模式 */}
                        {exerciseMode === 'fill' && (
                          <>
                            <div style={{ 
                              color: '#000000', 
                              marginBottom: '0.5rem',
                              lineHeight: '1.4',
                              fontSize: getSubtitleFontSizes().english,
                              fontWeight: '500'
                            }}>
                              {fillBlanks.map((item, itemIndex) => {
                                if (item.type === 'text') {
                                  return (
                                    <span key={itemIndex}>{item.content} </span>
                                  );
                                } else {
                                  const isRevealed = revealedWords[item.id] || false;
                                  return (
                                    <span
                                      key={item.id}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setRevealedWords(prev => ({
                                          ...prev,
                                          [item.id]: !prev[item.id]
                                        }));
                                      }}
                                      style={{
                                        padding: '0.1rem 0.8rem',
                                        backgroundColor: isRevealed ? 'transparent' : 'rgba(26, 115, 232, 0.2)',
                                        cursor: 'pointer',
                                        borderRadius: '6px',
                                        fontWeight: '500',
                                        color: isRevealed ? '#1a73e8' : 'transparent',
                                        userSelect: 'none',
                                        display: 'inline-block',
                                        minWidth: '50px',
                                        transition: 'all 0.2s ease'
                                      }}
                                      title={isRevealed ? "点击隐藏" : "点击查看"}
                                    >
                                      {item.correctAnswer}
                                    </span>
                                  );
                                }
                              })}
                            </div>
                            <div style={{ 
                              color: 'rgba(0,0,0,0.6)', 
                              lineHeight: '1.4',
                              fontWeight: 'normal',
                              fontSize: getSubtitleFontSizes().chinese
                            }}>
                              {subtitle.chinese}
                            </div>
                          </>
                        )}
                        
                        {/* 跟读练习模式 */}
                        {exerciseMode === 'repeat' && (
                          <>
                            <div style={{ 
                              color: '#000000', 
                              marginBottom: '0.5rem',
                              lineHeight: '1.4',
                              fontSize: getSubtitleFontSizes().english,
                              fontWeight: '500'
                            }}>
                              {renderAnnotatedText(subtitle.english)}
                            </div>
                            <div style={{ 
                              backgroundColor: 'transparent',
                              borderRadius: '8px',
                              padding: '1rem',
                              marginTop: '0.5rem',
                              display: 'flex',
                              gap: '1rem',
                              alignItems: 'center'
                            }}>
                              {/* 播放原音按钮 */}
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  const videoElement = isFloatingPlayerMode ? floatingVideoRef.current : videoRef.current;
                                  if (playingSubtitleIndex === index && videoElement && !videoElement.paused) {
                                    videoElement.pause();
                                    setIsPlaying(false);
                                    setPlayingSubtitleIndex(null);
                                  } else {
                                    playOriginalAudio(index);
                                    setPlayingSubtitleIndex(index);
                                  }
                                }}
                                onTouchStart={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                }}
                                onTouchEnd={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  const videoElement = isFloatingPlayerMode ? floatingVideoRef.current : videoRef.current;
                                  if (playingSubtitleIndex === index && videoElement && !videoElement.paused) {
                                    videoElement.pause();
                                    setIsPlaying(false);
                                    setPlayingSubtitleIndex(null);
                                  } else {
                                    playOriginalAudio(index);
                                    setPlayingSubtitleIndex(index);
                                  }
                                }}
                                style={{
                                  background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                                  border: 'none',
                                  color: 'white',
                                  padding: '0.75rem',
                                  borderRadius: '50%',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '40px',
                                  height: '40px',
                                  transition: 'all 0.2s ease',
                                  touchAction: 'manipulation',
                                  WebkitTapHighlightColor: 'transparent',
                                  outline: 'none'
                                }}
                                title={playingSubtitleIndex === index ? "暂停" : "播放原音"}
                              >
                                {playingSubtitleIndex === index ? (
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                                    <rect x="6" y="4" width="4" height="16"/>
                                    <rect x="14" y="4" width="4" height="16"/>
                                  </svg>
                                ) : (
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                                    <path d="M8 5v14l11-7z"/>
                                  </svg>
                                )}
                              </button>
                              {/* 开始/停止录音按钮 */}
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  toggleRecording(index);
                                }}
                                onTouchStart={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                }}
                                onTouchEnd={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  toggleRecording(index);
                                }}
                                style={{
                                  background: recordingIndex === index ? 'rgba(255, 0, 0, 0.8)' : 'rgba(255, 100, 100, 0.8)',
                                  border: recordingIndex === index ? '2px solid #ff0000' : 'none',
                                  color: 'white',
                                  padding: '0.75rem',
                                  borderRadius: '50%',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '40px',
                                  height: '40px',
                                  animation: recordingIndex === index ? 'pulse 1s infinite' : 'none',
                                  transition: 'all 0.2s ease',
                                  touchAction: 'manipulation',
                                  WebkitTapHighlightColor: 'transparent'
                                }}
                                title={recordingIndex === index ? "停止录音" : "开始录音"}
                              >
                                {recordingIndex === index ? '⏹' : '🎤'}
                              </button>
                              {/* 播放录音按钮 */}
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  playRecordedAudio(index);
                                }}
                                onTouchStart={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                }}
                                onTouchEnd={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  playRecordedAudio(index);
                                }}
                                style={{
                                  background: recordedAudioUrlRef.current[index] ? 'rgba(76, 175, 80, 0.8)' : 'rgba(100, 100, 100, 0.5)',
                                  border: 'none',
                                  color: 'white',
                                  padding: '0.75rem',
                                  borderRadius: '50%',
                                  cursor: recordedAudioUrlRef.current[index] ? 'pointer' : 'not-allowed',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '40px',
                                  height: '40px',
                                  transition: 'all 0.2s ease',
                                  touchAction: 'manipulation',
                                  WebkitTapHighlightColor: 'transparent',
                                  outline: 'none'
                                }}
                                title={playingRecordingIndex === index ? "暂停录音" : (recordedAudioUrlRef.current[index] ? "播放录音" : "请先录音")}
                              >
                                {playingRecordingIndex === index ? (
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                                    <rect x="6" y="4" width="4" height="16"/>
                                    <rect x="14" y="4" width="4" height="16"/>
                                  </svg>
                                ) : (
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                                    <path d="M8 5v14l11-7z"/>
                                  </svg>
                                )}
                              </button>
                            </div>
                          </>
                        )}
                        
                        {exerciseMode === 'none' && subtitleMode === 'bilingual' && (
                          <>
                            <div style={{ 
                              color: currentSubtitle === index ? '#000000' : '#1a1a1a', 
                              marginBottom: '0.5rem', 
                              lineHeight: '1.4',
                              fontWeight: currentSubtitle === index ? 'bold' : '500',
                              fontSize: getSubtitleFontSizes().english
                            }}>
                              {renderAnnotatedText(subtitle.english)}
                            </div>
                            <div style={{ 
                              color: currentSubtitle === index ? '#000000' : 'rgba(0,0,0,0.6)', 
                              lineHeight: '1.4',
                              fontWeight: currentSubtitle === index ? 'bold' : 'normal',
                              fontSize: getSubtitleFontSizes().chinese
                            }}>
                              {subtitle.chinese}
                            </div>
                          </>
                        )}
                        {exerciseMode === 'none' && subtitleMode === 'english' && (
                          <div style={{ 
                            color: currentSubtitle === index ? '#000000' : '#1a1a1a', 
                            lineHeight: '1.4',
                            fontWeight: currentSubtitle === index ? 'bold' : '500',
                            fontSize: getSubtitleFontSizes().english
                          }}>
                            {renderAnnotatedText(subtitle.english)}
                          </div>
                        )}
                        {exerciseMode === 'none' && subtitleMode === 'chinese' && (
                          <div style={{ 
                            color: currentSubtitle === index ? '#000000' : '#333333', 
                            lineHeight: '1.4',
                            fontWeight: currentSubtitle === index ? 'bold' : 'normal',
                            fontSize: getSubtitleFontSizes().chinese
                          }}>
                            {subtitle.chinese}
                          </div>
                        )}
                      </div>
                    );
                  })}
                    </div>
                  </div>
                </div>
              </div>


            </div>
          )}
          
        </div>
      </div>
      
      {/* 重置确认框 */}
      {showResetConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)'
          }}>
            <h3 style={{
              color: '#1a2b4c',
              fontSize: '1.3rem',
              fontWeight: '600',
              margin: '0 0 1rem 0'
            }}>重置学习进度</h3>
            <p style={{
              color: 'rgba(26, 43, 76, 0.8)',
              fontSize: '1rem',
              margin: '0 0 2rem 0',
              lineHeight: '1.6'
            }}>确定要重置这个视频的学习进度吗？这将清除完成状态和观看记录。</p>
            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowResetConfirm(false)}
                style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0',
                  backgroundColor: 'white',
                  color: '#1a2b4c',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'white';
                }}
              >
                取消
              </button>
              <button
                onClick={() => {
                  // 重置学习进度
                  if (lesson && lesson._id) {
                    try {
                      // 从已学习课程中移除
                      const learnedCourses = JSON.parse((localStorage.getItem('learnedCourses') || '[]').replace(/^"undefined"$/, '[]'));
                      const updatedLearnedCourses = learnedCourses.filter(id => id !== lesson._id);
                      localStorage.setItem('learnedCourses', JSON.stringify(updatedLearnedCourses));
                      
                      // 清除学习统计中的相关记录
                      const savedStats = localStorage.getItem('learningStats');
                      if (savedStats) {
                        const stats = JSON.parse(savedStats);
                        // 这里可以根据需要清除相关的统计数据
                        localStorage.setItem('learningStats', JSON.stringify(stats));
                      }
                      
                      console.log('视频学习进度已重置:', lesson._id);
                    } catch (error) {
                      console.error('重置学习进度失败:', error);
                    }
                  }
                  setShowResetConfirm(false);
                  alert('学习进度已重置！');
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#bb2d3b';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(220, 53, 69, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#dc3545';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                确定
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* 底部固定功能组件 */}
      {!isDesktop && (
        <div className="lesson-bottom-controls" style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(10px)',
          borderTop: '1px solid rgba(0, 0, 0, 0.1)',
          padding: '1rem 1.5rem',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '1.5rem',
          zIndex: 500,
          boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)'
        }}>
        {/* 返回首页按钮 - 只在iPad模式下显示 */}
        {isTablet && !isMobile && (
          <button
            onClick={() => navigate('/')}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#333',
              padding: '0.75rem 1rem',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.25rem',
              fontSize: '0.85rem',
              fontWeight: '500',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '22px' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                <polyline points="9 22 9 12 15 12 15 22"></polyline>
              </svg>
            </div>
            <span>首页</span>
          </button>
        )}
        {/* 字幕按钮 */}
        <div className="dropdown-container lesson-bottom-button" style={{ position: 'relative' }}>
          <button
            onClick={() => toggleDropdown('subtitle')}
            className="lesson-bottom-btn"
            style={{
              background: 'transparent',
              border: 'none',
              color: '#333',
              padding: '0.75rem 1rem',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.25rem',
              fontSize: '0.85rem',
              fontWeight: '500',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
          >
            <div style={{ fontSize: '0.8rem', position: 'relative', width: '18px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ position: 'absolute', top: 0, left: 0, fontWeight: 'bold' }}>文</span>
              <span style={{ position: 'absolute', bottom: '-2px', right: '-1px', fontSize: '0.8em', fontWeight: 'bold' }}>A</span>
            </div>
            <span>字幕</span>
          </button>
          {dropdownOpen.subtitle && (
            <div style={{
              position: 'fixed',
              bottom: '80px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '400px',
              maxWidth: '90vw',
              backgroundColor: 'white',
              border: '1px solid rgba(0, 0, 0, 0.15)',
              borderRadius: '12px',
              padding: '1.75rem 0.75rem 2rem',
              zIndex: 1000,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}>
              {/* 字幕切换 */}
              <div style={{ marginBottom: '0.75rem', borderBottom: '1px solid #e0e0e0', paddingBottom: '0.75rem' }}>
                <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '0.5rem', color: 'rgba(0, 0, 0, 0.7)' }}>
                  字幕切换
                </div>
                <div style={{ display: 'flex', gap: '0.25rem', backgroundColor: 'rgba(0,0,0,0.05)', borderRadius: '20px', padding: '0.25rem' }}>
                  <button
                    onClick={() => selectOption('subtitle', 'bilingual')}
                    style={{
                      flex: 1,
                      padding: '0.5rem 1rem',
                      borderRadius: '16px',
                      border: 'none',
                      backgroundColor: subtitleMode === 'bilingual' ? '#86EFAC' : 'transparent',
                      color: subtitleMode === 'bilingual' ? '#15803d' : '#666',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    双语
                  </button>
                  <button
                    onClick={() => selectOption('subtitle', 'english')}
                    style={{
                      flex: 1,
                      padding: '0.5rem 1rem',
                      borderRadius: '16px',
                      border: 'none',
                      backgroundColor: subtitleMode === 'english' ? '#86EFAC' : 'transparent',
                      color: subtitleMode === 'english' ? '#15803d' : '#666',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    英文
                  </button>
                  <button
                    onClick={() => selectOption('subtitle', 'chinese')}
                    style={{
                      flex: 1,
                      padding: '0.5rem 1rem',
                      borderRadius: '16px',
                      border: 'none',
                      backgroundColor: subtitleMode === 'chinese' ? '#86EFAC' : 'transparent',
                      color: subtitleMode === 'chinese' ? '#15803d' : '#666',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    中文
                  </button>
                </div>
              </div>

              {/* 字体大小 */}
              <div>
                <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '0.75rem', color: 'rgba(0, 0, 0, 0.7)' }}>
                  字体大小
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ fontSize: '0.85rem', color: '#666', fontWeight: '500', whiteSpace: 'nowrap' }}>
                    {subtitleFontSize === 'small' ? '小' : subtitleFontSize === 'medium' ? '中' : subtitleFontSize === 'large' ? '大' : '特大'}
                  </span>
                  <input
                    type="range"
                    min="1"
                    max="4"
                    value={subtitleFontSize === 'small' ? 1 : subtitleFontSize === 'medium' ? 2 : subtitleFontSize === 'large' ? 3 : 4}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      const size = value === 1 ? 'small' : value === 2 ? 'medium' : value === 3 ? 'large' : 'extraLarge';
                      setSubtitleFontSize(size);
                      localStorage.setItem('subtitleFontSize', size);
                    }}
                    style={{
                      flex: 1,
                      height: '4px',
                      borderRadius: '2px',
                      WebkitAppearance: 'none',
                      appearance: 'none',
                      background: 'linear-gradient(to right, #86EFAC 0%, #86EFAC ' + ((subtitleFontSize === 'small' ? 25 : subtitleFontSize === 'medium' ? 50 : subtitleFontSize === 'large' ? 75 : 100)) + '%, #e0e0e0 ' + ((subtitleFontSize === 'small' ? 25 : subtitleFontSize === 'medium' ? 50 : subtitleFontSize === 'large' ? 75 : 100)) + '%, #e0e0e0 100%)',
                      cursor: 'pointer'
                    }}
                  />
                  <span style={{ fontSize: '0.9rem', color: '#333', fontWeight: '500', whiteSpace: 'nowrap' }}>
                    {subtitleFontSize === 'extraLarge' ? '特大' : ''}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 倍速按钮 */}
        <div className="dropdown-container lesson-bottom-button" style={{ position: 'relative' }}>
          <button
            onClick={() => toggleDropdown('bottomSpeed')}
            className="lesson-bottom-btn"
            style={{
              background: 'transparent',
              border: 'none',
              color: '#333',
              padding: '0.75rem 1rem',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.25rem',
              fontSize: '0.85rem',
              fontWeight: '500',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.8rem',
              fontWeight: 'bold',
              height: '22px'
            }}>
              {playbackSpeed}x
            </div>
            <span>倍速</span>
          </button>
          {dropdownOpen.bottomSpeed && (
            <div style={{
              position: 'absolute',
              bottom: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              marginBottom: '0.5rem',
              backgroundColor: 'white',
              border: '1px solid rgba(0, 0, 0, 0.15)',
              borderRadius: '12px',
              padding: '0.75rem',
              zIndex: 1000,
              minWidth: '80px',
              width: 'fit-content',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}>
              {[0.5, 0.75, 1, 1.25, 1.5, 2].map(speed => (
                <div 
                  key={speed}
                  style={{
                    marginBottom: speed !== 2 ? '0.5rem' : '0',
                    padding: '0.75rem 1rem',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    backgroundColor: playbackSpeed === speed ? 'rgba(134, 239, 172, 0.3)' : 'transparent',
                    textAlign: 'center',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => {
                    updatePlaybackSpeed(speed);
                    setDropdownOpen(prev => ({ ...prev, bottomSpeed: false }));
                  }}
                  onMouseEnter={(e) => {
                    if (playbackSpeed !== speed) {
                      e.currentTarget.style.backgroundColor = 'rgba(134, 239, 172, 0.15)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (playbackSpeed !== speed) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {speed}x
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 播放/暂停按钮 */}
        <button
          className="lesson-bottom-play-btn"
          onClick={() => {
            const videoElement = isFloatingPlayerMode ? floatingVideoRef.current : videoRef.current;
            if (videoElement) {
              if (lesson?.content?.videoUrl && (lesson.content.videoUrl.includes('youtube.com') || lesson.content.videoUrl.includes('youtu.be'))) {
                try {
                  if (isPlaying) {
                    videoElement.contentWindow.postMessage(JSON.stringify({
                      event: 'command',
                      func: 'pauseVideo',
                      args: []
                    }), '*');
                    setIsPlaying(false);
                  } else {
                    videoElement.contentWindow.postMessage(JSON.stringify({
                      event: 'command',
                      func: 'playVideo',
                      args: []
                    }), '*');
                    setIsPlaying(true);
                  }
                } catch (error) {
                  console.error('无法控制YouTube视频:', error);
                }
              } else {
                if (isPlaying) {
                  videoElement.pause();
                  setIsPlaying(false);
                } else {
                  videoElement.play().catch(error => {
                    console.error('播放视频失败:', error);
                  });
                  setIsPlaying(true);
                }
              }
            }
          }}
          style={{
            background: 'linear-gradient(135deg, #86EFAC, #4ade80)',
            border: 'none',
            color: 'white',
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 15px rgba(74, 222, 128, 0.4)',
            transition: 'all 0.3s ease',
            outline: 'none',
            WebkitTapHighlightColor: 'transparent'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.05)';
            e.target.style.boxShadow = '0 6px 20px rgba(74, 222, 128, 0.5)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1)';
            e.target.style.boxShadow = '0 4px 15px rgba(74, 222, 128, 0.4)';
          }}
        >
          {isPlaying ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
              <rect x="6" y="4" width="4" height="16"/>
              <rect x="14" y="4" width="4" height="16"/>
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
              <path d="M8 5v14l11-7z"/>
            </svg>
          )}
        </button>

        {/* 循环按钮 */}
        <div className="dropdown-container lesson-bottom-button" style={{ position: 'relative' }}>
          <button
            onClick={() => toggleDropdown('bottomPlay')}
            className="lesson-bottom-btn"
            style={{
              background: 'transparent',
              border: 'none',
              color: '#333',
              padding: '0.75rem 1rem',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.25rem',
              fontSize: '0.85rem',
              fontWeight: '500',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '22px' }}>
              <FiRepeat size={22} />
            </div>
            <span>循环</span>
          </button>
          {dropdownOpen.bottomPlay && (
            <div style={{
              position: 'fixed',
              bottom: '80px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '400px',
              maxWidth: '90vw',
              backgroundColor: 'white',
              border: '1px solid rgba(0, 0, 0, 0.15)',
              borderRadius: '12px',
              padding: '0.75rem',
              zIndex: 1000,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}>
              <div style={{ marginBottom: '0.75rem' }}>
                <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '0.5rem', color: 'rgba(0, 0, 0, 0.7)' }}>视听</div>
                <div style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', backgroundColor: playMode === 'single' ? 'rgba(134, 239, 172, 0.3)' : 'transparent', padding: '0.5rem', borderRadius: '8px', transition: 'all 0.2s ease' }} onClick={() => selectOption('bottomPlay', 'single')} onMouseEnter={(e) => { if (playMode !== 'single') { e.currentTarget.style.backgroundColor = 'rgba(134, 239, 172, 0.15)' } }} onMouseLeave={(e) => { if (playMode !== 'single') { e.currentTarget.style.backgroundColor = 'transparent' } }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>▶</span>
                    <span>单集播放</span>
                  </div>
                  {playMode === 'single' && <span style={{ color: 'green' }}>✓</span>}
                </div>
                <div style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', backgroundColor: playMode === 'singleLoop' ? 'rgba(134, 239, 172, 0.3)' : 'transparent', padding: '0.5rem', borderRadius: '8px', transition: 'all 0.2s ease' }} onClick={() => selectOption('bottomPlay', 'singleLoop')} onMouseEnter={(e) => { if (playMode !== 'singleLoop') { e.currentTarget.style.backgroundColor = 'rgba(134, 239, 172, 0.15)' } }} onMouseLeave={(e) => { if (playMode !== 'singleLoop') { e.currentTarget.style.backgroundColor = 'transparent' } }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>🔁</span>
                    <span>单集循环</span>
                  </div>
                  {playMode === 'singleLoop' && <span style={{ color: 'green' }}>✓</span>}
                </div>
              </div>
              <div style={{ borderTop: '1px solid rgba(0, 0, 0, 0.1)', paddingTop: '0.75rem' }}>
                <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '0.5rem', color: 'rgba(0, 0, 0, 0.7)' }}>句子</div>
                <div style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', backgroundColor: playMode === 'continuous' ? 'rgba(134, 239, 172, 0.3)' : 'transparent', padding: '0.5rem', borderRadius: '8px', transition: 'all 0.2s ease' }} onClick={() => selectOption('bottomPlay', 'continuous')} onMouseEnter={(e) => { if (playMode !== 'continuous') { e.currentTarget.style.backgroundColor = 'rgba(134, 239, 172, 0.15)' } }} onMouseLeave={(e) => { if (playMode !== 'continuous') { e.currentTarget.style.backgroundColor = 'transparent' } }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>▶</span>
                    <span>连续播放</span>
                  </div>
                  {playMode === 'continuous' && <span style={{ color: 'green' }}>✓</span>}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', backgroundColor: playMode === 'sentenceLoop' ? 'rgba(134, 239, 172, 0.3)' : 'transparent', padding: '0.5rem', borderRadius: '8px', transition: 'all 0.2s ease' }} onClick={() => selectOption('bottomPlay', 'sentenceLoop')} onMouseEnter={(e) => { if (playMode !== 'sentenceLoop') { e.currentTarget.style.backgroundColor = 'rgba(134, 239, 172, 0.15)' } }} onMouseLeave={(e) => { if (playMode !== 'sentenceLoop') { e.currentTarget.style.backgroundColor = 'transparent' } }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>🔁</span>
                    <span>单句循环</span>
                  </div>
                  {playMode === 'sentenceLoop' && <span style={{ color: 'green' }}>✓</span>}
                </div>
                
                {playMode === 'sentenceLoop' && (
                  <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
                    <div style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>当前进度：</span>
                      <span style={{ fontWeight: 'bold', color: '#1a73e8' }}>
                        {currentPlayCountDisplay} / {loopCount}
                      </span>
                    </div>
                    <div style={{ marginBottom: '0.75rem' }}>
                      <div style={{ marginBottom: '0.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>单句循环次数</span>
                        <span>{loopCount}次</span>
                      </div>
                      <input 
                        type="range" 
                        min="1" 
                        max="10" 
                        value={loopCount} 
                        onChange={(e) => {
                          const newCount = parseInt(e.target.value) || 1;
                          setLoopCount(newCount);
                          if (newCount <= currentSentencePlayCountRef.current) {
                            currentSentencePlayCountRef.current = 0;
                            isCurrentSentenceEndedRef.current = false;
                            setCurrentPlayCountDisplay(0);
                          }
                        }} 
                        style={{
                          width: '100%',
                          height: '4px',
                          backgroundColor: '#e0e0e0',
                          borderRadius: '2px',
                          outline: 'none',
                          WebkitAppearance: 'none',
                          margin: '0.5rem 0'
                        }}
                      />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>自动播放下句</span>
                      <label style={{ position: 'relative', display: 'inline-block', width: '50px', height: '24px' }}>
                        <input 
                          type="checkbox" 
                          checked={autoNext} 
                          onChange={() => setAutoNext(!autoNext)} 
                          style={{ opacity: 0, width: 0, height: 0 }}
                        />
                        <span style={{
                          position: 'absolute',
                          cursor: 'pointer',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: autoNext ? '#00ffc3' : '#ccc',
                          transition: '.4s',
                          borderRadius: '24px'
                        }}>
                        </span>
                        <span style={{
                          position: 'absolute',
                          content: '',
                          height: '16px',
                          width: '16px',
                          left: '4px',
                          bottom: '4px',
                          backgroundColor: 'white',
                          transition: '.4s',
                          borderRadius: '50%',
                          transform: autoNext ? 'translateX(26px)' : 'translateX(0)'
                        }}>
                        </span>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* 精读按钮 */}
        <div className="dropdown-container lesson-bottom-button" style={{ position: 'relative' }}>
          <button
            onClick={() => toggleDropdown('intensive')}
            className="lesson-bottom-btn"
            style={{
              background: 'transparent',
              border: 'none',
              color: '#333',
              padding: '0.75rem 1rem',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.25rem',
              fontSize: '0.85rem',
              fontWeight: '500',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '22px' }}>
              <FiBookOpen size={22} />
            </div>
            <span>精读</span>
          </button>
          {dropdownOpen.intensive && (
            <div style={{
              position: 'fixed',
              bottom: '80px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '400px',
              maxWidth: '90vw',
              backgroundColor: 'white',
              border: '1px solid rgba(0, 0, 0, 0.15)',
              borderRadius: '12px',
              padding: '0.75rem',
              zIndex: 1000,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}>
              <div 
                style={{ marginBottom: '0.5rem', padding: '0.75rem', borderRadius: '8px', backgroundColor: showWordCard ? 'rgba(134, 239, 172, 0.3)' : 'transparent', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', transition: 'all 0.2s ease' }}
                onClick={() => {
                  const videoElement = isFloatingPlayerMode ? floatingVideoRef.current : videoRef.current;
                  if (videoElement && !videoElement.paused) {
                    videoElement.pause();
                  }
                  setIsPlaying(false);
                  setPlayingSubtitleIndex(null);
                  setShowWordCard(true);
                  setDropdownOpen({ ...dropdownOpen, intensive: false });
                }}
                onMouseEnter={(e) => {
                  if (!showWordCard) {
                    e.currentTarget.style.backgroundColor = 'rgba(134, 239, 172, 0.15)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!showWordCard) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span>📚</span>
                  <span>单词卡片</span>
                </div>
                {showWordCard && <span style={{ color: 'green', fontWeight: 'bold' }}>✓</span>}
              </div>
              <div 
                style={{ marginBottom: '0.5rem', padding: '0.75rem', borderRadius: '8px', backgroundColor: intensiveMode === 'repeat' ? 'rgba(134, 239, 172, 0.3)' : 'transparent', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', transition: 'all 0.2s ease' }}
                onClick={() => {
                  const videoElement = isFloatingPlayerMode ? floatingVideoRef.current : videoRef.current;
                  if (videoElement && !videoElement.paused) {
                    videoElement.pause();
                  }
                  setIsPlaying(false);
                  setPlayingSubtitleIndex(null);
                  setIntensiveMode('repeat');
                  setExerciseMode('repeat');
                  setDropdownOpen({ ...dropdownOpen, intensive: false });
                }}
                onMouseEnter={(e) => {
                  if (intensiveMode !== 'repeat') {
                    e.currentTarget.style.backgroundColor = 'rgba(134, 239, 172, 0.15)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (intensiveMode !== 'repeat') {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span>🎤</span>
                  <span>跟读练习</span>
                </div>
                {intensiveMode === 'repeat' && <span style={{ color: 'green', fontWeight: 'bold' }}>✓</span>}
              </div>
              <div 
                style={{ marginBottom: '0.5rem', padding: '0.75rem', borderRadius: '8px', backgroundColor: intensiveMode === 'fill' ? 'rgba(134, 239, 172, 0.3)' : 'transparent', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', transition: 'all 0.2s ease' }}
                onClick={() => {
                  const videoElement = isFloatingPlayerMode ? floatingVideoRef.current : videoRef.current;
                  if (videoElement && !videoElement.paused) {
                    videoElement.pause();
                  }
                  setIsPlaying(false);
                  setPlayingSubtitleIndex(null);
                  setIntensiveMode('fill');
                  setExerciseMode('fill');
                  setDropdownOpen({ ...dropdownOpen, intensive: false });
                }}
                onMouseEnter={(e) => {
                  if (intensiveMode !== 'fill') {
                    e.currentTarget.style.backgroundColor = 'rgba(134, 239, 172, 0.15)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (intensiveMode !== 'fill') {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span>📝</span>
                  <span>填空练习</span>
                </div>
                {intensiveMode === 'fill' && <span style={{ color: 'green', fontWeight: 'bold' }}>✓</span>}
              </div>
              <div 
                style={{ padding: '0.75rem', borderRadius: '8px', backgroundColor: intensiveMode === 'none' ? 'rgba(134, 239, 172, 0.3)' : 'transparent', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', transition: 'all 0.2s ease' }}
                onClick={() => {
                  setIntensiveMode('none');
                  setExerciseMode('none');
                  setShowWordCard(false);
                  setShowWordDetail(false);
                  
                  // 恢复播放
                  const videoElement = isFloatingPlayerMode ? floatingVideoRef.current : videoRef.current;
                  if (videoElement && videoElement.paused) {
                    videoElement.play();
                    setIsPlaying(true);
                  }
                  
                  setDropdownOpen({ ...dropdownOpen, intensive: false });
                }}
                onMouseEnter={(e) => {
                  if (intensiveMode !== 'none') {
                    e.currentTarget.style.backgroundColor = 'rgba(134, 239, 172, 0.15)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (intensiveMode !== 'none') {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span>✕</span>
                  <span>关闭精读</span>
                </div>
                {intensiveMode === 'none' && <span style={{ color: 'green', fontWeight: 'bold' }}>✓</span>}
              </div>
            </div>
          )}
        </div>

        {/* 简介按钮 - 只在iPad模式下显示 */}
        {isTablet && !isMobile && (
          <button
            onClick={() => setShowIntroduction(!showIntroduction)}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#333',
              padding: '0.75rem 1rem',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.25rem',
              fontSize: '0.85rem',
              fontWeight: '500',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '22px' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <span>简介</span>
          </button>
        )}
      </div>
      )}
      
      {/* 单词卡片弹窗 */}
      {showWordCard && !showWordDetail && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: '#f5f7fa',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 10000
        }}>
          {/* 头部 */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem 1.5rem',
            backgroundColor: 'white',
            borderBottom: '1px solid #e8e8e8',
            position: 'relative'
          }}>
            <button
              onClick={() => setShowWordCard(false)}
              style={{
                position: 'absolute',
                left: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: '#0066ff',
                textDecoration: 'none',
                padding: '0.6rem 0.8rem',
                borderRadius: '8px',
                backgroundColor: 'white',
                border: '1px solid rgba(0, 102, 255, 0.2)',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(0, 102, 255, 0.05)';
                e.target.style.boxShadow = '0 2px 10px rgba(0, 102, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'white';
                e.target.style.boxShadow = 'none';
              }}
            >
              <FiArrowLeft />
            </button>
            <h3 style={{
              fontSize: '1.2rem',
              fontWeight: '600',
              color: '#1a1a1a',
              margin: 0
            }}>单词卡片</h3>
          </div>
          
          {/* 过滤按钮 */}
          <div style={{
            padding: '1rem 1.5rem',
            display: 'flex',
            gap: '0.75rem'
          }}>
            {[
              { key: 'all', label: '全部', count: wordsData.length + phrasesData.length + expressionsData.length },
              { key: 'unmarked', label: '未标记', count: (wordsData.filter(item => !item.status).length + phrasesData.filter(item => !item.status).length + expressionsData.filter(item => !item.status).length) },
              { key: 'known', label: '认识', count: (wordsData.filter(item => item.status === 'known').length + phrasesData.filter(item => item.status === 'known').length + expressionsData.filter(item => item.status === 'known').length) },
              { key: 'unknown', label: '不认识', count: (wordsData.filter(item => item.status === 'unknown').length + phrasesData.filter(item => item.status === 'unknown').length + expressionsData.filter(item => item.status === 'unknown').length) }
            ].map(({ key, label, count }) => (
              <button
                key={key}
                onClick={() => setWordFilter(key)}
                style={{
                  flex: 1,
                  padding: isMobile ? '0.75rem 0.5rem' : '0.75rem 1rem',
                  borderRadius: '25px',
                  border: wordFilter === key ? '2px solid #84cc16' : '2px solid #d1d5db',
                  backgroundColor: wordFilter === key ? '#dcfce7' : 'white',
                  color: wordFilter === key ? '#15803d' : '#666',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: wordFilter === key ? '600' : '400',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: isMobile ? '0.25rem' : '0.5rem'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {label}
                <span style={{ fontSize: '0.85rem', opacity: 0.8 }}>{count}</span>
              </button>
            ))}
          </div>
          
          {/* 分类卡片 */}
          <div style={{
            flex: 1,
            padding: '0 1.5rem 1.5rem',
            overflowY: 'auto'
          }}>
            {/* 单词卡 */}
            <div
              onClick={() => {
                setCurrentCardType('words');
                setCurrentWordIndex(0);
                setShowWordDefinition(false);
                setShowWordDetail(true);
              }}
              style={{
                padding: '1.5rem',
                borderRadius: '16px',
                backgroundColor: '#d1fae5',
                marginBottom: '1rem',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              <span style={{ fontSize: '1.25rem', fontWeight: '600', color: '#065f46' }}>单词</span>
              <span style={{
                backgroundColor: 'white',
                padding: '0.25rem 0.75rem',
                borderRadius: '20px',
                fontSize: '0.95rem',
                fontWeight: '500',
                color: '#374151',
                border: '1px solid #d1d5db'
              }}>{wordsData.length}</span>
            </div>
            
            {/* 短语卡 */}
            <div
              onClick={() => {
                setCurrentCardType('phrases');
                setCurrentWordIndex(0);
                setShowWordDefinition(false);
                setShowWordDetail(true);
              }}
              style={{
                padding: '1.5rem',
                borderRadius: '16px',
                backgroundColor: '#dbeafe',
                marginBottom: '1rem',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              <span style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1e40af' }}>短语</span>
              <span style={{
                backgroundColor: 'white',
                padding: '0.25rem 0.75rem',
                borderRadius: '20px',
                fontSize: '0.95rem',
                fontWeight: '500',
                color: '#374151',
                border: '1px solid #d1d5db'
              }}>{phrasesData.length}</span>
            </div>
            
            {/* 表达卡 */}
            <div
              onClick={() => {
                setCurrentCardType('expressions');
                setCurrentWordIndex(0);
                setShowWordDefinition(false);
                setShowWordDetail(true);
              }}
              style={{
                padding: '1.5rem',
                borderRadius: '16px',
                backgroundColor: '#fee2e2',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              <span style={{ fontSize: '1.25rem', fontWeight: '600', color: '#991b1b' }}>自然科普词汇</span>
              <span style={{
                backgroundColor: 'white',
                padding: '0.25rem 0.75rem',
                borderRadius: '20px',
                fontSize: '0.95rem',
                fontWeight: '500',
                color: '#374151',
                border: '1px solid #d1d5db'
              }}>{expressionsData.length}</span>
            </div>
          </div>
        </div>
      )}
      
      {/* 简介弹窗 */}
      {showIntroduction && (
        <div style={{
          position: 'fixed',
          bottom: '80px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '400px',
          maxWidth: '90vw',
          backgroundColor: 'white',
          border: '1px solid rgba(0, 0, 0, 0.15)',
          borderRadius: '12px',
          padding: '1.75rem 0.75rem 2rem',
          zIndex: 1000,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          maxHeight: '300px',
          overflowY: 'auto'
        }}>
          <p style={{ color: 'rgba(0,0,0,0.7)', lineHeight: '1.6', margin: 0, fontSize: '0.95rem' }}>
            {lesson.description || '这是一个科学视频，通过生动的讲解和实验，帮助孩子们了解科学知识。'}
          </p>
        </div>
      )}
      
      {/* 单词详情弹窗 */}
      {showWordDetail && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#f5f7fa',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 10001
          }}
          onTouchStart={(e) => {
            // 检查触摸目标是否是按钮，如果是则不处理
            const target = e.target;
            if (target.tagName === 'BUTTON' || target.closest('button')) {
              return;
            }
            setTouchStartX(e.touches[0].clientX);
            setTouchEndX(e.touches[0].clientX);
            setIsSwiping(false);
          }}
          onTouchMove={(e) => {
            // 检查触摸目标是否是按钮，如果是则不处理
            const target = e.target;
            if (target.tagName === 'BUTTON' || target.closest('button')) {
              return;
            }
            setTouchEndX(e.touches[0].clientX);
            const touchDiff = touchStartX - e.touches[0].clientX;
            if (Math.abs(touchDiff) > 10) {
              setIsSwiping(true);
            }
          }}
          onTouchEnd={(e) => {
            // 检查触摸目标是否是按钮，如果是则不处理
            const target = e.target;
            if (target.tagName === 'BUTTON' || target.closest('button')) {
              return;
            }
            const touchDiff = touchStartX - touchEndX;
            const minSwipeDistance = 50;
            
            if (Math.abs(touchDiff) > minSwipeDistance) {
              let maxIndex;
              if (currentCardType === 'words') maxIndex = wordsData.length - 1;
              else if (currentCardType === 'phrases') maxIndex = phrasesData.length - 1;
              else maxIndex = expressionsData.length - 1;
              
              if (touchDiff > 0) {
                // 向左滑动，下一个
                if (currentWordIndex < maxIndex) {
                  setCurrentWordIndex(currentWordIndex + 1);
                }
              } else {
                // 向右滑动，上一个
                if (currentWordIndex > 0) {
                  setCurrentWordIndex(currentWordIndex - 1);
                }
              }
            }
            setIsSwiping(false);
          }}
        >
          {/* 头部 */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem 1.5rem',
            backgroundColor: 'white',
            borderBottom: '1px solid #e8e8e8',
            position: 'relative'
          }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowWordDetail(false);
                setShowWordDefinition(false);
              }}
              style={{
                position: 'absolute',
                left: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: '#0066ff',
                textDecoration: 'none',
                padding: '0.6rem 0.8rem',
                borderRadius: '8px',
                backgroundColor: 'white',
                border: '1px solid rgba(0, 102, 255, 0.2)',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(0, 102, 255, 0.05)';
                e.target.style.boxShadow = '0 2px 10px rgba(0, 102, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'white';
                e.target.style.boxShadow = 'none';
              }}
            >
              <FiArrowLeft />
            </button>
            <div style={{
              fontSize: '1rem',
              fontWeight: '600',
              color: '#1a73e8',
              backgroundColor: 'rgba(26, 115, 232, 0.1)',
              padding: '0.5rem 1rem',
              borderRadius: '20px',
              border: '1px solid rgba(26, 115, 232, 0.2)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span>{currentWordIndex + 1}</span>
              <span style={{ color: 'rgba(26, 115, 232, 0.6)' }}>/</span>
              <span>{currentCardType === 'words' ? wordsData.length : currentCardType === 'phrases' ? phrasesData.length : expressionsData.length}</span>
            </div>
          </div>
          
          {/* 卡片内容 */}
          <div style={{
            flex: 1,
            padding: '1rem',
            display: 'flex',
            alignItems: 'stretch',
            justifyContent: 'center'
          }}>
            <div 
              style={{
                backgroundColor: (() => {
                  let currentData;
                  if (currentCardType === 'words') currentData = wordsData[currentWordIndex];
                  else if (currentCardType === 'phrases') currentData = phrasesData[currentWordIndex];
                  else currentData = expressionsData[currentWordIndex];
                  
                  if (!currentData) return 'white';
                  return currentData.status === 'known' ? 'rgba(40, 167, 69, 0.1)' : currentData.status === 'unknown' ? 'rgba(220, 53, 69, 0.35)' : 'white';
                })(),
                borderRadius: '24px',
                padding: '2rem 2.5rem',
                width: '100%',
                maxWidth: '600px',
                height: '100%',
                boxShadow: '0 6px 16px rgba(0, 102, 255, 0.08)',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                border: '2px solid rgba(0, 102, 255, 0.15)',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onClick={(e) => {
                if (!isSwiping) {
                  if (showWordDefinition) {
                    setShowWordDefinition(false);
                  } else {
                    setShowWordDefinition(true);
                  }
                }
              }}
            >
              {/* 右上角按钮 - 单词/短语/表达都有 */}
              {(() => {
                let currentData;
                if (currentCardType === 'words') currentData = wordsData[currentWordIndex];
                else if (currentCardType === 'phrases') currentData = phrasesData[currentWordIndex];
                else currentData = expressionsData[currentWordIndex];
                
                if (!currentData) return null;
                
                return (
                  <div style={{
                    position: 'absolute',
                    top: '1.5rem',
                    right: '1rem',
                    display: 'flex',
                    flexDirection: (isMobile || isTablet) ? 'row' : 'column',
                    gap: (isMobile || isTablet) ? '0.5rem' : '0.3rem',
                    alignItems: 'center',
                    flexWrap: 'nowrap',
                    zIndex: 1
                  }}>
                    {currentData.cambridgeLevel && (
                      <div style={{
                        padding: '0.5rem 0.8rem',
                        borderRadius: '6px',
                        border: '1px solid #1a73e8',
                        backgroundColor: 'rgba(26, 115, 232, 0.1)',
                        color: '#1a73e8',
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        width: '80px',
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'default',
                        transition: 'all 0.2s ease'
                      }} onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(26, 115, 232, 0.2)';
                        e.currentTarget.style.transform = 'scale(1.05)';
                      }} onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(26, 115, 232, 0.1)';
                        e.currentTarget.style.transform = 'scale(1)';
                      }}>
                        {currentData.cambridgeLevel}
                      </div>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (currentCardType === 'words') {
                          updateWordStatus(currentData.id, 'known');
                        } else if (currentCardType === 'phrases') {
                          updatePhraseStatus(currentData.id, 'known');
                        } else {
                          updateExpressionStatus(currentData.id, 'known');
                        }
                      }}
                      style={{
                        padding: '0.5rem 0.8rem',
                        borderRadius: '6px',
                        border: currentData.status === 'known' ? '1px solid #28a745' : '1px solid #28a745',
                        backgroundColor: currentData.status === 'known' ? '#28a745' : 'white',
                        color: currentData.status === 'known' ? 'white' : '#28a745',
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                        fontWeight: '500',
                        width: '80px',
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease'
                      }}
                      title="认识"
                      onMouseEnter={(e) => {
                        if (currentData.status !== 'known') {
                          e.currentTarget.style.backgroundColor = 'rgba(40, 167, 69, 0.1)';
                        }
                        e.currentTarget.style.transform = 'scale(1.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = currentData.status === 'known' ? '#28a745' : 'white';
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    >
                      ✓ 认识
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (currentCardType === 'words') {
                          saveUnknownWord(currentData);
                        } else if (currentCardType === 'phrases') {
                          updatePhraseStatus(currentData.id, 'unknown');
                        } else {
                          updateExpressionStatus(currentData.id, 'unknown');
                        }
                      }}
                      style={{
                        padding: '0.5rem 0.8rem',
                        borderRadius: '6px',
                        border: currentData.status === 'unknown' ? '1px solid #dc3545' : '1px solid #dc3545',
                        backgroundColor: currentData.status === 'unknown' ? '#dc3545' : 'white',
                        color: currentData.status === 'unknown' ? 'white' : '#dc3545',
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                        fontWeight: '500',
                        width: '80px',
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease'
                      }}
                      title="不认识，添加到单词卡"
                      onMouseEnter={(e) => {
                        if (currentData.status !== 'unknown') {
                          e.currentTarget.style.backgroundColor = 'rgba(220, 53, 69, 0.1)';
                        }
                        e.currentTarget.style.transform = 'scale(1.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = currentData.status === 'unknown' ? '#dc3545' : 'white';
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    >
                      ✗ 不认识
                    </button>
                  </div>
                );
              })()}
              
              {/* 单词/短语/表达 */}
              {(() => {
                let currentData;
                if (currentCardType === 'words') currentData = wordsData[currentWordIndex];
                else if (currentCardType === 'phrases') currentData = phrasesData[currentWordIndex];
                else currentData = expressionsData[currentWordIndex];
                
                if (!currentData) return null;
                
                const wordText = currentData.word || currentData.phrase || currentData.expression;
                
                return (
                  <div style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <div style={{
                      textAlign: 'center',
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      paddingTop: '3rem'
                    }}>
                      <div style={{
                        fontSize: '4rem',
                        fontWeight: 'bold',
                        color: '#1a1a1a',
                        marginBottom: '1rem'
                      }}>
                        {wordText}
                      </div>
                      
                      {/* 音标和发音按钮 */}
                      <div style={{
                        marginBottom: '0.7rem',
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'nowrap',
                        justifyContent: 'center'
                      }}>
                        {currentData.phonetic && (
                          <div style={{
                            fontSize: '1rem',
                            color: 'rgba(0, 0, 0, 0.7)',
                            fontFamily: 'Arial, sans-serif',
                            whiteSpace: 'nowrap',
                            marginRight: '1rem'
                          }}>
                            {currentData.phonetic}
                          </div>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // 创建一个辅助函数来获取高质量声音
                            const getHighQualityVoice = () => {
                              // 如果 voices 还没加载，尝试获取
                              if (!voicesLoaded) {
                                window.speechSynthesis.getVoices();
                              }
                              
                              const voices = speechSynthesis.getVoices();
                              if (!voices || voices.length === 0) return null;
                              
                              // 优先尝试使用 Google 女声
                              let voice = voices.find(v => 
                                v.lang === 'en-US' && 
                                (v.name.includes('Google') && v.name.toLowerCase().includes('female'))
                              );
                              if (voice) return voice;
                              
                              // 其次尝试使用 Google 男声或女声
                              voice = voices.find(v => 
                                v.lang === 'en-US' && 
                                v.name.includes('Google')
                              );
                              if (voice) return voice;
                              
                              // 再次尝试 Samantha 或 Alex
                              voice = voices.find(v => 
                                v.lang === 'en-US' && 
                                (v.name.includes('Samantha') || v.name.includes('Alex'))
                              );
                              if (voice) return voice;
                              
                              // 最后尝试任何 en-US 的声音
                              return voices.find(v => v.lang === 'en-US');
                            };
                            
                            // 使用浏览器语音合成的备用函数
                            const speakWithBrowser = () => {
                              try {
                                const utterance = new SpeechSynthesisUtterance(wordText);
                                utterance.lang = 'en-US';
                                utterance.rate = 0.85;
                                const highQualityVoice = getHighQualityVoice();
                                if (highQualityVoice) {
                                  utterance.voice = highQualityVoice;
                                }
                                speechSynthesis.cancel(); // 清除之前的发音队列
                                speechSynthesis.speak(utterance);
                              } catch (e) {
                                console.error('浏览器发音失败:', e);
                              }
                            };
                            
                            try {
                              // 先尝试使用有道语音合成 API
                              const audio = new Audio(`https://dict.youdao.com/dictvoice?audio=${encodeURIComponent(wordText)}&type=2`);
                              
                              let played = false;
                              
                              // 设置较长的超时时间
                              const timeout = setTimeout(() => {
                                if (!played) {
                                  console.log('有道TTS响应超时，使用备用发音');
                                  speakWithBrowser();
                                }
                              }, 2000); // 2秒超时
                              
                              audio.addEventListener('canplaythrough', () => {
                                if (!played) {
                                  played = true;
                                  clearTimeout(timeout);
                                  audio.play().catch(() => {
                                    speakWithBrowser();
                                  });
                                }
                              });
                              
                              audio.onerror = () => {
                                if (!played) {
                                  played = true;
                                  clearTimeout(timeout);
                                  console.log('有道TTS加载失败，使用浏览器发音');
                                  speakWithBrowser();
                                }
                              };
                              
                              // 尝试预加载音频
                              audio.load();
                              
                              // 同时尝试播放，如果音频已经缓存了就立即播放
                              audio.play().then(() => {
                                if (!played) {
                                  played = true;
                                  clearTimeout(timeout);
                                }
                              }).catch(error => {
                                // 如果播放失败，让 canplaythrough 事件来处理
                                console.log('初始播放失败，等待音频加载');
                              });
                            } catch (e) {
                              console.error('发音失败:', e);
                              speakWithBrowser();
                            }
                          }}
                          style={{
                            background: 'rgba(200, 200, 200, 0.3)',
                            border: 'none',
                            borderRadius: '50%',
                            width: '36px',
                            height: '36px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            fontSize: '1.1rem',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = 'rgba(200, 200, 200, 0.5)';
                            e.target.style.transform = 'scale(1.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'rgba(200, 200, 200, 0.3)';
                            e.target.style.transform = 'scale(1)';
                          }}
                        >
                          🔊
                        </button>
                      </div>
                      
                      {/* 显示释义按钮 */}
                      {!showWordDefinition ? (
                        <div style={{ 
                          flex: 1, 
                          display: 'flex', 
                          flexDirection: 'column', 
                          justifyContent: 'flex-start',
                          alignItems: 'center',
                          marginTop: '2rem'
                        }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowWordDefinition(true);
                            }}
                            style={{
                              padding: '1rem 2rem',
                              borderRadius: '12px',
                              border: '2px solid #1a73e8',
                              backgroundColor: 'white',
                              color: '#1a73e8',
                              cursor: 'pointer',
                              fontSize: '1.8rem',
                              fontWeight: 'bold',
                              textAlign: 'center',
                              transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#1a73e8';
                              e.currentTarget.style.color = 'white';
                              e.currentTarget.style.transform = 'scale(1.05)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'white';
                              e.currentTarget.style.color = '#1a73e8';
                              e.currentTarget.style.transform = 'scale(1)';
                            }}
                          >
                            点击显示释义
                          </button>
                        </div>
                      ) : (
                        <div 
                          style={{ textAlign: 'left', flex: 1, overflowY: 'auto' }}
                        >
                          {/* 词性 */}
                          <div style={{
                            marginBottom: '0.5rem',
                            display: 'flex',
                            alignItems: 'center'
                          }}>
                            {currentData.partOfSpeech && (
                              <div style={{
                                display: 'flex',
                                alignItems: 'center'
                              }}>
                                <div style={{
                                  background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                                  color: 'white',
                                  padding: '0.2rem 0.5rem',
                                  borderRadius: '4px',
                                  fontSize: '0.7rem',
                                  fontWeight: 'bold',
                                  marginRight: '0.5rem',
                                  whiteSpace: 'nowrap',
                                  height: '20px',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}>
                                  词性
                                </div>
                                <div style={{
                                  fontSize: '1.1rem',
                                  color: 'rgba(0, 0, 0, 0.9)',
                                  fontWeight: '500',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {currentData.partOfSpeech}
                                </div>
                              </div>
                            )}
                          </div>
                          
                          {/* 中文翻译 */}
                          <div style={{
                            marginBottom: '0.7rem',
                            display: 'flex',
                            alignItems: 'center'
                          }}>
                            {currentData.chineseTranslation && (
                              <div style={{
                                display: 'flex',
                                alignItems: 'center'
                              }}>
                                <div style={{
                                  background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                                  color: 'white',
                                  padding: '0.2rem 0.5rem',
                                  borderRadius: '4px',
                                  fontSize: '0.7rem',
                                  fontWeight: 'bold',
                                  marginRight: '0.5rem',
                                  whiteSpace: 'nowrap',
                                  height: '20px',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}>
                                  中译
                                </div>
                                <div style={{
                                  fontSize: '1rem',
                                  color: 'rgba(0, 0, 0, 0.9)',
                                  fontWeight: '500',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }} title={currentData.chineseTranslation}>
                                  {currentData.chineseTranslation}
                                </div>
                              </div>
                            )}
                          </div>
                        
                        {/* 英文定义 - 仅显示后端提交的 */}
                        {currentData.definition && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            marginTop: '0.2rem',
                            marginBottom: '0.7rem'
                          }}>
                            <div style={{
                              background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                              color: 'white',
                              padding: '0.2rem 0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.7rem',
                              fontWeight: 'bold',
                              marginRight: '0.5rem',
                              whiteSpace: 'nowrap',
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              英译
                            </div>
                            <div style={{
                              fontSize: '0.9rem',
                              color: 'rgba(0, 0, 0, 0.6)',
                              flex: 1
                            }}>
                              {currentData.definition}
                            </div>
                          </div>
                        )}
                        
                            {/* 联想词 - 仅显示后端提交的 */}
                            {currentData.synonyms && (
                              <div style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                marginBottom: '0.2rem',
                                marginTop: '0.2rem'
                              }}>
                                <div style={{
                                  background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                                  color: 'white',
                                  padding: '0.2rem 0.5rem',
                                  borderRadius: '4px',
                                  fontSize: '0.7rem',
                                  fontWeight: 'bold',
                                  marginRight: '0.5rem',
                                  whiteSpace: 'nowrap',
                                  height: '20px',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}>
                                  联想词
                                </div>
                                <div style={{
                                  fontSize: '0.85rem',
                                  color: 'rgba(0, 0, 0, 0.6)',
                                  flex: 1
                                }}>
                                  {currentData.synonyms}
                                </div>
                              </div>
                            )}
                        
                        {/* 白色底框 - 例句 */}
                        {currentData.sentence && (
                          <div 
                            style={{
                              backgroundColor: 'white',
                              borderRadius: '12px',
                              padding: '0.8rem',
                              marginTop: '0.7rem',
                              transition: 'all 0.2s',
                              position: 'relative',
                              width: '100%',
                              boxSizing: 'border-box'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = 'rgba(26, 115, 232, 0.05)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'white';
                            }}
                          >
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              marginBottom: '0.5rem'
                            }}>
                              <div style={{
                                background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                                color: 'white',
                                padding: '0.2rem 0.5rem',
                                borderRadius: '4px',
                                fontSize: '0.7rem',
                                fontWeight: 'bold',
                                display: 'inline-block'
                              }}>
                                原文例句
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  let targetIndex = -1;
                                  
                                  // 优先使用保存的字幕索引
                                  if (currentData.subtitleIndex !== undefined && subtitles[currentData.subtitleIndex]) {
                                    targetIndex = currentData.subtitleIndex;
                                    console.log('使用保存的字幕索引播放:', targetIndex);
                                  } else if (currentData.sentence) {
                                    // 如果没有保存的索引，再通过文本匹配
                                    const sentenceLower = currentData.sentence.toLowerCase();
                                    for (let i = 0; i < subtitles.length; i++) {
                                      const sub = subtitles[i];
                                      const subLower = sub.english.toLowerCase();
                                      if (subLower.includes(sentenceLower) || sentenceLower.includes(subLower)) {
                                        targetIndex = i;
                                        console.log('通过文本匹配找到字幕索引:', targetIndex);
                                        break;
                                      }
                                    }
                                  }
                                  
                                  if (targetIndex !== -1) {
                                    setCurrentSubtitle(targetIndex);
                                    playOriginalAudio(targetIndex);
                                  }
                                }}
                                onTouchStart={(e) => {
                                  e.stopPropagation();
                                  e.currentTarget.style.transform = 'scale(0.95)';
                                }}
                                onTouchEnd={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  e.currentTarget.style.transform = 'scale(1)';
                                  let targetIndex = -1;
                                  
                                  // 优先使用保存的字幕索引
                                  if (currentData.subtitleIndex !== undefined && subtitles[currentData.subtitleIndex]) {
                                    targetIndex = currentData.subtitleIndex;
                                    console.log('使用保存的字幕索引播放:', targetIndex);
                                  } else if (currentData.sentence) {
                                    // 如果没有保存的索引，再通过文本匹配
                                    const sentenceLower = currentData.sentence.toLowerCase();
                                    for (let i = 0; i < subtitles.length; i++) {
                                      const sub = subtitles[i];
                                      const subLower = sub.english.toLowerCase();
                                      if (subLower.includes(sentenceLower) || sentenceLower.includes(subLower)) {
                                        targetIndex = i;
                                        console.log('通过文本匹配找到字幕索引:', targetIndex);
                                        break;
                                      }
                                    }
                                  }
                                  
                                  if (targetIndex !== -1) {
                                    setCurrentSubtitle(targetIndex);
                                    playOriginalAudio(targetIndex);
                                  }
                                }}
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  minWidth: '32px',
                                  minHeight: '32px',
                                  borderRadius: '50%',
                                  backgroundColor: '#87CEEB',
                                  border: 'none',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  marginLeft: '0.5rem',
                                  transition: 'all 0.2s ease',
                                  WebkitTapHighlightColor: 'transparent'
                                }}
                                title="播放原文"
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#5DADE2';
                                  e.currentTarget.style.transform = 'scale(1.1)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = '#87CEEB';
                                  e.currentTarget.style.transform = 'scale(1)';
                                }}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                                  <path d="M8 5v14l11-7z"/>
                                </svg>
                              </button>
                            </div>
                            <div style={{
                              fontSize: '0.95rem',
                              color: '#333',
                              marginBottom: '0.3rem',
                              lineHeight: '1.5'
                            }} dangerouslySetInnerHTML={{
                              __html: currentData.sentence.replace(
                                new RegExp(`\\b${wordText}\\b`, 'gi'),
                                `<span style="color: #1a73e8; font-weight: bold;">${wordText}</span>`
                              )
                            }} />
                            {currentData.sentenceChinese && (
                              <div style={{
                                fontSize: '0.85rem',
                                color: 'rgba(0, 0, 0, 0.6)',
                                lineHeight: '1.4'
                              }}>
                                {currentData.sentenceChinese}
                              </div>
                            )}
                          </div>
                        )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
          

        </div>
      )}
    </div>
  );
};

export default Lesson;